/**
 * Smart Recommendation Engine for RUG Assistant
 * Tracks user behavior and generates personalized recommendations
 */

class RecommendationEngine {
  constructor(store) {
    this.store = store;
    this.preferences = this.loadPreferences();
    this.clickHistory = this.loadClickHistory();
    this.viewDuration = {}; // Track how long user looks at products
  }

  loadPreferences() {
    try {
      const saved = localStorage.getItem("rug_preferences");
      return saved ? JSON.parse(saved) : this.defaultPreferences();
    } catch (e) {
      return this.defaultPreferences();
    }
  }

  defaultPreferences() {
    return {
      preferredStyles: {},
      preferredColors: {},
      preferredMaterials: {},
      preferredRooms: {},
      priceRange: { min: 0, max: 10000 },
      engagementScore: {},
      clickCount: {},
      lastUpdated: new Date().toISOString(),
    };
  }

  loadClickHistory() {
    try {
      const saved = localStorage.getItem("rug_click_history");
      return saved ? JSON.parse(saved).slice(-100) : []; // Keep last 100
    } catch (e) {
      return [];
    }
  }

  savePreferences() {
    localStorage.setItem("rug_preferences", JSON.stringify(this.preferences));
  }

  saveClickHistory() {
    localStorage.setItem(
      "rug_click_history",
      JSON.stringify(this.clickHistory),
    );
  }

  /**
   * Track product view/click
   */
  trackProductView(product, action = "view") {
    const timestamp = new Date().toISOString();

    // Update click history
    this.clickHistory.push({
      productId: product.id,
      action, // 'view', 'click', 'add-to-cart', 'wishlist'
      timestamp,
      metadata: {
        style: product.style,
        color: product.color,
        material: product.material,
        room: product.room,
        price: product.price,
      },
    });

    // Update preference scores
    if (product.style) {
      this.preferences.preferredStyles[product.style] =
        (this.preferences.preferredStyles[product.style] || 0) +
        this.getActionWeight(action);
    }
    if (product.color) {
      this.preferences.preferredColors[product.color] =
        (this.preferences.preferredColors[product.color] || 0) +
        this.getActionWeight(action);
    }
    if (product.material) {
      this.preferences.preferredMaterials[product.material] =
        (this.preferences.preferredMaterials[product.material] || 0) +
        this.getActionWeight(action);
    }
    if (product.room) {
      this.preferences.preferredRooms[product.room] =
        (this.preferences.preferredRooms[product.room] || 0) +
        this.getActionWeight(action);
    }

    // Track price interest
    const price =
      parseFloat(String(product.price).replace(/[^0-9.]/g, "")) || 0;
    if (price > 0) {
      this.preferences.clickCount[price] =
        (this.preferences.clickCount[price] || 0) + 1;
    }

    this.savePreferences();
    this.saveClickHistory();
  }

  getActionWeight(action) {
    const weights = {
      view: 1,
      click: 3,
      "add-to-cart": 10,
      wishlist: 8,
      compare: 5,
      "room-view": 7,
    };
    return weights[action] || 1;
  }

  /**
   * Get top user preferences
   */
  getTopPreferences(type = "styles", limit = 3) {
    const preferences = this.preferences[`preferred${type}`];
    if (!preferences) return [];

    return Object.entries(preferences)
      .sort((a, b) => b[1] - a[1])
      .slice(0, limit)
      .map(([name, score]) => ({ name, score }));
  }

  /**
   * Generate recommendation reason
   */
  generateReason() {
    const topStyles = this.getTopPreferences("Styles", 1);
    const topColors = this.getTopPreferences("Colors", 1);
    const topRooms = this.getTopPreferences("Rooms", 1);

    const reasons = [];
    if (topStyles.length > 0) reasons.push(`${topStyles[0].name} style`);
    if (topColors.length > 0) reasons.push(`${topColors[0].name} tones`);
    if (topRooms.length > 0) reasons.push(`perfect for ${topRooms[0].name}`);

    if (reasons.length === 0) return "Based on your browsing history";
    return `Based on your interest in ${reasons.join(", ")}`;
  }

  /**
   * Get engagement score for trending recommendations
   */
  getEngagementMetrics() {
    const last7Days = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    const recentClicks = this.clickHistory.filter(
      (click) => new Date(click.timestamp) > last7Days,
    );

    return {
      viewCount: recentClicks.filter((c) => c.action === "view").length,
      cartCount: recentClicks.filter((c) => c.action === "add-to-cart").length,
      wishlistCount: recentClicks.filter((c) => c.action === "wishlist").length,
      comparisonCount: recentClicks.filter((c) => c.action === "compare")
        .length,
      totalEngagement: recentClicks.length,
    };
  }

  /**
   * Get similar products based on user history
   */
  getSimilarProductCriteria() {
    const topStyles = this.getTopPreferences("Styles", 2).map((s) => s.name);
    const topColors = this.getTopPreferences("Colors", 2).map((c) => c.name);
    const topMaterials = this.getTopPreferences("Materials", 2).map(
      (m) => m.name,
    );

    return {
      styles: topStyles,
      colors: topColors,
      materials: topMaterials,
    };
  }

  /**
   * Get user behavioral score (0-100)
   */
  getUserEngagementLevel() {
    const metrics = this.getEngagementMetrics();
    const score = Math.min(100, (metrics.totalEngagement / 50) * 100);
    return Math.round(score);
  }

  /**
   * Get user profile summary (for display in dashboard)
   */
  getUserProfile() {
    const topStyles = this.getTopPreferences("Styles", 3);
    const topColors = this.getTopPreferences("Colors", 3);
    const topRooms = this.getTopPreferences("Rooms", 3);
    const metrics = this.getEngagementMetrics();

    return {
      favoriteStyles: topStyles.map((s) => s.name),
      favoriteColors: topColors.map((c) => c.name),
      favoriteRooms: topRooms.map((r) => r.name),
      engagementLevel: this.getUserEngagementLevel(),
      totalViews: metrics.viewCount,
      totalCartsAdded: metrics.cartCount,
      totalWishlistedItems: metrics.wishlistCount,
      lastUpdated: this.preferences.lastUpdated,
    };
  }

  /**
   * Get next product recommendations (for "View all" expansion)
   */
  getRecommendedProducts(products = [], limit = 6) {
    if (!products.length) return [];

    const criteria = this.getSimilarProductCriteria();
    const scored = products.map((product) => {
      let score = 0;

      if (criteria.styles.includes(product.style)) score += 30;
      if (criteria.colors.includes(product.color)) score += 25;
      if (criteria.materials.includes(product.material)) score += 20;

      // Boost items not yet viewed
      const isViewed = this.clickHistory.some(
        (h) => h.productId === product.id,
      );
      if (!isViewed) score += 15;

      return { product, score };
    });

    return scored
      .sort((a, b) => b.score - a.score)
      .slice(0, limit)
      .map((s) => s.product);
  }

  /**
   * Clear all data (for testing/reset)
   */
  reset() {
    this.preferences = this.defaultPreferences();
    this.clickHistory = [];
    localStorage.removeItem("rug_preferences");
    localStorage.removeItem("rug_click_history");
  }
}

if (typeof window !== "undefined") {
  window.RecommendationEngine = RecommendationEngine;
}

/**
 * Touch Gesture Handler for RUG Assistant
 * Provides swipe, long-press, and haptic feedback
 */

class GestureHandler {
  constructor() {
    this.touchStartX = 0;
    this.touchEndX = 0;
    this.touchStartTime = 0;
    this.longPressTimer = null;
    this.minSwipeDistance = 50;
    this.longPressDuration = 500;
  }

  // Initialize gesture listeners
  init(element) {
    if (!element) return;

    element.addEventListener("touchstart", (e) => this.onTouchStart(e), false);
    element.addEventListener("touchend", (e) => this.onTouchEnd(e), false);
    element.addEventListener(
      "touchcancel",
      (e) => this.onTouchCancel(e),
      false,
    );
    element.addEventListener("touchmove", (e) => this.onTouchMove(e), false);
  }

  onTouchStart(e) {
    this.touchStartX = e.changedTouches[0].screenX;
    this.touchStartTime = Date.now();
    this.longPressTimer = setTimeout(() => {
      this.triggerLongPress(e);
    }, this.longPressDuration);
  }

  onTouchEnd(e) {
    this.touchEndX = e.changedTouches[0].screenX;
    clearTimeout(this.longPressTimer);

    const diff = this.touchStartX - this.touchEndX;
    const duration = Date.now() - this.touchStartTime;

    // Swipe detected
    if (Math.abs(diff) > this.minSwipeDistance && duration < 500) {
      if (diff > 0) {
        this.triggerSwipeLeft(e);
      } else {
        this.triggerSwipeRight(e);
      }
    }
  }

  onTouchMove(e) {
    // Clear long press if user moves
    if (Math.abs(e.changedTouches[0].screenX - this.touchStartX) > 10) {
      clearTimeout(this.longPressTimer);
    }
  }

  onTouchCancel(e) {
    clearTimeout(this.longPressTimer);
  }

  triggerSwipeLeft(e) {
    const event = new CustomEvent("swipe-left", { detail: e });
    document.dispatchEvent(event);
    this.haptic("light");
  }

  triggerSwipeRight(e) {
    const event = new CustomEvent("swipe-right", { detail: e });
    document.dispatchEvent(event);
    this.haptic("light");
  }

  triggerLongPress(e) {
    const event = new CustomEvent("long-press", { detail: e });
    document.dispatchEvent(event);
    this.haptic("medium");
  }

  // Haptic feedback (if supported)
  haptic(type = "light") {
    if (navigator.vibrate) {
      const patterns = {
        light: 10,
        medium: 20,
        heavy: 40,
        success: [20, 10, 20],
      };
      const pattern = patterns[type] || 10;
      navigator.vibrate(pattern);
    }
  }

  // Setup carousel with swipe support
  initCarousel(carouselElement, options = {}) {
    if (!carouselElement) return;

    const { onNext, onPrev } = options;
    let currentIndex = 0;
    const items = carouselElement.querySelectorAll("[data-carousel-item]");

    document.addEventListener("swipe-left", (e) => {
      if (e.target.closest(carouselElement)) {
        currentIndex = (currentIndex + 1) % items.length;
        this.scrollCarouselTo(carouselElement, currentIndex);
        onNext && onNext(currentIndex);
        this.haptic("light");
      }
    });

    document.addEventListener("swipe-right", (e) => {
      if (e.target.closest(carouselElement)) {
        currentIndex = (currentIndex - 1 + items.length) % items.length;
        this.scrollCarouselTo(carouselElement, currentIndex);
        onPrev && onPrev(currentIndex);
        this.haptic("light");
      }
    });

    this.init(carouselElement);
  }

  scrollCarouselTo(carousel, index) {
    const item = carousel.querySelector(`[data-carousel-item="${index}"]`);
    if (item) {
      carousel.scrollTo({
        left: item.offsetLeft - carousel.offsetLeft,
        behavior: "smooth",
      });
    }
  }

  // Long press quick action
  setupQuickAction(element, callback) {
    document.addEventListener("long-press", (e) => {
      if (e.target.closest(element) || element.contains(e.target)) {
        callback(e);
        this.haptic("success");
      }
    });
  }
}

// Export for use
if (typeof window !== "undefined") {
  window.GestureHandler = GestureHandler;
}

/**
 * Personal Dashboard for RUG Assistant
 * Displays user profile, wishlists, and saved items
 */

class PersonalDashboard {
  constructor(store, recommendationEngine) {
    this.store = store;
    this.engine = recommendationEngine;
  }

  /**
   * Render user profile card
   */
  renderProfileCard() {
    const profile = this.engine.getUserProfile();
    const initials = this.getInitials();

    return `
        <div class="profile-card">
            <div class="profile-header">
                <div class="profile-avatar">${initials}</div>
                <div class="profile-info">
                    <h3>Your Shopping Profile</h3>
                    <p class="profile-level">Engagement Level: <span class="level-${profile.engagementLevel}">${profile.engagementLevel}%</span></p>
                </div>
            </div>
            
            <div class="profile-stats">
                <div class="stat">
                    <i class="fa fa-eye"></i>
                    <span>${profile.totalViews}</span>
                    <small>Viewed</small>
                </div>
                <div class="stat">
                    <i class="fa fa-heart"></i>
                    <span>${profile.totalWishlistedItems}</span>
                    <small>Saved</small>
                </div>
                <div class="stat">
                    <i class="fa fa-shopping-bag"></i>
                    <span>${profile.totalCartsAdded}</span>
                    <small>Carted</small>
                </div>
            </div>

            <div class="profile-preferences">
                <h4>Your Preferences</h4>
                <div class="preference-tags">
                    <div class="pref-category">
                        <strong>Styles:</strong>
                        ${profile.favoriteStyles.map((s) => `<span class="tag">${s}</span>`).join("")}
                    </div>
                    <div class="pref-category">
                        <strong>Colors:</strong>
                        ${profile.favoriteColors.map((c) => `<span class="tag">${c}</span>`).join("")}
                    </div>
                    <div class="pref-category">
                        <strong>Rooms:</strong>
                        ${profile.favoriteRooms.map((r) => `<span class="tag">${r}</span>`).join("")}
                    </div>
                </div>
            </div>
        </div>
        `;
  }

  /**
   * Render wishlisted items
   */
  renderWishlists() {
    const wishlist = this.store.data.wishlist || [];

    if (wishlist.length === 0) {
      return `
            <div class="empty-state">
                <i class="fa fa-heart"></i>
                <p>No saved items yet</p>
                <button class="discovery-btn btn-accent btn-sm" data-app-action="openAssistantWith" data-arg0="Show me trending rugs">
                    Explore Recommendations
                </button>
            </div>
            `;
    }

    return `
        <div class="wishlist-grid">
            ${wishlist.map((item) => this.renderWishlistCard(item)).join("")}
        </div>
        `;
  }

  renderWishlistCard(item) {
    return `
        <div class="wishlist-card">
            <img src="${item.imgUrl}" alt="${item.title}" loading="lazy">
            <div class="wishlist-card-body">
                <h4>${item.title}</h4>
                <p class="price">${item.price}</p>
                <div class="actions">
                    <button class="discovery-btn btn-primary btn-sm" data-app-action="openProduct" data-arg0="${item.id}">
                        <i class="fa fa-eye"></i> View
                    </button>
                    <button class="discovery-btn btn-secondary btn-sm" data-app-action="quickAddProduct" data-arg0="${item.id}">
                        <i class="fa fa-shopping-bag"></i> Add
                    </button>
                </div>
            </div>
        </div>
        `;
  }

  /**
   * Render recently viewed items
   */
  renderRecentlyViewed() {
    const viewed = (this.store.data.viewed || []).slice(0, 6);

    if (viewed.length === 0) {
      return `<p class="text-muted">No recently viewed items</p>`;
    }

    return `
        <div class="carousel-horizontal">
            ${viewed
              .map(
                (item, idx) => `
            <div class="carousel-item" data-carousel-item="${idx}">
                <img src="${item.imgUrl}" alt="${item.title}" loading="lazy">
                <p class="text-sm">${item.title}</p>
            </div>
            `,
              )
              .join("")}
        </div>
        `;
  }

  /**
   * Render saved searches
   */
  renderSavedSearches() {
    const searches = (this.store.data.searchHistory || []).slice(0, 5);

    if (searches.length === 0) {
      return `<p class="text-muted">No search history</p>`;
    }

    return `
        <div class="saved-searches">
            ${searches
              .map(
                (query) => `
            <button class="search-chip" data-app-action="sendAction" data-arg0="${query}">
                <i class="fa fa-search"></i> ${query}
            </button>
            `,
              )
              .join("")}
        </div>
        `;
  }

  /**
   * Render full personal dashboard
   */
  renderFullDashboard() {
    return `
        <div class="personal-dashboard">
            <div class="dashboard-header">
                <h2><i class="fa fa-user-circle"></i> Your Personal Assistant</h2>
                <p class="subtitle">Personalized recommendations based on your preferences</p>
            </div>

            <!-- Profile Card -->
            <section class="dashboard-section">
                <h3>Profile & Preferences</h3>
                ${this.renderProfileCard()}
            </section>

            <!-- Wishlists -->
            <section class="dashboard-section">
                <h3><i class="fa fa-heart"></i> Saved Items (${this.store.data.wishlist?.length || 0})</h3>
                ${this.renderWishlists()}
            </section>

            <!-- Recently Viewed -->
            <section class="dashboard-section">
                <h3><i class="fa fa-history"></i> Recently Viewed</h3>
                ${this.renderRecentlyViewed()}
            </section>

            <!-- Saved Searches -->
            <section class="dashboard-section">
                <h3><i class="fa fa-bookmark"></i> Quick Search</h3>
                ${this.renderSavedSearches()}
            </section>

            <!-- Loyalty/Stats -->
            <section class="dashboard-section loyalty-section">
                <h3><i class="fa fa-star"></i> Your Benefits</h3>
                <div class="benefits-grid">
                    <div class="benefit-card">
                        <i class="fa fa-truck"></i>
                        <strong>Free Shipping</strong>
                        <p>On orders over $50</p>
                    </div>
                    <div class="benefit-card">
                        <i class="fa fa-undo"></i>
                        <strong>30-Day Returns</strong>
                        <p>No questions asked</p>
                    </div>
                    <div class="benefit-card">
                        <i class="fa fa-headset"></i>
                        <strong>Expert Support</strong>
                        <p>Chat with AI assistant</p>
                    </div>
                </div>
            </section>
        </div>
        `;
  }

  /**
   * Get user initials for avatar
   */
  getInitials() {
    const name = localStorage.getItem("rug_user_name") || "Guest";
    return (
      name
        .split(" ")
        .map((n) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2) || "RU"
    );
  }
}

if (typeof window !== "undefined") {
  window.PersonalDashboard = PersonalDashboard;
}

/**
 * Discovery-AI Rug Assistant — Frontend v2.0
 * Complete rewrite: accessible, functional, zero hardcoding
 */

// ============================================================
// CONFIG
// ============================================================
const API_BASE =
  window.ASSISTANT_API_BASE || "https://assistant.productadvisorai.com";
const PRODUCT_API_BASE =
  window.BACKEND_BASE_URL || "https://assistant.productadvisorai.com";

// ============================================================
// API Client
// ============================================================
class DiscoveryAPI {
  constructor(base) {
    this.base = base || API_BASE; // Rug Assistant (agent/assistant endpoints)
    this.productBase = PRODUCT_API_BASE; // ProductAdvisor (browse/search/discover/recommendations)
  }

  async _fetch(url, opts = {}) {
    const resp = await fetch(url, opts);
    const data = await resp.json().catch(() => null);
    if (!resp.ok) {
      const detail =
        data?.detail || data?.error || `Request failed (${resp.status})`;
      throw new Error(detail);
    }
    return data;
  }

  get tenant() {
    if (window.shopkeeperTenant) return window.shopkeeperTenant;
    if (window.ShopifyContextPayload?.shop?.permanent_domain) {
      return window.ShopifyContextPayload.shop.permanent_domain
        .replace(".myshopify.com", "")
        .replace(/[-.]/g, "_");
    }
    if (window.Shopify?.shop) {
      return window.Shopify.shop.replace(".myshopify.com", "").replace(/[-.]/g, "_");
    }
    return "vogue_rugs";
  }

  // ── Agent endpoints (Rug Assistant) ──────────────────────────

  message(text, sessionId, facts = {}, context = {}, memory = {}) {
    return this._fetch(`${this.base}/assistant/message`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tenant_id: this.tenant,
        message: text,
        session_id: sessionId,
        facts,
        context,
        memory,
      }),
    });
  }

  syncContext(sessionId, context, memory) {
    return this._fetch(`${this.base}/assistant/context`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tenant_id: this.tenant,
        session_id: sessionId,
        context,
        memory,
      }),
    });
  }

  pdpQuestions(product) {
    return this._fetch(`${this.base}/assistant/pdp-questions`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenant_id: this.tenant, product }),
    });
  }

  tenantKnowledge() {
    return this._fetch(
      `${this.base}/tenant/${encodeURIComponent(this.tenant)}/knowledge`,
    );
  }

  // ── Product endpoints (ProductAdvisor direct) ────────────────

  discover(text, mode, state, facts) {
    return this._fetch(`${this.productBase}/api/discover`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tenant_id: this.tenant,
        user_msg: text,
        mode: mode || "conversational",
        state,
        facts,
      }),
    });
  }

  search(q, page = 1) {
    return this._fetch(`${this.base}/assistant/search`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenant_id: this.tenant, q, page }),
    });
  }

  browse(facts, page = 1, pageSize = 12) {
    return this._fetch(`${this.base}/assistant/browse`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tenant_id: this.tenant,
        facts,
        page,
        page_size: pageSize,
      }),
    });
  }

  recommend(productId, productHandle = "") {
    return this._fetch(`${this.base}/assistant/recommendations`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tenant_id: this.tenant,
        product_id: String(productId),
        product_handle: productHandle ? String(productHandle) : String(productId),
      }),
    });
  }

  analyzeImage(file, options = {}) {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("tenant", this.tenant);
    fd.append(
      "skip_visualizer_points",
      options.skipVisualizerPoints ? "true" : "false",
    );
    return this._fetch(`${this.base}/api/analyze-image`, {
      method: "POST",
      body: fd,
    });
  }

  contactStore(payload) {
    return this._fetch(`${this.base}/assistant/contact-store`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tenant_id: this.tenant, ...payload }),
    });
  }
}

// ============================================================
// IndexedDB Wrapper (AgentDB)
// ============================================================
class AgentDB {
  constructor() {
    this.dbName = "RugbieAgentDB";
    this.version = 1;
    this.db = null;
    this.ready = this.init();
  }

  init() {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(this.dbName, this.version);
      req.onupgradeneeded = (e) => {
        const db = e.target.result;
        if (!db.objectStoreNames.contains("messages")) {
          db.createObjectStore("messages", {
            keyPath: "id",
            autoIncrement: true,
          });
        }
        if (!db.objectStoreNames.contains("context")) {
          db.createObjectStore("context", { keyPath: "sessionId" });
        }
      };
      req.onsuccess = (e) => {
        this.db = e.target.result;
        resolve();
      };
      req.onerror = () => reject(req.error);
    });
  }

  async saveMessage(sessionId, role, content) {
    await this.ready;
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction("messages", "readwrite");
      tx.objectStore("messages").add({
        sessionId,
        role,
        content,
        timestamp: Date.now(),
      });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getMessages(sessionId) {
    await this.ready;
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction("messages", "readonly");
      const req = tx.objectStore("messages").getAll();
      req.onsuccess = () => {
        const msgs = req.result
          .filter((m) => m.sessionId === sessionId)
          .filter(
            (m) =>
              m.content &&
              !m.content.includes("skeleton-card") &&
              !m.content.includes("typing-status"),
          )
          .sort((a, b) => a.timestamp - b.timestamp)
          .slice(-50);
        resolve(msgs);
      };
      req.onerror = () => reject(req.error);
    });
  }

  async getSessionSummaries() {
    await this.ready;
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction("messages", "readonly");
      const req = tx.objectStore("messages").getAll();
      req.onsuccess = () => {
        const sessions = {};
        req.result.forEach((m) => {
          if (
            m.content &&
            !m.content.includes("skeleton-card") &&
            !m.content.includes("typing-status")
          ) {
            if (!sessions[m.sessionId]) {
              sessions[m.sessionId] = {
                id: m.sessionId,
                startTime: m.timestamp,
                lastTime: m.timestamp,
                messages: [],
              };
            }
            sessions[m.sessionId].messages.push(m);
            sessions[m.sessionId].lastTime = Math.max(
              sessions[m.sessionId].lastTime,
              m.timestamp,
            );
          }
        });
        const summaries = Object.values(sessions)
          .map((s) => {
            s.messages.sort((a, b) => a.timestamp - b.timestamp);
            const firstUserMsg = s.messages.find((m) => m.role === "user");
            const titleText = firstUserMsg
              ? firstUserMsg.content.replace(/<[^>]+>/g, "").trim()
              : "Shopping Session";
            const title =
              titleText.substring(0, 40) + (titleText.length > 40 ? "..." : "");
            return {
              id: s.id,
              startTime: s.startTime,
              lastTime: s.lastTime,
              title,
              count: s.messages.length,
            };
          })
          .sort((a, b) => b.lastTime - a.lastTime);
        resolve(summaries);
      };
      req.onerror = () => reject(tx.error);
    });
  }

  async pruneOldMessages(maxAgeMs = 24 * 60 * 60 * 1000) {
    await this.ready;
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction("messages", "readwrite");
      const store = tx.objectStore("messages");
      const req = store.getAll();
      const cutoff = Date.now() - maxAgeMs;
      req.onsuccess = () => {
        req.result
          .filter((m) => (m.timestamp || 0) < cutoff)
          .forEach((m) => store.delete(m.id));
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async clearMessages(sessionId) {
    await this.ready;
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction("messages", "readwrite");
      const store = tx.objectStore("messages");
      const req = store.getAll();
      req.onsuccess = () => {
        const toDelete = req.result.filter((m) => m.sessionId === sessionId);
        toDelete.forEach((m) => store.delete(m.id));
        resolve();
      };
      req.onerror = () => reject(req.error);
    });
  }

  async saveContext(sessionId, contextData) {
    await this.ready;
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction("context", "readwrite");
      tx.objectStore("context").put({ sessionId, ...contextData });
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  async getContext(sessionId) {
    await this.ready;
    return new Promise((resolve, reject) => {
      const tx = this.db.transaction("context", "readonly");
      const req = tx.objectStore("context").get(sessionId);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
}

// ============================================================
// Store (localStorage persistence)
// ============================================================
class Store {
  constructor() {
    this.sessionId = this._loadOrCreateSession();
    this.data = this._load();
  }

  _loadOrCreateSession() {
    try {
      localStorage.removeItem("rug_session_id");
    } catch (e) {
      /* ignore */
    }
    let id = sessionStorage.getItem("rug_session_id");
    if (!id) {
      id = crypto.randomUUID
        ? crypto.randomUUID()
        : "sess_" + Math.random().toString(36).slice(2) + Date.now();
      sessionStorage.setItem("rug_session_id", id);
      sessionStorage.setItem("rug_session_started_at", String(Date.now()));
    } else if (!sessionStorage.getItem("rug_session_started_at")) {
      sessionStorage.setItem("rug_session_started_at", String(Date.now()));
    }
    return id;
  }

  _load() {
    try {
      const raw =
        localStorage.getItem("rug_store") ||
        sessionStorage.getItem("rug_store");
      if (raw) return { ...this._defaults(), ...JSON.parse(raw) };
    } catch (e) {
      /* ignore corrupt data */
    }
    return this._defaults();
  }

  _defaults() {
    return {
      cart: [],
      wishlist: [],
      compare: [],
      viewed: [],
      facts: {},
      searchHistory: [],
      uploadedPhotos: [],

      // Canonical Behavior Context
      preferred_categories: [],
      preferred_attributes: {},
      interaction_flags: {
        room_uploaded: false,
        visualizer_used: false,
        filter_used: false,
        sort_used: false,
      },
      conversation_state: {
        intent: "browse",
        stage: "discovery",
        active_category: null,
      },
    };
  }

  save() {
    const json = JSON.stringify(this.data);
    localStorage.setItem("rug_store", json);
    sessionStorage.setItem("rug_store", json);
    if (window.app && window.app.db) {
      window.app.db
        .saveContext(this.sessionId, this.data)
        .catch((e) => console.warn("IDB context save failed", e));
    }
  }

  // --- Cart (Shopify API Integration) ---
  async init() {
    try {
      const res = await fetch("/cart.js");
      this._cart = await res.json();
      this._syncMemory();
    } catch (e) {
      console.warn("Failed to init Shopify cart:", e);
    }
  }

  _syncMemory() {
    if (!this._cart) return;
    this.data.cart = (this._cart.items || []).map((item, index) => ({
      key: item.key,
      line: index + 1,
      variant_id: item.variant_id,
      product_id: item.product_id,
      id: item.product_id, // For fallback compatibility
      size: item.variant_options ? item.variant_options[0] : null,
      title: item.title,
      handle: item.handle,
      url: item.url,
      product_url: item.url,
      qty: item.quantity,
      quantity: item.quantity,
      price: item.price / 100, // Shopify returns cents
      imgUrl: item.image,
      image: item.image,
    }));
    this.save();
  }

  // Resolve a real Shopify numeric variant ID from a product handle or id
  async _resolveVariantId(idOrHandle, preferredSize = "") {
    // If it's already a pure numeric ID (Shopify variant), use it directly
    if (/^\d+$/.test(String(idOrHandle))) return Number(idOrHandle);

    // Try to look it up from the product catalog for a handle
    const handle = idOrHandle;
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (res.ok) {
        const product = await res.json();
        if (product.variants && product.variants.length > 0) {
          const normalizedSize = String(preferredSize || "")
            .toLowerCase()
            .replace(/\s+/g, "");
          if (normalizedSize) {
            const bySize = product.variants.find((v) => {
              const title = String(v.title || "")
                .toLowerCase()
                .replace(/\s+/g, "");
              const options = (v.options || []).map((o) =>
                String(o || "")
                  .toLowerCase()
                  .replace(/\s+/g, ""),
              );
              return (
                v.available !== false &&
                (title.includes(normalizedSize) ||
                  options.includes(normalizedSize))
              );
            });
            if (bySize) return bySize.id;
          }
          // Return the first available variant, or just the first one.
          const available = product.variants.find((v) => v.available);
          return (available || product.variants[0]).id;
        }
      }
    } catch (e) {
      console.warn("Variant resolution from /products/ failed:", e);
    }

    // Fallback: try the numeric id as-is (will likely 422)
    return idOrHandle;
  }

  async addToCart(item, quantity = 1, properties = {}) {
    // Resolve variant ID: prefer explicit variant_id, else resolve from handle
    let variantId = item.variant_id;
    if (!variantId || !/^\d+$/.test(String(variantId))) {
      // item.id could be a product handle like "charleston-distressed-blue" or a product numeric id
      // item.handle is also possible
      const lookup = item.handle || item.id;
      variantId = await this._resolveVariantId(lookup, item.size);
    }

    try {
      const dawnCart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");
      let sections = [];
      if (dawnCart && typeof dawnCart.getSectionsToRender === "function") {
        sections = dawnCart.getSectionsToRender().map((s) => s.id);
      }

      const body = { id: Number(variantId), quantity, properties };
      if (sections.length > 0) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      console.log("[Discovery-AI] Adding to cart, variant:", variantId, body);

      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        console.error("Add to cart failed:", res.status, errBody);
        throw new Error(errBody?.description || "Add to cart failed");
      }

      const addResponse = await res.json();

      if (
        dawnCart &&
        typeof dawnCart.renderContents === "function" &&
        addResponse.sections
      ) {
        dawnCart.renderContents(addResponse);
      }

      const cartRes = await fetch("/cart.js");
      this._cart = await cartRes.json();
      this._syncMemory();

      // Update header badges and dispatch events to update theme cart components in real time
      const itemCount = this._cart.item_count || 0;
      const evtDetail = {
        resource: this._cart,
        sourceId: "shopping-agent",
        data: { itemCount, source: "shopping-agent" },
        item_count: itemCount,
      };

      document.dispatchEvent(new CustomEvent("cart:update", { bubbles: true, detail: evtDetail }));
      document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail: evtDetail }));
      window.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail: evtDetail }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail: evtDetail }));

      return { success: true, cart: this.data.cart };
    } catch (e) {
      console.error("addToCart error", e);
      return { success: false, error: e.message };
    }
  }

  async removeFromCart(variantId, size = "", lineKey = "") {
    try {
      const dawnCart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");
      let sections = [];
      if (dawnCart && typeof dawnCart.getSectionsToRender === "function") {
        sections = dawnCart.getSectionsToRender().map((s) => s.id);
      }

      const body = lineKey
        ? { id: lineKey, quantity: 0 }
        : { id: variantId, quantity: 0 };
      if (sections.length > 0) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      const res = await fetch("/cart/change.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error("Remove from cart failed");

      const changeResponse = await res.json();

      // To update Dawn's drawer on removal, we can re-render it if we have sections
      if (
        dawnCart &&
        dawnCart.classList.contains("is-empty") === false &&
        changeResponse.sections
      ) {
        // Dawn's cart-drawer doesn't have a direct render for change, but replacing innerHTML of sections works
        const cartDrawerWrapper = document.querySelector("cart-drawer");
        if (cartDrawerWrapper) {
          sections.forEach((id) => {
            const el = document.getElementById(id);
            if (el && changeResponse.sections[id]) {
              // Dawn section HTML parsing
              const html = new DOMParser().parseFromString(
                changeResponse.sections[id],
                "text/html",
              );
              const newEl = html.getElementById(id);
              if (newEl) el.innerHTML = newEl.innerHTML;
            }
          });
        }
      }

      const cartRes = await fetch("/cart.js");
      this._cart = await cartRes.json();
      this._syncMemory();
      document.dispatchEvent(
        new CustomEvent("cart:refresh", { detail: this._cart }),
      );
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:updated", { bubbles: true }),
      );
    } catch (e) {
      console.error("removeFromCart error", e);
    }
    return this.data.cart;
  }

  async updateCartQty(variantId, size, delta, lineKey = "") {
    // Find current qty
    const item = this.data.cart.find(
      (c) =>
        (lineKey && String(c.key) === String(lineKey)) ||
        String(c.variant_id) === String(variantId) ||
        String(c.id) === String(variantId),
    );
    if (item) {
      const newQty = Math.max(0, (item.quantity || 1) + delta);
      try {
        const res = await fetch("/cart/change.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id: lineKey || variantId, quantity: newQty }),
        });
        const cartRes = await fetch("/cart.js");
        this._cart = await cartRes.json();
        this._syncMemory();
        document.dispatchEvent(
          new CustomEvent("cart:refresh", { detail: this._cart }),
        );
      } catch (e) {
        console.error("updateCartQty error", e);
      }
    }
    return this.data.cart;
  }
  getCartTotal() {
    return this._cart ? this._cart.total_price / 100 : 0;
  }

  // --- Wishlist ---
  toggleWishlist(item) {
    const idx = this.data.wishlist.findIndex((w) => w.id === item.id);
    if (idx >= 0) {
      this.data.wishlist.splice(idx, 1);
    } else {
      this.data.wishlist.push(item);
    }
    this.save();
    return idx < 0; // true = added
  }
  isWishlisted(id) {
    return this.data.wishlist.some((w) => w.id === id);
  }

  // --- Compare ---
  addToCompare(item) {
    if (this.data.compare.length >= 4) return false;
    if (this.data.compare.some((c) => c.id === item.id)) return false;
    this.data.compare.push(item);
    this.save();
    return true;
  }
  removeFromCompare(id) {
    this.data.compare = this.data.compare.filter((c) => c.id !== id);
    this.save();
  }
  clearCompare() {
    this.data.compare = [];
    this.save();
  }

  // --- Viewed ---
  addViewed(item) {
    this.data.viewed = [
      item,
      ...this.data.viewed.filter((v) => v.id !== item.id),
    ].slice(0, 20);
    this.save();
  }

  // --- Facts ---
  updateFacts(newFacts, overwrite = false) {
    if (overwrite) {
      const filterKeys = [
        "color",
        "room",
        "budget",
        "style",
        "size",
        "size_bucket",
        "shape",
        "material",
        "pattern",
        "collection",
        "washable",
        "pet_friendly",
      ];
      filterKeys.forEach((k) => {
        if (!(k in newFacts)) {
          delete this.data.facts[k];
        }
      });
    }
    this.data.facts = { ...this.data.facts, ...newFacts };
    this.save();
  }

  addSearch(query) {
    this.data.searchHistory = [
      query,
      ...this.data.searchHistory.filter((item) => item !== query),
    ].slice(0, 50);
    this.save();
  }

  clearFacts(keys = []) {
    keys.forEach((key) => delete this.data.facts[key]);
    this.save();
  }

  addUploadedPhoto(file, facts = {}) {
    this.data.uploadedPhotos = [
      {
        name: file.name,
        type: file.type,
        size: file.size,
        analyzedAt: new Date().toISOString(),
        facts,
      },
      ...this.data.uploadedPhotos,
    ].slice(0, 10);
    this.save();
  }

  assistantMemory(messages = []) {
    return {
      viewed_products: this.data.viewed,
      compared_products: this.data.compare,
      budget: this.data.facts.budget || null,
      preferred_styles: this.data.facts.style || [],
      preferred_colors: this.data.facts.color || [],
      room_type: this.data.facts.room || null,
      search_history: this.data.searchHistory,
      uploaded_room_images: this.data.uploadedPhotos,
      cart_activity: this.data.cart,
      messages: messages.slice(-20).map((msg) => ({
        role: msg.role,
        content: String(msg.content || "")
          .replace(/\s+/g, " ")
          .trim()
          .slice(0, 1200),
        timestamp: msg.timestamp || null,
      })),
    };
  }

  getLastViewed() {
    return this.data.viewed?.[0] || null;
  }
}

// ============================================================
// UIRenderer — builds HTML strings (accessible, <img> based)
// ============================================================
class UIRenderer {
  esc(str) {
    if (!str) return "";
    // Decode any pre-existing HTML entities safely first
    const decoder = document.createElement("textarea");
    decoder.innerHTML = String(str);
    const decoded = decoder.value;
    
    // Then safely escape for HTML display
    const d = document.createElement("div");
    d.textContent = decoded;
    return d.innerHTML;
  }
  escAttr(str) {
    return this.esc(str).replace(/"/g, "&quot;").replace(/'/g, "&#39;");
  }
  escJS(str) {
    return this.esc(str)
      .replace(/\\/g, "\\\\")
      .replace(/'/g, "\\'")
      .replace(/"/g, "&quot;");
  }

  formatPrice(p) {
    if (!p) return "";
    const s = String(p);
    if (s.startsWith("$")) return s;
    const n = parseFloat(s.replace(/[^0-9.]/g, ""));
    return isNaN(n)
      ? s
      : `$${n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  priceNumber(value) {
    if (value === undefined || value === null || value === "") return 0;
    if (typeof value === "number") return value;
    const raw = String(value).replace(/,/g, "").trim();
    const n = parseFloat(raw.replace(/[^0-9.]/g, ""));
    if (isNaN(n)) return 0;
    return n;
  }

  priceBlock(product = {}) {
    const priceNum = this.priceNumber(
      product.price ||
        product.sale_price ||
        product.discounted_price ||
        product.final_price,
    );
    const compareNum = this.priceNumber(
      product.compare_at_price ||
        product.original_price ||
        product.regular_price ||
        product.msrp ||
        product.was_price,
    );
    const price = this.formatPrice(priceNum);
    if (compareNum && priceNum && compareNum > priceNum) {
      return `<div class="dai-chat-price dai-chat-price--sale">
                <s>${this.formatPrice(compareNum)}</s>
                <strong>${price}</strong>
            </div>`;
    }
    return price
      ? `<div class="dai-chat-price"><strong>${price}</strong></div>`
      : "";
  }

  loading(statusText) {
    const text = statusText || "Thinking…";
    return `<div class="typing-status" role="status" aria-label="${this.escAttr(text)}">
            <div class="status-icon"><svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="11"/><g transform="translate(12,12) rotate(45) scale(0.5) translate(-12,-12)"><path d="M18 3a3 3 0 0 0-3 3v12a3 3 0 0 0 3 3 3 3 0 0 0 3-3 3 3 0 0 0-3-3H6a3 3 0 0 0-3 3 3 3 0 0 0 3 3 3 3 0 0 0 3-3V6a3 3 0 0 0-3-3 3 3 0 0 0-3 3 3 3 0 0 0 3 3h12a3 3 0 0 0 3-3 3 3 0 0 0-3-3z"></path></g></svg></div>
            <span class="status-text">${this.esc(text)}</span>
        </div>`;
  }

  skeletonGrid(count = 3) {
    let cards = "";
    for (let i = 0; i < count; i++) {
      cards += `<div class="skeleton-card" style="animation-delay:${i * 40}ms">
                <div class="skeleton-img"></div>
                <div class="skeleton-body">
                    <div class="skeleton-line medium"></div>
                    <div class="skeleton-line short"></div>
                    <div class="skeleton-line price"></div>
                </div>
            </div>`;
    }
    return `<div class="chat-product-grid">${cards}</div>`;
  }

  browseLoading(query = "") {
    const label = query
      ? `Matching best rugs for "${query}"...`
      : "Matching best rugs for your room...";
    return `<div class="match-loader" role="status" aria-label="${this.escAttr(label)}">
            <div class="match-loader__loom"><span></span><span></span><span></span><span></span></div>
            <div><strong>${this.esc(label)}</strong><small>Checking color, size, material, price and availability</small></div>
        </div>`;
  }

  chatLoading(query = "") {
    const label = query
      ? "Reading product and store details..."
      : "Thinking...";
    return `<div class="chat-loader" role="status" aria-label="${this.escAttr(label)}">
            <span></span><span></span><span></span><strong>${this.esc(label)}</strong>
        </div>`;
  }

  // --- Welcome ---
  welcomePrompt() {
    return `<p class="welcome-prompt">Ask me to find rugs by size, color, room, budget, material, care needs, order status, or services.</p>`;
  }

  // --- Quick Chips ---
  chips(items) {
    if (!items || !Array.isArray(items)) return "";
    const validItems = items.filter(
      (i) => typeof i === "string" && i.trim().length > 0,
    );
    if (validItems.length === 0) return "";
    return `<div class="quick-actions">${validItems.map((i) => `<button class="chip action-chip-btn" data-action="${this.escAttr(i)}">${this.esc(i)}</button>`).join("")}</div>`;
  }

  toolMenu(items) {
    if (!items || !Array.isArray(items)) return "";
    return `<div class="tool-grid">
            ${items
              .map(
                (
                  item,
                ) => `<button class="tool-tile" data-app-action="openTool" data-arg0="${this.escAttr(item.id)}">
                <div class="tool-icon" style="margin-bottom:8px;color:var(--color-brass)">${item.icon}</div>
                <span>${this.esc(item.label)}</span>
            </button>`,
              )
              .join("")}
        </div>`;
  }

  // --- Horizontal Product Carousel ---
  horizontalProductCarousel(products, title = null) {
    window.app?.registerProducts(products);
    let html = "";
    if (title) {
      html += `<h4 class="carousel-title" style="margin-bottom:var(--space-xs); font-family:var(--font-heading);">${this.esc(title)}</h4>`;
    }
    html += `<div class="chat-carousel-container" style="position: relative; width: 100%; margin: var(--space-md) 0;">
            <button type="button" class="carousel-scroll-btn carousel-scroll-prev" aria-label="Scroll left" title="Scroll left" style="display: none;">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"></polyline></svg>
            </button>
            <div class="chat-product-carousel" style="display:flex; overflow-x:auto; gap:var(--space-md); padding-bottom:var(--space-sm); scroll-behavior:smooth; -webkit-overflow-scrolling:touch; width:100%; scrollbar-width: none;">
                ${products.map((p) => this.productCard(p, false, true)).join("")}
            </div>
            ${
              products.length > 1
                ? `<button type="button" class="carousel-scroll-btn carousel-scroll-next" aria-label="Scroll right" title="Scroll right">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"></polyline></svg>
            </button>`
                : ""
            }
        </div>`;
    return html;
  }

  // --- Product Grid (in-chat) ---
  productGrid(products, isRecommended = false) {
    window.app?.registerProducts(products);
    return `<div class="chat-product-grid">${products.map((p) => this.productCard(p, isRecommended)).join("")}</div>`;
  }

  // --- Curated Picks (AI-Recommended) ---
  curatedPicks(
    products,
    reason = "Based on your preferences",
    totalResults = 0,
  ) {
    if (!products || products.length === 0) return "";
    window.app?.registerProducts(products);
    const shown = Math.min(products.length, 3);
    const remaining = Math.max(0, totalResults - shown);

    let html = `<div class="curated-recommendation">
            <p class="curated-reason">${this.esc(reason)}</p>
            <div class="curated-products">
                ${products
                  .slice(0, 3)
                  .map((p) => this.productCard(p, true))
                  .join("")}
            </div>`;

    if (remaining > 0) {
      html += `<button class="discovery-btn btn-secondary" style="width:100%; margin-top:var(--space-md)" data-app-action="showAllResults"><i class="fa fa-arrow-right"></i> View all ${totalResults} results</button>`;
    }

    html += `</div>`;

    // Add contextual action chips
    html += `<div class="quick-actions" style="margin-top: var(--space-sm);">
            <button class="chip action-chip-btn" data-action="See in my room"><i class="fa fa-cube"></i> See in my room</button>
            <button class="chip action-chip-btn" data-action="Filter results"><i class="fa fa-sliders-h"></i> Filter results</button>
            <button class="chip action-chip-btn" data-action="More like these"><i class="fa fa-search"></i> More like these</button>
        </div>`;

    return html;
  }

  productCard(p, isCurated = false, isHorizontal = false) {
    const id = this.escAttr(p.id);
    let rawHandle = p.shopify_handle || p.handle || "";
    if (!rawHandle && (p.url || p.product_url || p.uri)) {
      const pUrl = p.product_url || p.url || p.uri || "";
      const m = pUrl.match(/\/products\/([^\/\?#]+)/);
      if (m) rawHandle = m[1];
    }
    if (!rawHandle && p.id && !/^\d+$/.test(String(p.id)) && !String(p.id).startsWith("rec_")) {
      rawHandle = String(p.id);
    }
    const cleanHandle = String(rawHandle || "")
      .replace(/^owfam-/, "")
      .replace(/^\/products\//, "")
      .split("?")[0]
      .split("#")[0]
      .trim();
    const handle = this.escAttr(cleanHandle || p.id);
    const title = this.esc(p.title);
    const safeTitle = this.escJS(p.title);

    const priceNum = this.priceNumber(
      p.price || p.sale_price || p.discounted_price || p.final_price,
    );
    const compareNum = this.priceNumber(
      p.compare_at_price ||
        p.original_price ||
        p.regular_price ||
        p.msrp ||
        p.was_price,
    );
    const priceMarkup = this.priceBlock(p);

    const discountPct =
      compareNum && compareNum > priceNum
        ? Math.round(((compareNum - priceNum) / compareNum) * 100)
        : null;

    const invQty = p.inventory_quantity || 0;
    const lowStock = invQty <= 5 && invQty > 0;

    const size = this.esc(
      p.size ||
        p.dimensions ||
        (p.tags && p.tags.find((t) => t.match(/\d+x\d+/i))) ||
        "8' x 10'",
    );
    const material = this.esc(
      p.material ||
        (p.tags &&
          p.tags.find((t) =>
            ["wool", "jute", "synthetic", "cotton"].includes(t.toLowerCase()),
          )) ||
        "Wool Blend",
    );
    const pileHeight = this.esc(p.pile_height || "Medium Pile");
    const vendor = this.esc(p.vendor || p.brand || "");

    const img = this.escAttr(p.image_url || p.imgUrl || p.image || "");
    const rawSizes = Array.isArray(p.sizes)
      ? p.sizes
      : Array.isArray(p.variants)
        ? p.variants.filter(v => v.available !== false).map((v) => v.title).filter(Boolean)
        : [];
    const sizes = rawSizes
      .filter((s) => s && String(s).toLowerCase() !== "default title")
      .slice(0, 6);

    const isRugPad =
      title.toLowerCase().includes("pad") ||
      (p.type && String(p.type).toLowerCase().includes("pad")) ||
      (p.tags && p.tags.some((t) => String(t).toLowerCase().includes("pad")));

    const cardClass = isCurated
      ? "chat-product-curated card-enter"
      : "chat-product card-enter";
    const productUrl = p.url || p.product_url || p.uri || `/products/${handle}`;

    const orientationClass = isHorizontal
      ? "layout-horizontal"
      : "layout-vertical";

    const tagHtml = `<div class="card-badge card-badge-texture" style="background: rgba(255,255,255,0.1); padding: 4px 8px; border-radius: 4px; font-size: 12px; text-transform: uppercase;">${material} &middot; ${pileHeight}</div>`;
    const discountBadge = discountPct
      ? `<span class="dai-badge-discount badge price__badge-sale color-accent-1" style="position:absolute; top:8px; left:8px; z-index:2;">${discountPct}% OFF</span>`
      : "";
    const lowStockBanner = lowStock
      ? `<div class="dai-low-stock" style="color: #e53935; font-size: 0.8rem; margin: 4px 0;">⚡ Only ${invQty} left!</div>`
      : "";

    if (cleanHandle && (!p.variants || p.variants.length === 0 || !p.options)) {
      const candidates = [cleanHandle, cleanHandle.replace(/^owfam-/, ''), 'owfam-' + cleanHandle.replace(/^owfam-/, '')].filter(Boolean);
      (async () => {
        let prodData = null;
        let matchedHandle = cleanHandle;
        for (const h of candidates) {
          try {
            const r = await fetch(`/products/${h}.js`);
            if (r.ok) {
              prodData = await r.json();
              matchedHandle = h;
              break;
            }
          } catch (_) {}
        }
        if (!prodData || !Array.isArray(prodData.variants) || prodData.variants.length === 0) return;

        p.variants = prodData.variants;
        p.options = prodData.options;
        p.handle = matchedHandle;
        if (window.app?.productCatalog) {
          window.app.productCatalog.set(String(p.id), p);
        }

        const updateDom = () => {
          const rows = Array.from(document.querySelectorAll('.result-size-row')).filter(
            (r) => r.getAttribute('data-product-id') === String(id) || r.getAttribute('data-handle') === cleanHandle || r.getAttribute('data-handle') === matchedHandle
          );
          if (rows.length > 0) {
            const sizeIdx = Array.isArray(prodData.options)
              ? prodData.options.findIndex((o) => (typeof o === "string" ? o : o.name || "").toLowerCase() === "size")
              : -1;
            const extractedSizes = [];
            prodData.variants.forEach((v) => {
              if (v.available === false) return;
              const vOpts = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter(Boolean);
              let sVal = sizeIdx !== -1 && vOpts[sizeIdx] ? vOpts[sizeIdx] : v.option1 || v.title || "";
              if (sVal && String(sVal).toLowerCase() !== "default title") {
                extractedSizes.push(sVal);
              }
            });
            const uniqueSizes = [...new Set(extractedSizes)].slice(0, 6);
            if (uniqueSizes.length > 0) {
              rows.forEach((sizeRow) => {
                if (sizeRow.children.length === 0) {
                  sizeRow.innerHTML = uniqueSizes
                    .map(
                      (s) =>
                        `<button type="button" class="result-size-chip" data-app-action="selectResultSize" data-arg0="${id}" data-arg1="${this.escAttr(s)}" style="padding: 2px 8px; font-size: 11px; border: 1px solid rgba(255,255,255,0.2); border-radius: 4px; background: transparent; color: #ccc; cursor: pointer; transition: all 0.2s;">${this.esc(s)}</button>`
                    )
                    .join("");
                  sizeRow.style.display = "flex";
                }
              });
            }
          }

          const seeBtns = Array.from(document.querySelectorAll('.see-in-room-btn')).filter(
            (b) => b.getAttribute('data-id') === String(id) || b.getAttribute('data-product-handle') === cleanHandle || b.getAttribute('data-product-handle') === matchedHandle
          );
          seeBtns.forEach((seeBtn) => {
            seeBtn.setAttribute("data-product-handle", matchedHandle);
            const firstVar = prodData.variants.find((v) => v.available) || prodData.variants[0];
            if (firstVar) {
              seeBtn.setAttribute("data-variant-id", firstVar.id);
              const vImg = firstVar.featured_image?.src || firstVar.featured_image;
              if (vImg) seeBtn.setAttribute("data-rug", vImg);
            }
          });
        };

        updateDom();
        setTimeout(updateDom, 150);
        setTimeout(updateDom, 500);
      })();
    }

    return `<div class="${cardClass} ${orientationClass}" data-product-id="${id}" style="margin-bottom: 12px; border-radius: var(--radius-md); overflow: hidden; position: relative;">
            ${discountBadge}
            <div class="chat-product-img-wrapper" style="background-color: #f5f5f5; padding: 12px; display: flex; align-items: center; justify-content: center;">
                <a href="${productUrl}" class="view-product-btn" data-id="${id}" data-url="${productUrl}" style="display: block; width: 100%; height: 100%;">
                    <img src="${img}" alt="${title}" class="main-img" loading="lazy" style="border-radius: 4px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); width: 100%; height: 100%; object-fit: cover; max-height: 200px;" onerror="if(this.dataset.retried){this.style.display='none';}else{this.dataset.retried='true';if(this.src.includes('owimages.com')){this.src=this.src.replace('https://www.owimages.com/hr_3200/','https://cdn.shopify.com/s/files/1/0728/3549/8122/files/');}}">
                </a>
            </div>
            <div class="chat-product-body surface-commerce" style="padding: 12px; background: #1a1a1a; color: white;">
                ${p.reason ? `<div class="recommendation-reason" style="font-size: 12px; color: #b89f71; font-weight: 600; margin-bottom: 8px; display: flex; align-items: center; gap: 6px;"><i class="fa fa-magic"></i> ${this.esc(p.reason)}</div>` : ""}
                <div class="card-badges-row" style="display: flex; gap: 8px; margin-bottom: 8px;">
                    ${tagHtml}
                </div>
                ${vendor ? `<div class="meta-vendor" style="font-size: 12px; text-transform: uppercase; color: #aaa; margin-bottom: 4px;">${vendor}</div>` : ""}
                <h4 class="product-title" style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600;">
                    <a href="${productUrl}" class="view-product-btn" data-id="${id}" data-url="${productUrl}" style="color: white; text-decoration: none;">${title}</a>
                </h4>
                
                ${priceMarkup}

                ${lowStockBanner}

                <style>.result-size-chip.selected { background: #fff !important; color: #000 !important; font-weight: 600; border-color: #fff !important; }</style>
                <div class="result-size-row" data-product-id="${id}" data-handle="${handle}" aria-label="Available sizes" style="display: ${sizes.length ? 'flex' : 'none'}; flex-wrap: wrap; gap: 4px; margin-top: 8px;">${sizes.map((s) => `<button type="button" class="result-size-chip" data-app-action="selectResultSize" data-arg0="${id}" data-arg1="${this.escAttr(s)}" style="padding: 2px 8px; font-size: 11px; border: 1px solid rgba(255,255,255,0.2); border-radius: 4px; background: transparent; color: #ccc; cursor: pointer; transition: all 0.2s;">${this.esc(s)}</button>`).join("")}</div>
                
                <div class="chat-product-actions" style="margin-top: 12px; display: flex; flex-direction: column; gap: 8px; width: 100%;">
                    <button class="discovery-btn btn-primary add-to-cart-dominant" data-app-action="quickAddProduct" data-arg0="${id}" style="width: 100%; background: #ffffff; color: #000000; font-weight: 700; border-radius: 50px; padding: 10px; display: flex; align-items: center; justify-content: center; gap: 6px; border: none; cursor: pointer; font-size: 13px;">
                        <i class="fa fa-plus"></i> Add to cart
                    </button>
                    ${
                      !isRugPad
                        ? `<button class="discovery-btn btn-ghost see-in-room-btn" data-app-action="seeProductInRoom" data-arg0="${id}" data-id="${id}" data-product-handle="${handle}" data-rug="${img}" data-size="${size}" data-is-chat-card="true" style="width: 100%; border: 1px solid rgba(255, 255, 255, 0.35); color: #ffffff; background: transparent; border-radius: 50px; padding: 10px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 13px; font-weight: 600;" title="See in my room">
                        <i class="fa fa-cube" style="margin-right: 6px;"></i> See in my room
                    </button>`
                        : ""
                    }
                </div>
            </div>
        </div>`;
  }

  recentlyViewed(items) {
    if (!items.length) {
      return this.knowledgeCard(
        "Recently Viewed",
        "<p>Your recently viewed rugs will appear here as you browse.</p>",
        "fa-clock-rotate-left",
      );
    }
    return `<div class="knowledge-card">
            <h4><i class="fa fa-clock-rotate-left" style="color:var(--color-accent)" aria-hidden="true"></i> Recently Viewed</h4>
            <div class="recent-list">
                ${items
                  .map(
                    (
                      item,
                    ) => `<button class="recent-item" data-app-action="openStoredProduct" data-arg0="${this.escAttr(item.id)}">
                    <img src="${this.escAttr(item.imgUrl || item.image_url || "")}" alt="${this.esc(item.title)}" loading="lazy" onerror="this.style.display='none'">
                    <span><strong>${this.esc(item.title)}</strong><small>${this.formatPrice(item.price)}</small></span>
                </button>`,
                  )
                  .join("")}
            </div>
        </div>`;
  }

  confidenceCard(score, reasons, product) {
    return `<div class="confidence-card">
            <div class="confidence-score" aria-label="Buying confidence score ${score} out of 100">
                <span>${score}</span><small>/100</small>
            </div>
            <div>
                <h4>Buying Confidence Score</h4>
                <p>${product ? `For <strong>${this.esc(product.title)}</strong>, based on your current product context, browsing activity, and saved preferences.` : "Based on your current browsing activity and saved preferences."}</p>
                <ul>${reasons.map((reason) => `<li>${this.esc(reason)}</li>`).join("")}</ul>
            </div>
        </div>`;
  }

  // --- Compare Card Grid ---
  compareTable(items) {
    return this.knowledgeCard(
      "Selection guidance",
      "<p>Product comparison is turned off for this store. I can help choose by size, material, price, care, or rug pad fit.</p>",
      "fa-lightbulb",
    );
    if (items.length < 2)
      return `<div class="compare-card"><p>Add at least 2 products to compare. Use the <strong>compare</strong> button on product cards.</p></div>`;
    const attrs = ["Price", "Size", "Color", "Style", "Material"];
    const keys = ["price", "size", "color", "style", "material"];

    // Detect which attributes differ across items
    const differs = keys.map((k) => {
      const vals = items.map((p) =>
        String(p[k] || "")
          .toLowerCase()
          .trim(),
      );
      return new Set(vals).size > 1;
    });

    return `<div class="compare-card">
            <h4><i class="fa fa-lightbulb" aria-hidden="true"></i> Selection Guidance</h4>
            <div class="compare-grid" role="table" aria-label="Selection guidance">
                ${items
                  .map(
                    (p) => `<div class="compare-column">
                    <div class="compare-column-img">
                        <img src="${this.escAttr(p.imgUrl || p.image_url || "")}" alt="${this.esc(p.title)}" loading="lazy" onload="this.classList.add('loaded')">
                    </div>
                    <div class="compare-column-title">${this.esc(p.title)}</div>
                    <div class="compare-column-remove">
                        <button class="discovery-btn btn-ghost btn-sm" style="font-size:0.65rem; padding:2px 6px;" data-app-action="removeCompare" data-arg0="${this.escAttr(p.id)}"><i class="fa fa-times"></i> Remove</button>
                    </div>
                    <div class="compare-attrs">
                        ${attrs
                          .map(
                            (
                              a,
                              i,
                            ) => `<div class="compare-attr-row${differs[i] ? " differs" : ""}">
                            <span class="compare-attr-label">${a}</span>
                            <span class="compare-attr-value">${this.esc(keys[i] === "price" ? this.formatPrice(p[keys[i]]) : p[keys[i]] || "—")}</span>
                        </div>`,
                          )
                          .join("")}
                    </div>
                </div>`,
                  )
                  .join("")}
            </div>
            <div style="margin-top:var(--space-md)"><button class="discovery-btn btn-ghost btn-sm" data-app-action="clearCompare">Clear All</button></div>
        </div>`;
  }

  // --- Knowledge Cards ---
  knowledgeCard(title, content, icon) {
    return `<div class="knowledge-card"><h4><i class="fa ${icon || "fa-info-circle"}" style="color:var(--color-accent)" aria-hidden="true"></i> ${this.esc(title)}</h4><div class="knowledge-content">${content}</div></div>`;
  }

  materialCard(mat) {
    const pros = (mat.pros || [])
      .map((p) => `<li class="knowledge-pros">${this.esc(p)}</li>`)
      .join("");
    const cons = (mat.cons || [])
      .map((c) => `<li class="knowledge-cons">${this.esc(c)}</li>`)
      .join("");
    return this.knowledgeCard(
      mat.name,
      `
            <p><strong>Durability:</strong> ${this.esc(mat.durability)} · <strong>Feel:</strong> ${this.esc(mat.feel)}</p>
            <p><strong>Price:</strong> ${this.esc(mat.price_range)} · <strong>Best for:</strong> ${this.esc((mat.best_rooms || []).join(", "))}</p>
            <p>${mat.pet_friendly ? "✅ Pet-friendly" : "❌ Not pet-friendly"} · ${mat.kid_friendly ? "✅ Kid-safe" : "❌ Not ideal for kids"} · ${mat.washable ? "✅ Washable" : "❌ Not machine washable"}</p>
            <p><strong>Care:</strong> ${this.esc(mat.cleaning)}</p>
            ${pros ? `<p><strong>Pros:</strong></p><ul>${pros}</ul>` : ""}
            ${cons ? `<p><strong>Cons:</strong></p><ul>${cons}</ul>` : ""}
        `,
      "fa-gem",
    );
  }

  sizeGuideCard(guide) {
    const rows = (guide.recommendations || [])
      .map(
        (r) =>
          `<tr><td>${this.esc(r.room_size)}</td><td><strong>${this.esc(r.rug_size)}</strong></td><td>${this.esc(r.note)}</td></tr>`,
      )
      .join("");
    return `<div class="knowledge-card"><h4><i class="fa fa-ruler-combined" style="color:var(--color-accent)" aria-hidden="true"></i> ${this.esc(guide.title)}</h4>
            <div class="size-tip"><i class="fa fa-lightbulb" aria-hidden="true"></i> ${this.esc(guide.tip)}</div>
            <table class="size-table"><thead><tr><th>Room / Furniture</th><th>Rug Size</th><th>Tip</th></tr></thead><tbody>${rows}</tbody></table>
        </div>`;
  }

  serviceCard(svc) {
    return this.knowledgeCard(
      svc.name,
      `
            <p>${this.esc(svc.description)}</p>
            <p><strong>Turnaround:</strong> ${this.esc(svc.turnaround)}</p>
            ${svc.appointment_required ? `<button class="discovery-btn btn-accent btn-sm" style="margin-top:var(--space-sm)" data-app-action="sendAction" data-arg0="Book an appointment"><i class="fa fa-calendar-alt"></i> Book Appointment</button>` : ""}
        `,
      "fa-concierge-bell",
    );
  }

  servicesList(services) {
    const items = services
      .map(
        (
          s,
        ) => `<div style="padding:var(--space-sm) 0;border-bottom:1px solid var(--color-border)">
            <strong>${this.esc(s.name)}</strong><br><span style="font-size:var(--text-sm);color:var(--color-text-secondary)">${this.esc(s.description)}</span><br>
            <span style="font-size:var(--text-xs);color:var(--color-text-muted)">Turnaround: ${this.esc(s.turnaround)}</span>
        </div>`,
      )
      .join("");
    return this.knowledgeCard("Our Services", items, "fa-store");
  }

  // --- Visualizer ---
  visualizerUpload(mode) {
    const isVisualizing = mode === "visualize";
    return `<div class="visualizer-card">
            <h4><i class="${isVisualizing ? "fa-cube" : "fa-camera"}" style="color:var(--color-accent)" aria-hidden="true"></i> ${isVisualizing ? "See this rug in your room" : "Analyze my room"}</h4>
            <p class="viz-desc">${isVisualizing ? "Upload a room photo and I will place the selected rug into the space." : "Upload a room photo and I will detect room style, colors, and layout, then suggest matching rugs."}</p>
            <div class="visualizer-drop viz-drop" role="button" tabindex="0" data-mode="${mode}" aria-label="Click or drag to upload room photo">
                <div class="viz-upload-icon"><i class="fa fa-cloud-upload-alt" aria-hidden="true"></i></div>
                <strong>Upload room photo</strong>
                <span>Drag a photo here, or choose one from your device.</span>
                <span class="discovery-btn btn-secondary btn-sm viz-file-label"><i class="fa fa-image"></i> Choose room photo</span>
                <small>JPEG, PNG, WEBP - max 10MB</small>
            </div>
            <input type="file" class="viz-file" accept="image/jpeg,image/png,image/webp" style="display:none" aria-label="Select room photo">
            <button class="discovery-btn btn-accent viz-analyze" disabled style="width:100%"><i class="fa fa-magic"></i> ${isVisualizing ? "See in My Room" : "Analyze and Suggest Rugs"}</button>
        </div>`;
  }

  roomAnalysis(data) {
    const facts = data.facts || data.payload?.facts || data.filter_state || {};
    const tags = Object.entries(facts)
      .filter(([k]) => !k.startsWith("_"))
      .map(([k, v]) => {
        const val = Array.isArray(v) ? v.join(", ") : v;
        return `<span class="fact-tag"><i class="fa fa-tag" aria-hidden="true"></i>${this.esc(k)}: ${this.esc(val)}</span>`;
      })
      .join("");
    return `<div class="room-analysis">
            <h4><i class="fa fa-check-circle" aria-hidden="true"></i> Room Analysis Complete</h4>
            <p>${this.esc(data.text || "Analysis complete")}</p>
            <div class="fact-tags">${tags}</div>
        </div>`;
  }

  // --- Appointment ---
  appointmentForm() {
    return `<div class="appointment-card">
            <h4><i class="fa fa-comments" style="color:var(--color-accent)" aria-hidden="true"></i> Contact Store Team</h4>
            <form id="appointment-form" class="app-managed-form" data-form-type="appointment">
                <div class="form-row">
                    <div class="form-group"><label for="appt-name">Full Name <span aria-hidden="true">*</span></label>
                        <input id="appt-name" name="full_name" class="form-control" placeholder="e.g. Jane Smith" required></div>
                    <div class="form-group"><label for="appt-phone">Phone Number <span aria-hidden="true">*</span></label>
                        <input id="appt-phone" name="phone" class="form-control" type="tel" placeholder="e.g. (281) 719-0487" required></div>
                </div>
                <div class="form-group"><label for="appt-email">Email <span>(optional)</span></label>
                    <input id="appt-email" name="email" class="form-control" type="email" placeholder="e.g. jane@email.com"></div>
                <div class="form-group"><label for="appt-service">Service Needed <span>(optional)</span></label>
                    <select id="appt-service" name="service" class="form-control" required>
                        <option value="">Select a service</option>
                        <option value="cleaning">Professional Rug Cleaning</option>
                        <option value="repair">Rug Repair</option>
                        <option value="restoration">Rug Restoration</option>
                        <option value="appraisal">Rug Appraisal</option>
                        <option value="custom_rugs">Custom Rugs</option>
                        <option value="consultation">Consultation</option>
                    </select></div>
                <div class="form-group"><label for="appt-notes">Short Description / Notes <span>(optional)</span></label>
                    <textarea id="appt-notes" name="notes" class="form-control" rows="4" placeholder="Tell us more about your rug or project..."></textarea></div>
                <div class="form-group"><label for="appt-photo">Upload Photo <span>(optional)</span></label>
                    <label class="service-upload-box" for="appt-photo"><i class="fa fa-image"></i><strong>Click to upload or drag and drop</strong><small>JPG, PNG up to 10MB</small></label>
                    <input id="appt-photo" name="photo" type="file" accept="image/jpeg,image/png" style="display:none"></div>
                <button type="submit" class="discovery-btn btn-accent" style="width:100%"><i class="fa fa-arrow-right"></i> Send to Store Team</button>
            </form>
            <p class="viz-note"><i class="fa fa-info-circle"></i> We will pass these details to the store team and open Shopify Inbox when it is available.</p>
        </div>`;
  }

  serviceConfirmationCard(req) {
    return `<div class="confirmation-card interactive-service-card" style="background: #1a1a1a; border: 1px solid #333; border-radius: 12px; padding: 16px; margin-top: 8px; color: #fff;">
            <h4 style="margin-top: 0; color: #4caf50; display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600;">
                <i class="fa fa-check-circle"></i> Service Request Received
            </h4>
            <p style="font-size: 13px; color: #aaa; margin-bottom: 12px;">Our store team will reach out to you shortly.</p>
            <div style="background: rgba(255,255,255,0.06); padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 12px; display: flex; flex-direction: column; gap: 4px;">
                <div><strong style="color:#ddd;">Service:</strong> ${this.esc(req.service || "Rug Service")}</div>
                <div><strong style="color:#ddd;">Name:</strong> ${this.esc(req.name || "")}</div>
                <div><strong style="color:#ddd;">Phone:</strong> ${this.esc(req.phone || "")}</div>
            </div>
            <div class="service-card-actions" style="display: flex; gap: 8px; flex-wrap: wrap;">
                <button class="discovery-btn btn-secondary btn-sm" data-app-action="openShopifyInbox"><i class="fa fa-comments"></i> Chat Live</button>
                <button class="discovery-btn btn-ghost btn-sm" data-app-action="startServiceFlow"><i class="fa fa-redo"></i> Request Another Service</button>
            </div>
        </div>`;
  }

  // --- Order Track ---
  orderTrackForm() {
    return `<div class="appointment-card">
            <h4><i class="fa fa-truck" style="color:var(--color-accent)" aria-hidden="true"></i> Track My Order</h4>
            <p style="font-size:var(--text-sm);color:var(--color-text-secondary);margin-bottom:12px;">Enter the order number and checkout email. We use both to keep order details private.</p>
            <form id="order-track-form" class="app-managed-form" data-form-type="track-order">
                <div class="form-group" style="margin-bottom:8px;">
                    <label for="track-order-id">Order Number</label>
                    <input type="text" id="track-order-id" placeholder="#1001" required style="width:100%; padding:8px; border:1px solid var(--color-border); border-radius:4px;">
                </div>
                <div class="form-group" style="margin-bottom:12px;">
                    <label for="track-email">Checkout Email</label>
                    <input type="email" id="track-email" placeholder="you@example.com" required style="width:100%; padding:8px; border:1px solid var(--color-border); border-radius:4px;">
                </div>
                <button type="submit" class="discovery-btn btn-accent" style="width:100%"><i class="fa fa-search"></i> Track Order</button>
            </form>
        </div>`;
  }

  inboxHandoffCard(body, shopEmail, handoffId, inboxOpened, copied) {
    return `<div class="confirmation-card compact">
            <h4><i class="fa fa-comments" aria-hidden="true"></i> Shopify Chat Handoff Ready</h4>
            <p>${inboxOpened ? "I opened Shopify Chat." : "Open Shopify Chat to send this to the store team."} ${copied ? "Your message is copied." : "Copy your message, then send it in chat."}</p>
            ${handoffId ? `<p class="text-muted" style="font-size:var(--text-xs)">Handoff ID: ${this.esc(handoffId)}</p>` : ""}
            <textarea class="form-control" rows="5" readonly style="margin:10px 0">${this.esc(body)}</textarea>
            <div class="btn-group" style="display:flex;gap:8px;flex-wrap:wrap">
                <button class="discovery-btn btn-accent btn-sm" data-app-action="openShopifyInbox"><i class="fa fa-comments"></i> Open Chat</button>
                <button class="discovery-btn btn-secondary btn-sm" data-app-action="copyHandoffMessage" data-arg0="${this.escAttr(body)}"><i class="fa fa-copy"></i> Copy Message</button>
                <a class="discovery-btn btn-secondary btn-sm" href="mailto:${encodeURIComponent(shopEmail)}?subject=${encodeURIComponent("Service Request")}&body=${encodeURIComponent(body)}"><i class="fa fa-envelope"></i> Email Instead</a>
            </div>
        </div>`;
  }

  orderStatusCard(order) {
    const statusEmoji =
      {
        processing: "🔄",
        shipped: "🚚",
        delivered: "✅",
        cancelled: "❌",
      }[order.status?.toLowerCase()] || "📦";

    const trackingLink = order.tracking_url
      ? `<a href="${order.tracking_url}" target="_blank" class="discovery-btn btn-primary btn-sm" style="margin-top:12px; display:inline-block;">Track Package →</a>`
      : "";

    return `<div class="confirmation-card compact">
            <h4>${statusEmoji} ${this.esc(order.status_label || order.status)}</h4>
            <div style="font-size: 0.9rem; margin-top: 8px;">
                <div><strong>Order #${this.esc(order.order_number)}</strong> &middot; ${order.item_count || 1} item(s)</div>
                ${order.estimated_delivery ? `<div>ETA: ${this.esc(order.estimated_delivery)}</div>` : ""}
            </div>
            ${trackingLink}
        </div>`;
  }

  orderStatusCard(order) {
    const status =
      order.fulfillment_status ||
      order.status_label ||
      order.status ||
      "Processing";
    const trackingUrl =
      order.tracking_url ||
      (Array.isArray(order.tracking_urls) ? order.tracking_urls[0] : "");
    const trackingNumber =
      order.tracking_number ||
      (Array.isArray(order.tracking_numbers) ? order.tracking_numbers[0] : "");
    const items = Array.isArray(order.line_items) ? order.line_items : [];
    const normalizedStatus = String(status || "").toLowerCase();
    const currentStep = /delivered/.test(normalizedStatus)
      ? 3
      : /shipped|fulfilled|in_transit|out_for_delivery/.test(normalizedStatus)
        ? 2
        : 1;
    const statusLabel =
      currentStep === 3
        ? "Delivered"
        : currentStep === 2
          ? "On the way"
          : "Confirmed";
    const statusCopy =
      currentStep === 3
        ? "Your order has been delivered."
        : currentStep === 2
          ? "Your order is on the way."
          : "We're preparing these items for shipping.";
    const steps = ["Confirmed", "Shipped", "Delivered"]
      .map((label, index) => {
        const step = index + 1;
        const state =
          step < currentStep
            ? "done"
            : step === currentStep
              ? "active"
              : "pending";
        return `<div class="dai-order-step ${state}">
                <span class="dai-order-dot">${step < currentStep ? '<i class="fa fa-check"></i>' : ""}</span>
                <span>${label}</span>
            </div>`;
      })
      .join("");
    const itemRows = items.length
      ? `<div class="dai-order-items">
            ${items
              .map((item) => {
                const productUrl = item.product_url || item.url || "";
                const itemImage =
                  item.image || item.image_url || item.imgUrl || "";
                const itemBody = `
                <div class="dai-order-thumb">${itemImage ? `<img src="${this.escAttr(itemImage)}" alt="${this.escAttr(item.title || "Item")}" loading="lazy" onerror="this.remove(); this.parentElement.classList.add('is-missing'); this.parentElement.textContent='No image';">` : "<span>No image</span>"}</div>
                <div class="dai-order-item-main">
                    <strong>${this.esc(item.title || "Item")}</strong>
                    ${item.variant_title ? `<span>${this.esc(item.variant_title)}</span>` : ""}
                    <small>Qty: ${this.esc(item.quantity || 1)}${item.price ? ` - ${this.formatPrice(item.price)}` : ""}</small>
                </div>
                ${productUrl ? '<i class="fa fa-chevron-right dai-order-item-arrow" aria-hidden="true"></i>' : ""}`;
                return productUrl
                  ? `<a class="dai-order-item dai-order-item-link" href="${this.escAttr(productUrl)}" aria-label="View ${this.escAttr(item.title || "order item")}">${itemBody}</a>`
                  : `<div class="dai-order-item">${itemBody}</div>`;
              })
              .join("")}
        </div>`
      : "";
    const total = order.total_price
      ? `<div class="dai-order-total"><span>Total</span><strong>${this.esc(order.currency || "USD")} ${this.formatPrice(order.total_price)}</strong></div>`
      : "";
    const trackingLink = trackingUrl
      ? `<a href="${this.escAttr(trackingUrl)}" target="_blank" class="dai-order-action primary">Track package</a>`
      : "";

    return `<div class="dai-order-card">
            <div class="dai-order-head">
                <div>
                    <div class="dai-order-kicker">Order status</div>
                    <h4>${this.esc(statusLabel)}</h4>
                    <p>${this.esc(statusCopy)}</p>
                </div>
                <div class="dai-order-badge">${this.esc(status)}</div>
            </div>
            <div class="dai-order-progress">${steps}</div>
            <div class="dai-order-meta">
                ${order.order_number ? `<span><strong>Order #${this.esc(order.order_number)}</strong></span>` : ""}
                <span>${this.esc(order.item_count || items.length || 1)} item(s)</span>
                ${order.financial_status ? `<span>Payment: ${this.esc(order.financial_status)}</span>` : ""}
                ${trackingNumber ? `<span>Tracking: ${this.esc(trackingNumber)}</span>` : ""}
            </div>
            ${itemRows}
            ${total}
            <div class="dai-order-actions">
                ${order.order_status_url ? `<a href="${this.escAttr(order.order_status_url)}" target="_blank" class="dai-order-action">View order</a>` : ""}
                <button class="dai-order-action" data-app-action="sendAction" data-arg0="what is in my cart">View cart</button>
                <button class="dai-order-action" data-app-action="sendAction" data-arg0="care instructions">Care tips</button>
            </div>
            ${trackingLink}
        </div>`;
  }

  cartStateCardLegacy(cartItems) {
    if (!cartItems || !cartItems.length) {
      return `<div class="knowledge-card">Your cart is empty.</div>`;
    }
    const rows = cartItems
      .map(
        (item) => `
            <div class="dai-cart-item" style="display:flex; gap:12px; margin-bottom:12px; align-items:center;">
                <img src="${this.escAttr(item.image || item.imgUrl)}" alt="${this.escAttr(item.title)}" width="48" height="48" style="border-radius:4px; object-fit:cover;" />
                <div class="dai-cart-item-info" style="flex:1;">
                    <div style="font-size:0.9rem; font-weight:bold;">${this.esc(item.title)}</div>
                    <div style="font-size:0.8rem; color:#aaa;">Qty: ${item.quantity || item.qty} — $${Number(item.price).toFixed(2)}</div>
                </div>
                <div class="dai-cart-item-actions" style="display:flex; flex-direction:column; gap:4px;">
                    <button class="discovery-btn btn-ghost btn-sm" style="padding:2px 8px; font-size:0.7rem;" data-app-action="removeFromCart" data-arg0="${item.variant_id || item.id}">Remove</button>
                    <button class="discovery-btn btn-secondary btn-sm" style="padding:2px 8px; font-size:0.7rem;" data-app-action="showSimilar" data-arg0="${item.product_id || item.id}">Find Similar</button>
                </div>
            </div>
        `,
      )
      .join("");
    return `<div class="knowledge-card"><h4><i class="fa fa-shopping-cart"></i> Your Cart</h4>${rows}</div>`;
  }

  cartStateCard(cartItems) {
    if (!cartItems || !cartItems.length) {
      return `<div class="knowledge-card">Your cart is empty.</div>`;
    }
    const itemCount = cartItems.reduce(
      (sum, item) => sum + Number(item.quantity || item.qty || 1),
      0,
    );
    const subtotal = cartItems.reduce((sum, item) => {
      const qty = Number(item.quantity || item.qty || 1);
      return sum + this.priceNumber(item.price) * qty;
    }, 0);
    const rows = cartItems
      .map(
        (item) => `
            <div class="dai-cart-item" style="display:flex; gap:12px; margin-bottom:12px; align-items:center;">
                ${item.image || item.imgUrl ? `<img src="${this.escAttr(item.image || item.imgUrl)}" alt="${this.escAttr(item.title)}" width="48" height="48" style="border-radius:4px; object-fit:cover;" />` : ""}
                <div class="dai-cart-item-info" style="flex:1;">
                    <div style="font-size:0.9rem; font-weight:bold;">${this.esc(item.title)}</div>
                    ${item.size && String(item.size).toLowerCase() !== "default title" ? `<div style="font-size:0.78rem; color:#aaa;">Size: ${this.esc(item.size)}</div>` : ""}
                    <div style="font-size:0.8rem; color:#aaa;">Qty: ${this.esc(item.quantity || item.qty || 1)} - ${this.formatPrice(item.price)}</div>
                </div>
                <div class="dai-cart-item-actions" style="display:flex; flex-direction:column; gap:4px;">
                    <button class="discovery-btn btn-ghost btn-sm" style="padding:2px 8px; font-size:0.7rem;" data-app-action="removeFromCart" data-arg0="${item.variant_id || item.id}">Remove</button>
                    <button class="discovery-btn btn-secondary btn-sm" style="padding:2px 8px; font-size:0.7rem;" data-app-action="showSimilar" data-arg0="${item.product_id || item.id}">Find Similar</button>
                </div>
            </div>
        `,
      )
      .join("");
    return `<div class="knowledge-card">
            <h4><i class="fa fa-shopping-cart"></i> Your Cart</h4>
            <p style="margin:0 0 12px; color:#aaa;">${itemCount} item${itemCount === 1 ? "" : "s"} - Subtotal ${this.formatPrice(subtotal)}</p>
            ${rows}
        </div>`;
  }

  productActionCard(product) {
    if (!product) return "";
    const url =
      product.url ||
      product.product_url ||
      (product.handle || product.shopify_handle
        ? `/products/${product.handle || product.shopify_handle}`
        : "");
    if (!url) return "";
    let rawImg =
      product.image_url ||
      product.imgUrl ||
      product.image ||
      product.featured_image ||
      (product.selected_variant &&
        (product.selected_variant.featured_image ||
          product.selected_variant.image_url)) ||
      "";
    if (typeof rawImg === "object" && rawImg !== null) {
      rawImg = rawImg.src || rawImg.url || "";
    }
    if (!rawImg && this._getActiveProduct()) {
      const active = this._getActiveProduct();
      rawImg = active.imgUrl || active.image_url || active.image || "";
    }
    if (!rawImg && window.location.pathname.includes("/products/")) {
      rawImg = this._extractPDPImageFromDOM();
    }
    const img = rawImg;
    const title = product.title || "Selected rug";
    const priceMarkup = this.priceBlock(product);
    const meta = [
      product.material,
      product.size,
      product.style || product.color,
    ]
      .filter(Boolean)
      .join(" · ");
    const description =
      product.description ||
      product.summary ||
      `${title} is ready to view, add to cart, or preview in your room.`;
    const productId = this.escAttr(product.id || product.handle || product.shopify_handle || "");
    return `<div class="dai-product-action-card">
            ${
              img
                ? `<a class="dai-product-action-image" href="${this.escAttr(url)}" aria-label="View ${this.escAttr(title)}">
                <img src="${this.escAttr(img)}" alt="${this.escAttr(title)}" loading="lazy" onerror="this.closest('.dai-product-action-image').style.display='none'">
            </a>`
                : ""
            }
            <div class="dai-product-action-body">
                ${meta ? `<div class="dai-product-action-meta">${this.esc(meta)}</div>` : ""}
                <a class="dai-product-action-title" href="${this.escAttr(url)}">${this.esc(title)}</a>
                ${priceMarkup}
                <p class="dai-product-action-copy">${this.esc(description).slice(0, 150)}</p>
                <div class="dai-product-action-buttons">
                    <button class="dai-product-action-link primary" data-app-action="quickAddProduct" data-arg0="${productId}">Add to my cart</button>
                </div>
            </div>
        </div>`;
  }

  cartCareCard(cartItems, title = "Care instructions for your cart") {
    if (!cartItems || !cartItems.length)
      return this.knowledgeCard(
        title,
        "<p>Your cart is empty. Add a rug and I can give care guidance for it.</p>",
        "fa-droplet",
      );
    const rows = cartItems
      .map((item) => {
        const text = `${item.title || ""} ${item.material || ""}`.toLowerCase();
        let care =
          "Vacuum regularly without a beater bar, rotate occasionally, and blot spills immediately with a clean dry cloth.";
        if (/polyester|polypropylene|synthetic|washable/.test(text))
          care =
            "Vacuum regularly to keep it fresh, and blot any spills quickly with a dry cloth. For deeper cleans, spot clean with a mild detergent and water, or machine wash on a gentle cycle if applicable.";
        if (/wool/.test(text))
          care =
            "Vacuum gently without a beater bar to avoid shedding. Blot spills immediately with a clean cloth, avoid soaking, and use professional cleaning for deep stains.";
        if (/jute|sisal|natural/.test(text))
          care =
            "Vacuum regularly to remove dirt. Keep the rug dry and avoid wet cleaning, as natural fibers can stain or warp when exposed to moisture. Blot spills immediately.";
        return `<li><strong>${this.esc(item.title)}</strong>: ${this.esc(care)}</li>`;
      })
      .join("");
    return this.knowledgeCard(title, `<ul>${rows}</ul>`, "fa-droplet");
  }

  stainCareCard() {
    return this.knowledgeCard(
      "Quick stain care",
      `
            <ul>
                <li>Blot the stain immediately with a clean, dry white cloth. Do not rub.</li>
                <li>Keep the area as dry as possible until the rug material is confirmed.</li>
                <li>For wool, silk, antique, pet odor, water damage, or deep stains, request professional cleaning.</li>
            </ul>
            <p style="margin-top:8px">Upload a photo in the service form so the store can review the rug condition.</p>
        `,
      "fa-droplet",
    );
  }

  rugPadAdviceCard(cartItems) {
    if (!cartItems || !cartItems.length)
      return this.knowledgeCard(
        "Rug pad",
        "<p>Your cart is empty. Add a rug and I can recommend the right pad size.</p>",
        "fa-layer-group",
      );
    const rows = cartItems
      .map((item) => {
        const size =
          item.size && String(item.size).toLowerCase() !== "default title"
            ? ` for ${this.esc(item.size)}`
            : "";
        return `<li><strong>${this.esc(item.title)}</strong>: yes, use a rug pad${size}. It helps prevent slipping, protects the floor, and makes the rug feel cushier.</li>`;
      })
      .join("");
    return this.knowledgeCard(
      "Do you need a rug pad?",
      `<ul>${rows}</ul><p style="margin-top:8px">For best fit, choose a pad slightly smaller than the rug so it stays hidden under the edges.</p>`,
      "fa-layer-group",
    );
  }

  rugPadFallbackCard() {
    return this.knowledgeCard(
      "Rug pads",
      `
            <p>Use a rug pad slightly smaller than your rug to help prevent slipping, protect the floor, and add cushion.</p>
            <div class="btn-group" style="margin-top:var(--space-sm)">
                <a class="discovery-btn btn-accent btn-sm" href="/search?q=rug%20pad"><i class="fa fa-search"></i> Search rug pads</a>
                <a class="discovery-btn btn-secondary btn-sm" href="/collections/rug-pads"><i class="fa fa-layer-group"></i> Rug Pads collection</a>
            </div>
        `,
      "fa-layer-group",
    );
  }

  // --- Cart Item Added ---
  cartAdded(title) {
    return `<div class="confirmation-card compact">
            <p><i class="fa fa-check-circle" style="color:var(--color-success)" aria-hidden="true"></i> <strong>${this.esc(title)}</strong> added to cart!</p>
            <div class="btn-group" style="margin-top:var(--space-sm)">
                <button class="discovery-btn btn-accent btn-sm" data-app-action="sendAction" data-arg0="What rug pad should I use?">Add Rug Pad</button>
                <button class="discovery-btn btn-secondary btn-sm" data-app-action="toggleDrawer">Continue Shopping</button>
            </div>
        </div>`;
  }
}

// ============================================================
// Homepage Product Card Builder
// ============================================================
function buildHomepageCard(p) {
  const ui = new UIRenderer();
  window.app?.registerProducts([p]);
  const id = ui.escAttr(p.id);
  const handle = ui.escAttr(p.handle || p.shopify_handle || p.id || "");
  const title = ui.esc(p.title);
  const price = ui.formatPrice(p.price);
  const style = ui.esc(p.style || "");
  const color = ui.esc(p.color || "");
  const img = ui.escAttr(p.image_url || "");
  const wishlisted = window.app?.store.isWishlisted(p.id) ? " wishlisted" : "";
  const isRugPad =
    title.toLowerCase().includes("pad") ||
    (p.type && String(p.type).toLowerCase().includes("pad"));

  return `<div class="product-card" data-app-action="openProduct" data-arg0="${id}">
        <div class="product-card-image">
            <img src="${img}" alt="${title}" loading="lazy" onerror="this.style.display='none'">
            <div class="product-card-overlay" onclick="event.stopPropagation()">
                <div class="hover-sizes">${p.sizes ? (Array.isArray(p.sizes) ? p.sizes.join(", ") : p.sizes) : p.size || "Size available"}</div>
                <div class="overlay-actions">
                    <button class="overlay-btn${wishlisted}" aria-label="Save to wishlist" data-app-action="toggleWishlistHP" data-arg0="${id}" data-arg1="${ui.escJS(p.title)}" data-arg2="${ui.escJS(p.price)}" data-arg3="${img}"><i class="fa fa-heart"></i></button>
                    <button class="overlay-btn" aria-label="Quick add to cart" data-app-action="quickAddProduct" data-arg0="${id}"><i class="fa fa-shopping-bag"></i></button>
                </div>
            </div>
        </div>
        <div class="product-card-body">
            <h3 class="product-card-title">${title}</h3>
            <p class="product-card-price">${price}</p>
            <div class="product-card-actions" onclick="event.stopPropagation()">
                <button class="discovery-btn btn-primary btn-sm" data-app-action="openProduct" data-arg0="${id}">View Product</button>
                <button class="discovery-btn btn-secondary btn-sm" data-app-action="quickAddProduct" data-arg0="${id}">Add to Cart</button>
                ${!isRugPad ? `<button class="discovery-btn btn-ghost btn-sm see-in-room-btn" data-app-action="seeProductInRoom" data-arg0="${id}" data-id="${id}" data-product-handle="${handle}" data-rug="${img}" data-size="${ui.escAttr(p.size || "")}" data-is-chat-card="true">See in my room</button>` : ""}
            </div>
            <p class="product-card-meta">${[style, color].filter(Boolean).join(" · ")}</p>
        </div>
    </div>`;
}

// ============================================================
// Main Application
// ============================================================
(function () {
  try {
    const pending = sessionStorage.getItem("pending_see_in_room");
    if (pending) {
      sessionStorage.removeItem("pending_see_in_room");
      window.pendingSeeInRoom = JSON.parse(pending);
    }
  } catch (e) {}
})();

class App {
  constructor() {
    this.api = new DiscoveryAPI(API_BASE);
    this.db = new AgentDB();
    this.store = new Store();
    this.ui = new UIRenderer();

    // State
    this.drawerOpen = false;
    this.cartOpen = false;
    this.contextProduct = null;
    if (
      window.ShopifyContextPayload &&
      window.ShopifyContextPayload.product &&
      window.ShopifyContextPayload.template === "product"
    ) {
      const p = window.ShopifyContextPayload.product;
      this.contextProduct = this._productFromShopifyPayload(p);
    } else if (
      window.location.pathname.includes("/products/") &&
      window.meta &&
      window.meta.product
    ) {
      const p = window.meta.product;
      this.contextProduct = {
        id: String(p.id),
        handle:
          (window.location.pathname.split("/products/")[1] || "").split(
            /[?/#]/,
          )[0] || "",
        title:
          p.variants?.[0]?.name?.split(" - ")?.[0] ||
          window.document.title.split(" - ")[0] ||
          "",
        size: p.variants?.[0]?.public_title || "",
        price: p.variants?.[0]?.price || p.price || 0,
        imgUrl:
          p.variants?.[0]?.featured_image?.src ||
          p.featured_image ||
          p.image ||
          "",
        url: window.location.pathname,
        collection: p.vendor || "",
        vendor: p.vendor || "",
        brand: p.vendor || "",
        material: this._materialFromProductPayload(p),
        style: p.type || "",
      };
    }
    this.productCatalog = new Map();
    if (this.contextProduct) {
      const domDetails = this._extractPDPDetailsFromDOM();
      if (!this.contextProduct.origin && domDetails.origin) {
        this.contextProduct.origin = this.contextProduct.country_of_origin = this.contextProduct.made_in = domDetails.origin;
      }
      if (!this.contextProduct.material && domDetails.material) {
        this.contextProduct.material = domDetails.material;
      }
      if (!this.contextProduct.construction && domDetails.construction) {
        this.contextProduct.construction = domDetails.construction;
      }
      if (window.location.pathname.includes("/products/")) {
        this.pdpProduct = { ...this.contextProduct };
      }
      this.productCatalog.set(this.contextProduct.id, this.contextProduct);
    }
    this.sending = false;
    this._previousView = "home";
    this.currentBrowseResults = [];
    this.currentSearchResults = [];
    this.lastShownProducts = [];
    this.pendingCartIntent = null;
    this.pendingVisualizerIntent = null;
    this.pendingServiceRequest = null;
    this.toolsOpen = false;
    this.tools = [
      {
        id: "analyze",
        label: "Analyze my room",
        icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 10.5 12 4l9 6.5"></path><path d="M5 10v9h14v-9"></path><path d="M9 19v-5h6v5"></path><path d="m8 7 8 10"></path></svg>',
      },
      {
        id: "visualizer",
        label: "See in my room",
        icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>',
      },
      {
        id: "track-order",
        label: "Track My Order",
        icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"></rect><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon><circle cx="5.5" cy="18.5" r="2.5"></circle><circle cx="18.5" cy="18.5" r="2.5"></circle></svg>',
      },
      {
        id: "services",
        label: "Services",
        icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.1-3.1a6 6 0 0 1-7.9 7.9l-5.6 5.6a2.1 2.1 0 0 1-3-3l5.6-5.6a6 6 0 0 1 7.9-7.9l-3.1 3.1z"></path></svg>',
      },
      {
        id: "contact-store",
        label: "Contact Store",
        icon: '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"></path></svg>',
      },
    ];
    this.pdpTools = this.tools;

    // DOM refs
    this.drawer = document.getElementById("assistant-drawer");
    this.chatArea = document.getElementById("chat-area");
    this.chatInput = document.getElementById("chat-input");
    this.sendBtn = document.getElementById("send-btn");
    this.menuBtn = document.getElementById("assistant-menu-btn");
    this.toolMenu = document.getElementById("assistant-tool-menu");
    this.fab = document.getElementById("assistant-fab");
    this.fabText = document.getElementById("assistant-fab-text");
    this.liveContext = document.getElementById("assistant-live-context");
    this.overlay = document.getElementById("discovery-overlay");
    this.cartDrawer = document.getElementById("assistant-cart-panel") || document.getElementById("cart-drawer");

    this.historyBtn = document.getElementById("assistant-history-btn");
    this.historyArea = document.getElementById("history-area");
    this.historyList = document.getElementById("history-list");
    this.newChatBtn = document.getElementById("new-chat-btn");
    this.chatInputBar = document.querySelector(".chat-input-bar");
    this.scrollIndicator = document.getElementById("chat-scroll-indicator");

    this._bindEvents();
    this._renderToolMenu();
    this._updateLiveContext();
    this._loadClientKnowledge();
    this._startFabPromos();
    this._loadHomepage();
    this._updateCartUI();

    // Recommendation engine, dashboard and gesture handler
    try {
      this.recommender = new RecommendationEngine(this.store);
      this.dashboard = new PersonalDashboard(this.store, this.recommender);
      this.gestures = new GestureHandler();

      // Render dashboard and initialize carousels (if present)
      try {
        this.dashboard.renderFullDashboard();
      } catch (e) {
        console.warn("Dashboard init failed:", e);
      }
      try {
        const carouselEl = document.getElementById("pdp-reco-carousel");
        if (carouselEl) this.gestures.initCarousel(carouselEl);
      } catch (e) {
        console.warn("Gesture init failed:", e);
      }
    } catch (e) {
      console.warn("Optional frontend modules not available:", e);
    }

    if (this.historyBtn) {
      this.historyBtn.addEventListener("click", () => this.toggleHistoryView());
    }
    if (this.newChatBtn) {
      this.newChatBtn.addEventListener("click", () => this.startNewChat());
    }
  }

  async _restoreChatHistory() {
    try {
      this.db
        .pruneOldMessages(30 * 24 * 60 * 60 * 1000)
        .catch((e) => console.warn("IDB message prune failed", e));
      const msgs = await this.db.getMessages(this.store.sessionId);
      if (msgs && msgs.length > 0) {
        this.chatArea.innerHTML = "";
        msgs
          .filter((m) => !this._isStoreHandoffStatus(m?.content))
          .forEach((m) => {
            const div = document.createElement("div");
            div.className = `message ${m.role}`;
            div.innerHTML = m.content;
            this.chatArea.appendChild(div);
          });
        this.chatArea.scrollTop = this.chatArea.scrollHeight;
        this._chatRestored = true;
      } else {
        this._chatRestored = false;
      }
    } catch (e) {
      console.warn("Failed to restore chat history", e);
      this._chatRestored = false;
    }
  }

  async toggleHistoryView() {
    const isHidden = this.historyArea.hasAttribute("hidden");
    if (isHidden) {
      this.chatArea.setAttribute("hidden", "");
      if (this.chatInputBar) this.chatInputBar.setAttribute("hidden", "");
      this.historyArea.style.display = "flex";
      this.historyArea.removeAttribute("hidden");
      const recoRail = document.getElementById("picked-for-you-rail");
      if (recoRail) recoRail.setAttribute("hidden", "");

      this.historyList.innerHTML =
        '<div style="text-align:center; padding: 20px;"><i class="fa fa-spinner fa-spin"></i> Loading...</div>';

      try {
        const sessions = await this.db.getSessionSummaries();
        if (sessions.length === 0) {
          this.historyList.innerHTML =
            '<div style="text-align:center; padding: 20px; color: #666;">No previous chat history found.</div>';
          return;
        }

        let html =
          '<div style="display: flex; flex-direction: column; gap: 8px;">';
        sessions.forEach((s) => {
          const date = new Date(s.lastTime).toLocaleString(undefined, {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          });
          const isActive = s.id === this.store.sessionId;
          html += `
                        <div class="history-item" style="padding: 12px; border: 1px solid ${isActive ? "var(--color-primary)" : "#eee"}; border-radius: 8px; cursor: pointer; background: ${isActive ? "rgba(var(--color-primary-rgb), 0.05)" : "#fff"}; transition: all 0.2s;" data-session-id="${this.ui.escAttr(s.id)}">
                            <div style="font-weight: 600; font-size: 14px; margin-bottom: 4px;">${this.ui.esc(s.title)}</div>
                            <div style="font-size: 12px; color: #666; display: flex; justify-content: space-between;">
                                <span>${date}</span>
                                <span>${s.count} msgs</span>
                            </div>
                        </div>
                    `;
        });
        html += "</div>";

        this.historyList.innerHTML = html;

        this.historyList.querySelectorAll(".history-item").forEach((item) => {
          item.addEventListener("click", () =>
            this.loadSession(item.getAttribute("data-session-id")),
          );
        });
      } catch (e) {
        console.error("Failed to load history", e);
        this.historyList.innerHTML =
          '<div style="text-align:center; padding: 20px; color: red;">Failed to load history.</div>';
      }
    } else {
      this.historyArea.style.display = "none";
      this.historyArea.setAttribute("hidden", "");
      this.chatArea.removeAttribute("hidden");
      if (this.chatInputBar) this.chatInputBar.removeAttribute("hidden");
      this.chatArea.scrollTop = this.chatArea.scrollHeight;
    }
  }

  async loadSession(sessionId) {
    if (sessionId !== this.store.sessionId) {
      this.store.sessionId = sessionId;
      sessionStorage.setItem("rug_session_id", sessionId);
      this.chatArea.innerHTML = "";
      await this._restoreChatHistory();
    }
    this.toggleHistoryView();
  }

  async startNewChat() {
    const id = crypto.randomUUID
      ? crypto.randomUUID()
      : "sess_" + Math.random().toString(36).slice(2) + Date.now();
    this.store.sessionId = id;
    sessionStorage.setItem("rug_session_id", id);
    sessionStorage.setItem("rug_session_started_at", String(Date.now()));
    this.store.data.conversation_state = {
      intent: "browse",
      stage: "discovery",
      active_category: null,
    };
    this.store.save();

    this.chatArea.innerHTML = "";
    this.toggleHistoryView();

    const welcomeHtml = `
            <div class="welcome-card" style="margin-bottom: var(--space-md);">
                <h3>Welcome to ${window.ShopifyContextPayload?.shop?.name || "our store"} AI! ✨</h3>
                <p>I'm your personal shopping assistant. I can help you find products, answer questions, or give recommendations based on your preferences.</p>
                <div class="suggestion-chips" style="margin-top: var(--space-sm);">
                    <button class="discovery-btn btn-outline btn-sm" data-app-action="sendAction" data-arg0="Show me trending items">Show me trending items</button>
                    <button class="discovery-btn btn-outline btn-sm" data-app-action="sendAction" data-arg0="Help me find a gift">Help me find a gift</button>
                </div>
            </div>
        `;
    this._addMsg(welcomeHtml, "assistant", true);
  }

  _startFabPromos() {
    // Disabled static promos to allow dynamic ticker logic to run
  }

  _materialFromProductPayload(product = {}) {
    const direct =
      product.material || product.metafields?.custom?.material || "";
    if (
      direct &&
      !/collection:|construction:|shape normalized:/i.test(String(direct))
    )
      return String(direct);

    const tags = Array.isArray(product.tags) ? product.tags : [];
    const known = [
      "polyester",
      "wool",
      "cotton",
      "jute",
      "viscose",
      "silk",
      "polypropylene",
      "nylon",
      "acrylic",
      "seagrass",
      "sisal",
    ];
    const materialTag = tags.find((tag) =>
      known.includes(String(tag).trim().toLowerCase()),
    );
    if (materialTag) return materialTag;

    const haystack = [
      product.description,
      product.product_description,
      tags.join("; "),
    ]
      .filter(Boolean)
      .join("; ");
    const match = haystack.match(/\bmaterial\s*:\s*([^,;.]+)/i);
    return match ? match[1].trim() : "";
  }

  _getActiveProduct() {
    if (window.location.pathname.includes("/products/") && this.pdpProduct) {
      return this.pdpProduct;
    }
    return (
      this.pdpProduct ||
      this.contextProduct ||
      (this.store && typeof this.store.getLastViewed === "function"
        ? this.store.getLastViewed()
        : null) ||
      null
    );
  }

  _extractPDPImageFromDOM() {
    try {
      const vizBtn = document.querySelector(
        "#pdp-see-in-room, .pdp-view-in-room .see-in-room-btn, [data-rug]",
      );
      if (vizBtn) {
        const rug = vizBtn.getAttribute("data-rug") || vizBtn.dataset.rug;
        if (rug) return rug;
      }
      const imgEl = document.querySelector(
        ".product__media img, .product-single__photo img, .product-gallery img, .featured-media img, img[src*='/products/']",
      );
      if (imgEl) {
        const src = imgEl.src || imgEl.dataset.src || imgEl.srcset?.split(" ")[0];
        if (src) return src;
      }
    } catch (e) {
      console.warn("DOM image extract error:", e);
    }
    return "";
  }

  _extractPDPDetailsFromDOM() {
    const details = {
      origin: "",
      country_of_origin: "",
      made_in: "",
      material: "",
      construction: "",
    };
    try {
      const textNodes = Array.from(
        document.querySelectorAll(
          ".product-single__description, .product__description, .product-specifications, .product-details, .tech-specs, [data-product-details]",
        ),
      );
      if (document.body) textNodes.push(document.body);
      for (const node of textNodes) {
        const text = node.innerText || node.textContent || "";
        if (!details.origin) {
          const originMatch = text.match(
            /(?:origin|country of origin|made in|manufactured in)\s*:\s*([A-Za-z\s]+)/i,
          );
          if (originMatch)
            details.origin =
              details.country_of_origin =
              details.made_in =
                originMatch[1].trim();
        }
        if (!details.material) {
          const matMatch = text.match(
            /(?:material|composition|content|fiber)\s*:\s*([A-Za-z0-9\s,\/]+)/i,
          );
          if (matMatch) details.material = matMatch[1].trim();
        }
        if (!details.construction) {
          const constMatch = text.match(
            /(?:construction|technique|weaving|make)\s*:\s*([A-Za-z0-9\s,\/]+)/i,
          );
          if (constMatch) details.construction = constMatch[1].trim();
        }
      }
    } catch (e) {
      console.warn("Error extracting PDP details from DOM", e);
    }
    return details;
  }

  _productFromShopifyPayload(p = {}) {
    const selectedVariant =
      this._selectedVariantForPayload(p) || p.variant || {};
    const domDetails = this._extractPDPDetailsFromDOM();
    const product = {
      id: String(p.id),
      handle: p.handle,
      title: p.title || p.name || "",
      imgUrl:
        selectedVariant.featured_image ||
        selectedVariant.image_url ||
        p.image_url ||
        p.featured_image ||
        p.image ||
        "",
      collection: p.collection || "",
      vendor: p.vendor || "",
      brand: p.brand || p.vendor || "",
      origin: p.origin || p.country_of_origin || p.made_in || domDetails.origin || "",
      country_of_origin: p.country_of_origin || p.origin || p.made_in || domDetails.country_of_origin || "",
      made_in: p.made_in || p.origin || p.country_of_origin || domDetails.made_in || "",
      construction: p.construction || domDetails.construction || "",
      manufacturer: p.manufacturer || "",
      url: p.url || window.location.pathname,
      variants: p.variants || [],
      options: p.options || [],
      material: p.material || this._materialFromProductPayload(p) || domDetails.material || "",
      style: p.type || "",
      selected_variant: selectedVariant,
    };
    return this._applyVariantToProduct(product, selectedVariant, false);
  }

  _selectedVariantForPayload(product = {}) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const selectedId =
      this._selectedVariantIdFromPage() ||
      product.variant?.id ||
      product.selected_variant?.id;
    if (selectedId) {
      const match = variants.find(
        (variant) => String(variant.id) === String(selectedId),
      );
      if (match) return match;
    }
    return (
      product.variant ||
      product.selected_variant ||
      variants.find((variant) => variant.available) ||
      variants[0] ||
      null
    );
  }

  _selectedVariantIdFromPage() {
    const selectors = [
      'form[action*="/cart/add"] input[name="id"]',
      'form[action*="/cart/add"] select[name="id"]',
      'input[name="id"]',
      'select[name="id"]',
    ];
    for (const selector of selectors) {
      const el = document.querySelector(selector);
      if (el?.value) return el.value;
    }
    try {
      return (
        new URLSearchParams(window.location.search || "").get("variant") || ""
      );
    } catch (_) {
      return "";
    }
  }

  _variantOption(product = {}, variant = {}, optionName = "") {
    const wanted = String(optionName || "").toLowerCase();
    const options = Array.isArray(product.options) ? product.options : [];
    const index = options.findIndex(
      (option) => String(option.name || option).toLowerCase() === wanted,
    );
    if (index >= 0) return variant[`option${index + 1}`] || "";
    if (wanted === "size")
      return variant.title &&
        String(variant.title).toLowerCase() !== "default title"
        ? variant.title
        : "";
    return "";
  }

  _applyVariantToProduct(product = {}, variant = {}, explicitSize = false) {
    if (!product) return product;
    const next = { ...product };
    const selected = variant || {};
    next.selected_variant = selected;
    next.variant_id = selected.id || next.variant_id;
    next.size =
      this._variantOption(next, selected, "Size") ||
      selected.title ||
      next.size ||
      "";
    next.shape =
      this._variantOption(next, selected, "Shape") || next.shape || "";
    next.color =
      this._variantOption(next, selected, "Color") || next.color || "";
    if (selected.price !== undefined && selected.price !== null)
      next.price = selected.price;
    else if (next.price === undefined || next.price === null) next.price = 0;
    next.compare_at_price =
      selected.compare_at_price ||
      next.compare_at_price ||
      next.original_price ||
      next.regular_price;
    next.original_price = next.original_price || next.compare_at_price;
    if (selected.available !== undefined) next.available = selected.available;
    if (selected.inventory_quantity !== undefined)
      next.inventory_quantity = selected.inventory_quantity;
    if (selected.featured_image || selected.image_url) {
      const fi = selected.featured_image;
      next.imgUrl = (fi && typeof fi === 'object' && fi.src) ? fi.src : (fi || selected.image_url);
    }
    if (explicitSize) {
      next.sizeSelected = true;
      next.selectedSizeExplicit = true;
    }
    return next;
  }

  _syncSelectedVariantContext(explicitSize = true) {
    if (!this.contextProduct?.variants?.length) return;
    const variantId = this._selectedVariantIdFromPage();
    if (!variantId) return;
    const variant = this.contextProduct.variants.find(
      (item) => String(item.id) === String(variantId),
    );
    if (!variant) return;
    this.contextProduct = this._applyVariantToProduct(
      this.contextProduct,
      variant,
      explicitSize,
    );
    if (this.contextProduct.id)
      this.productCatalog.set(
        String(this.contextProduct.id),
        this.contextProduct,
      );
    if (window.ShopifyContextPayload?.product) {
      window.ShopifyContextPayload.product.variant = variant;
      window.ShopifyContextPayload.product.price = variant.price;
    }
    this._updateLiveContext();
    this._syncContext();
  }

  _updateLiveContext() {
    if (!this.liveContext) return;
    const product = this.contextProduct;
    if (product && product.title) {
      let metaParts = [];
      if (product.shape) metaParts.push(product.shape.toLowerCase());
      if (product.size) metaParts.push(product.size);
      if (product.color) metaParts.push(product.color);

      let metaText = metaParts.join(" / ");

      const isWashable =
        product.tags &&
        product.tags.some(
          (t) => typeof t === "string" && t.toLowerCase().includes("washable"),
        );
      if (isWashable) {
        metaText = metaText ? metaText + " | Washable" : "Washable";
      }

      this.liveContext.hidden = false;
      this.liveContext.innerHTML = `
                <span class="live-dot" aria-hidden="true"></span>
                <span class="live-label">Viewing</span>
                <strong>${this.ui.esc(product.title)}</strong>
                ${metaText ? `<small>${this.ui.esc(metaText)}</small>` : ""}
            `;
      return;
    }
    this.liveContext.hidden = true;
  }

  registerProducts(products = []) {
    this.lastShownProductIds = products.map((p) => String(p.id));
    this.lastShownProducts = products.slice(0, 12);
    products.forEach((product) => {
      if (product?.id) this.productCatalog.set(String(product.id), product);
    });
  }

  _visibleResultProducts() {
    const seen = new Set();
    const products = [];
    const cards = Array.from(
      this.chatArea?.querySelectorAll("[data-product-id]") || [],
    );
    cards.forEach((card) => {
      const id = String(card.getAttribute("data-product-id") || "");
      if (!id || seen.has(id)) return;
      const product = this.productCatalog.get(id);
      if (product) {
        seen.add(id);
        products.push(product);
      }
    });
    return products.length ? products : this.lastShownProducts || [];
  }

  _recentProductHistory() {
    const seen = new Set();
    const sources = [
      ...(this.store.data.viewed || []),
      ...(this.contextProduct ? [this.contextProduct] : []),
      ...(this._visibleResultProducts() || []),
      ...(this.lastShownProducts || []),
      ...Array.from(this.productCatalog.values()),
    ];
    return sources
      .filter((product) => {
        const key = String(
          product?.id || product?.handle || product?.title || "",
        );
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return product?.title;
      })
      .slice(0, 12);
  }

  _compactProductForMemory(product = {}) {
    return {
      id: product.id,
      title: product.title,
      handle: product.handle || product.shopify_handle,
      url: product.url || product.product_url,
      price: product.price,
      compare_at_price:
        product.compare_at_price ||
        product.original_price ||
        product.regular_price ||
        product.msrp ||
        product.was_price,
      original_price:
        product.original_price ||
        product.compare_at_price ||
        product.regular_price ||
        product.msrp ||
        product.was_price,
      size: product.size,
      color: product.color,
      style: product.style,
      material: product.material,
      vendor: product.vendor,
      brand: product.brand,
      origin: product.origin || product.country_of_origin || product.made_in,
      country_of_origin:
        product.country_of_origin || product.origin || product.made_in,
      made_in: product.made_in || product.origin || product.country_of_origin,
      manufacturer: product.manufacturer,
      image_url: product.image_url || product.imgUrl || product.image,
      selected_variant: product.selected_variant,
      variant_id: product.variant_id,
      variants: (product.variants || []).slice(0, 30).map((variant) => ({
        id: variant.id,
        title: variant.title,
        available: variant.available,
        price: variant.price,
        compare_at_price: variant.compare_at_price,
        option1: variant.option1,
        option2: variant.option2,
        option3: variant.option3,
      })),
      options: product.options,
    };
  }

  _detectShopifyContext() {
    const shopifyCtx = {
      is_shopify: !!window.Shopify,
      path: window.location.pathname,
      url: window.location.href,
    };

    // Deeply merge ShopifyContextPayload if available
    if (window.ShopifyContextPayload) {
      // Create a shallow copy of the payload to avoid mutating the global object
      const payloadCopy = { ...window.ShopifyContextPayload };

      // Strip large product lists to prevent massive API payloads (context issues)
      if (payloadCopy.collection) {
        payloadCopy.collection = { ...payloadCopy.collection };
        delete payloadCopy.collection.products;
      }
      if (payloadCopy.search) {
        payloadCopy.search = { ...payloadCopy.search };
        delete payloadCopy.search.results;
      }

      Object.assign(shopifyCtx, payloadCopy);
    }

    // Extract Shopify meta product if on a PDP and not provided by Payload
    if (!shopifyCtx.product && window.meta && window.meta.product) {
      shopifyCtx.product = window.meta.product;
    }

    // Extract from Shopify Analytics
    if (
      window.Shopify &&
      window.Shopify.analytics &&
      window.Shopify.analytics.meta
    ) {
      const page = window.Shopify.analytics.meta.page;
      if (page) {
        shopifyCtx.page_type = shopifyCtx.page_type || page.pageType;
        if (page.resourceId) shopifyCtx.resource_id = page.resourceId;
      }
    }

    return shopifyCtx;
  }

  async _pageContext() {
    const product = this._getActiveProduct();
    const shopifyContext = this._detectShopifyContext();

    // Scrape PDP recommendations if on PDP
    let pdpRecommendations = [];
    if (window.location.pathname.includes("/products/")) {
      try {
        const recContainer = document.querySelector(".product-recommendations, .related-products, #shopify-section-product-recommendations, .similar-products, [id*='product-recommendations']");
        if (recContainer) {
          const productAnchors = recContainer.querySelectorAll("a[href*='/products/']");
          const seenHandles = new Set();
          productAnchors.forEach(anchor => {
            const card = anchor.closest('.card-wrapper, .product-card-wrapper, .grid__item') || anchor.parentElement;
            const url = anchor.getAttribute("href");
            const handleMatch = url.match(/\/products\/([^?/#]+)/);
            if (!handleMatch) return;
            const handle = handleMatch[1];
            
            if (seenHandles.has(handle) || (product && (product.handle === handle || product.id === handle))) return;
            seenHandles.add(handle);
            
            const titleEl = card.querySelector(".card__heading, .card-information__text, .product-title, .h3, .h4, .title");
            const title = titleEl ? titleEl.textContent.trim() : handle.replace(/-/g, " ");
            
            const priceEl = card.querySelector(".price-item--sale, .price-item--regular, .price, .money");
            const priceText = priceEl ? priceEl.textContent.replace(/[^\d.]/g, "") : "0";
            const price = parseFloat(priceText) || 0;
            
            const imgEl = card.querySelector("img");
            const image_url = imgEl ? (imgEl.src || imgEl.dataset.src || "") : "";
            
            pdpRecommendations.push({
              handle,
              title,
              price,
              image_url: image_url,
              url: url
            });
          });
          
          if (pdpRecommendations.length > 0) {
            pdpRecommendations = await Promise.all(pdpRecommendations.map(async (rec) => {
              try {
                const res = await fetch(`/products/${rec.handle}.js`);
                if (res.ok) {
                  const shopifyProduct = await res.json();
                  rec.variants = shopifyProduct.variants || [];
                  let matchedVariant = null;
                  if (Array.isArray(shopifyProduct.variants)) {
                    const targetId = String(rec.selected_variant?.id || rec.variant_id || "");
                    const targetSize = String(rec.size || "").toLowerCase().replace(/[\s\-_'"]/g, "");
                    matchedVariant = shopifyProduct.variants.find((v) => {
                      if (targetId && String(v.id) === targetId) return true;
                      if (targetSize) {
                        const vTitle = String(v.title || "").toLowerCase().replace(/[\s\-_'"]/g, "");
                        const vOpt1 = String(v.option1 || "").toLowerCase().replace(/[\s\-_'"]/g, "");
                        const vOpt2 = String(v.option2 || "").toLowerCase().replace(/[\s\-_'"]/g, "");
                        if (vTitle === targetSize || vOpt1 === targetSize || vOpt2 === targetSize) return true;
                      }
                      return false;
                    });
                  }
                  if (matchedVariant) {
                    if (matchedVariant.price) rec.price = matchedVariant.price / 100;
                    if (matchedVariant.compare_at_price) {
                      rec.compare_at_price = matchedVariant.compare_at_price / 100;
                      rec.was_price = rec.compare_at_price;
                    }
                    rec.selected_variant = matchedVariant;
                    rec.variant_id = matchedVariant.id;
                  } else {
                    if (!rec.price && shopifyProduct.price) {
                      rec.price = shopifyProduct.price / 100;
                    }
                    if (!rec.compare_at_price) {
                      const compareAt = shopifyProduct.compare_at_price || (shopifyProduct.variants?.[0]?.compare_at_price);
                      if (compareAt) {
                        rec.compare_at_price = compareAt / 100;
                        rec.was_price = compareAt / 100;
                      }
                    }
                  }
                }
              } catch (e) {
                console.warn("Failed to enrich pdp recommendation", rec.handle, e);
              }
              return rec;
            }));
          }
        }
      } catch (e) {
        console.warn("Error scraping PDP recommendations", e);
      }
    }

    return {
      page_type: window.location.pathname.includes("/products/")
        ? "pdp"
        : shopifyContext.page_type || "homepage",
      pdp_recommendations: pdpRecommendations,
      shopify_context: shopifyContext,
      comparison_room: this.store.data.facts.comparison_room,
      comparison_room_rugs: this.store.data.facts.comparison_room_rugs,
      current_product: product
        ? {
            id: product.id,
            handle:
              product.handle ||
              product.shopify_handle ||
              (window.location.pathname.split("/products/")[1] || "").split(
                /[?/#]/,
              )[0] ||
              "",
            title: product.title,
            price: product.price,
            compare_at_price: product.compare_at_price,
            material: product.material,
            vendor: product.vendor,
            brand: product.brand,
            origin:
              product.origin || product.country_of_origin || product.made_in,
            country_of_origin:
              product.country_of_origin || product.origin || product.made_in,
            made_in:
              product.made_in || product.origin || product.country_of_origin,
            manufacturer: product.manufacturer,
            collection: product.collection,
            style: product.style,
            color: product.color,
            size: product.size,
            shape: product.shape,
            thickness:
              product.thickness ||
              (product.metafields
                ? product.metafields.thickness || product.metafields.pile_height
                : null),
            tags: product.tags,
            metafields: product.metafields,
            url:
              product.url ||
              product.product_url ||
              (product.handle
                ? `/products/${product.handle}`
                : window.location.pathname),
            available: product.available,
            inventory_quantity: product.inventory_quantity,
            selected_variant: product.selected_variant,
            variant_id: product.variant_id,
            variants: (product.variants || []).slice(0, 30).map((variant) => ({
              id: variant.id,
              title: variant.title,
              available: variant.available,
              price: variant.price,
              compare_at_price: variant.compare_at_price,
              option1: variant.option1,
              option2: variant.option2,
              option3: variant.option3,
            })),
            options: product.options,
            discount_pct:
              product.compare_at_price &&
              product.compare_at_price > product.price
                ? Math.round(
                    ((product.compare_at_price - product.price) /
                      product.compare_at_price) *
                      100,
                  )
                : null,
          }
        : null,
      last_shown_products: this._visibleResultProducts(),
      last_shown_product_ids: this.lastShownProductIds || [],
      recent_products: this.store.data.viewed.slice(0, 8),
      cart_count: this.store.data.cart.reduce(
        (sum, item) => sum + (item.qty || 1),
        0,
      ),
      wishlist_count: this.store.data.wishlist.length,

      // Canonical Behavior Context
      preferred_categories: this.store.data.preferred_categories,
      preferred_attributes: this.store.data.preferred_attributes,
      interaction_flags: this.store.data.interaction_flags,
      conversation_state: this.store.data.conversation_state,
    };
  }

  _syncContext() {
    this.store.save();
  }

  async _assistantMemoryWithMessages() {
    let messages = [];
    try {
      messages = await this.db.getMessages(this.store.sessionId);
    } catch (e) {
      console.warn("Failed to load recent chat memory", e);
    }
    const query = messages[messages.length - 1]?.content || "";
    const visibleProducts = this._visibleResultProducts()
      .map((product) => this._compactProductForMemory(product))
      .filter((product) => product.id || product.title);
    return {
      ...this.store.assistantMemory(messages),
      visible_product_graph: visibleProducts,
      product_graph: visibleProducts,
      client_rag: this._clientRagSnippets(query, messages),
    };
  }

  async _refreshCartMemory() {
    try {
      await this.store.init();
    } catch (e) {
      console.warn("Cart refresh failed", e);
    }
    this._updateCartUI();
    if (this.cartOpen) this._renderCart();
    return this.store.data.cart || [];
  }

  async _loadClientKnowledge() {
    const cacheKey = `rug_client_knowledge:${this.api.tenant}`;
    try {
      const cached = localStorage.getItem(cacheKey);
      if (cached) this.clientKnowledge = JSON.parse(cached);
    } catch (e) {
      this.clientKnowledge = null;
    }
    try {
      const data = await this.api.tenantKnowledge();
      this.clientKnowledge = data || {};
      localStorage.setItem(cacheKey, JSON.stringify(this.clientKnowledge));
    } catch (e) {
      console.warn(
        "Client knowledge load failed; using cached knowledge if available",
        e,
      );
    }
  }

  _flattenKnowledge(value, path = []) {
    if (!value) return [];
    if (typeof value === "string")
      return [{ title: path.join(" / ") || "Knowledge", text: value }];
    if (Array.isArray(value)) {
      return value.flatMap((item, index) =>
        this._flattenKnowledge(item, [...path, String(index + 1)]),
      );
    }
    if (typeof value === "object") {
      return Object.entries(value).flatMap(([key, val]) =>
        this._flattenKnowledge(val, [...path, key]),
      );
    }
    return [{ title: path.join(" / ") || "Knowledge", text: String(value) }];
  }

  _clientRagSnippets(query = "", messages = []) {
    const docs = this._flattenKnowledge(this.clientKnowledge || {});
    if (!docs.length) return [];
    const recentText = messages
      .slice(-6)
      .map((m) => m.content || "")
      .join(" ");
    const tokens = new Set(
      String(`${query} ${recentText}`)
        .toLowerCase()
        .match(/[a-z0-9]{3,}/g) || [],
    );
    if (!tokens.size) return docs.slice(0, 4);

    return docs
      .map((doc) => {
        const haystack = `${doc.title} ${doc.text}`.toLowerCase();
        let score = 0;
        tokens.forEach((token) => {
          if (haystack.includes(token)) score += 1;
        });
        return { ...doc, score };
      })
      .filter((doc) => doc.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 6)
      .map(({ title, text }) => ({ title, text: String(text).slice(0, 700) }));
  }

  _renderToolMenu() {
    if (!this.toolMenu) return;
    const currentTools = this.contextProduct ? this.pdpTools : this.tools;
    this.toolMenu.innerHTML = this.ui.toolMenu(currentTools);
  }

  toggleToolMenu(force) {
    this.toolsOpen = typeof force === "boolean" ? force : !this.toolsOpen;
    this.toolMenu.hidden = !this.toolsOpen;
    this.menuBtn.setAttribute("aria-expanded", String(this.toolsOpen));
  }

  _ensureDrawer() {
    if (!this.drawerOpen) this.toggleDrawer();
  }

  _currentProductOrRecent() {
    return this.contextProduct || this.store.getLastViewed();
  }

  _lastAssistantText() {
    const messages = Array.from(
      this.chatArea?.querySelectorAll(".message.assistant") || [],
    );
    const last = messages[messages.length - 1];
    return (last?.textContent || "").replace(/\s+/g, " ").trim();
  }

  _resolveProductReference(text = "") {
    const query = String(text).toLowerCase();
    const products = this._visibleResultProducts();
    const ordinals = {
      first: 0,
      "1st": 0,
      one: 0,
      second: 1,
      "2nd": 1,
      two: 1,
      third: 2,
      "3rd": 2,
      three: 2,
      fourth: 3,
      "4th": 3,
      four: 3,
      fifth: 4,
      "5th": 4,
      five: 4,
      sixth: 5,
      "6th": 5,
      six: 5,
    };
    for (const [word, index] of Object.entries(ordinals)) {
      if (new RegExp(`\\b${word}\\b`).test(query) && products[index])
        return products[index];
    }
    const match = query.match(
      /\b(?:rug|item|product)\s*(\d{1,2})\b|\b(\d{1,2})(?:st|nd|rd|th)?\s+(?:rug|item|product)\b/,
    );
    if (match) {
      const index = Number(match[1] || match[2]) - 1;
      if (products[index]) return products[index];
    }
    return null;
  }

  _rewriteContextualFollowup(query = "") {
    const normalized = String(query).trim().toLowerCase();
    if (
      !/^(yes|yeah|yep|sure|ok|okay|tell me more|more|know more)$/.test(
        normalized,
      )
    )
      return query;
    const lastAssistant = this._lastAssistantText().toLowerCase();
    const product = this.contextProduct || this.store.getLastViewed();
    if (!product) return query;
    if (
      lastAssistant.includes("would you like to know more") ||
      lastAssistant.includes("want to know more") ||
      lastAssistant.includes("ready to")
    ) {
      return `Tell me more about ${product.title}. Include material, size, care, and why it is a good choice.`;
    }
    return query;
  }

  _isProductInfoQuery(text = "") {
    return /\b(what about|tell me about|tell me more|more about|know more|details?)\b/i.test(
      text,
    );
  }

  _isProductSizeQuery(text = "") {
    return (
      /\b(size|sizes)\b/i.test(text) &&
      /\b(this|current|same|rug|product|available|availability|other|another)\b/i.test(
        text,
      )
    );
  }

  _isProductPriceQuery(text = "") {
    if (/\b(cleaning|clean|stain|repair|restoration|appraisal|sizing|service|consultation)\b/i.test(text)) return false;
    return (
      /\b(price|cost|how much)\b/i.test(text) &&
      /\b(this|current|same|rug|product)\b/i.test(text)
    );
  }

  _availableVariantRows(product = {}) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const rows = variants
      .map((variant) => ({
        id: variant.id,
        size:
          this._variantOption(product, variant, "Size") || variant.title || "",
        shape: this._variantOption(product, variant, "Shape") || "",
        color: this._variantOption(product, variant, "Color") || "",
        price: variant.price,
        compare_at_price: variant.compare_at_price,
        available: variant.available,
      }))
      .filter(
        (row) => row.size && String(row.size).toLowerCase() !== "default title",
      );
    const seen = new Set();
    return rows.filter((row) => {
      const key = `${row.size}|${row.shape}|${row.color}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  _productSizeText(product = {}) {
    const rows = this._availableVariantRows(product);
    if (!rows.length) {
      return `I don't see other size options listed for ${this.ui.esc(product.title || "this rug")}.`;
    }
    const selectedId = String(
      product.variant_id || product.selected_variant?.id || "",
    );
    const selected =
      rows.find((row) => selectedId && String(row.id) === selectedId) ||
      rows.find((row) => row.size === product.size) ||
      null;
    const available = rows.filter((row) => row.available !== false);
    
    available.sort((a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0));

    const chips = available.slice(0, 12).map((row) => {
        const isSelected = selected && row.id === selected.id;
        const sizeStr = `${row.size}${row.shape ? ` ${row.shape}` : ""}`;
        const priceStr = row.price ? `${this.ui.formatPrice(row.price)}` : "";
        
        return `<div class="size-buy-chip ${isSelected ? 'selected' : ''}" style="display:flex; justify-content:space-between; align-items:center; border: 1px solid var(--color-border); border-radius: 8px; padding: 12px; margin-bottom: 8px; background: var(--color-surface); box-shadow: 0 1px 3px rgba(0,0,0,0.05);">
            <div style="display:flex; flex-direction:column; gap:4px; flex: 1;">
                <span style="font-weight:600; font-size:0.95rem; line-height: 1.2;">${this.ui.esc(sizeStr)}</span>
                <span style="font-size:0.85rem; color:var(--color-text-secondary);">${priceStr}</span>
            </div>
            <button class="discovery-btn btn-primary btn-sm" style="margin:0; padding:6px 16px; font-weight: 600;" data-app-action="quickAddVariant" data-arg0="${row.id}">Add to Cart</button>
        </div>`;
    }).join('');

    return `
        <div class="product-sizes-container">
            <p style="margin-bottom: 12px; font-size: 0.95rem;">Available sizes for <strong>${this.ui.esc(product.title || "this rug")}</strong>:</p>
            ${chips}
        </div>
    `;
  }

  _productPriceText(product = {}) {
    this._syncSelectedVariantContext(true);
    const activeProduct = this.contextProduct || product;
    const size =
      activeProduct.size &&
      String(activeProduct.size).toLowerCase() !== "default title"
        ? ` for ${activeProduct.size}`
        : "";
    const shape = activeProduct.shape ? ` ${activeProduct.shape}` : "";
    const compare =
      activeProduct.compare_at_price &&
      this.ui.priceNumber(activeProduct.compare_at_price) >
        this.ui.priceNumber(activeProduct.price)
        ? `, marked down from ${this.ui.formatPrice(activeProduct.compare_at_price)}`
        : "";
    return `The selected ${this.ui.esc(activeProduct.title || "rug")}${size}${shape} is ${this.ui.formatPrice(activeProduct.price)}${compare}.`;
  }

  _productInfoText(product) {
    const bits = [];
    if (product.material) bits.push(`made from ${product.material}`);
    if (product.size) bits.push(`sized ${product.size}`);
    if (product.style) bits.push(`${product.style} style`);
    if (product.color) bits.push(`${product.color} color`);
    const price =
      product.price !== undefined &&
      product.price !== null &&
      product.price !== ""
        ? ` It is priced at ${this.ui.formatPrice(product.price)}.`
        : "";
    const detail = bits.length ? ` It is ${bits.join(", ")}.` : "";
    return `<strong>${this.ui.esc(product.title || "This rug")}</strong>${detail}${price} You can view the PDP, add it to cart, or preview it in your room.`;
  }

  _openCurrentProductPage() {
    const product = this.contextProduct || this.store.getLastViewed();
    if (!product) {
      this._addMsg(
        "Please select a rug first, then I can open its product page.",
        "assistant",
      );
      return true;
    }
    const url =
      product.url ||
      product.product_url ||
      (product.handle || product.shopify_handle
        ? `/products/${product.handle || product.shopify_handle}`
        : "");
    if (!url) {
      this._addMsg(
        "I do not have a product page URL for that rug yet. Please open it from the product card.",
        "assistant",
      );
      return true;
    }
    sessionStorage.setItem("rugbie_open_agent_on_load", "true");
    window.location.href = url;
    return true;
  }

  _classifyRoomIntent(text = "") {
    // Room features are intentionally separate:
    // analyze = recommend rugs from room facts; visualize = place a selected rug.
    const query = String(text || "")
      .trim()
      .toLowerCase();
    if (!query) return "none";

    const wantsRecommendations =
      /\b(analy[sz]e|recommend|suggest|suit|fits?|match|choose|find)\b.*\b(room|space|home|decor)\b/.test(
        query,
      ) ||
      /\b(what rug|which rug|rug recommendations?|suggest a rug|recommend a rug)\b/.test(
        query,
      ) ||
      /\b(analy[sz]e my room|what fits my room|what would suit my room|for this room)\b/.test(
        query,
      );
    const wantsVisualization =
      /\b(see|show|view|visuali[sz]e|preview|place|try)\b.*\b(room|space|my room|home|photo)\b/.test(
        query,
      ) ||
      /\b(see|show|view|visuali[sz]e|preview|place|try) (this|it|rug)\b/.test(
        query,
      ) ||
      /\bsee in (my )?room\b/.test(query) ||
      /\bshow in (my )?room\b/.test(query);
    const roomOnly =
      /\b(my room|this room|room photo|room)\b/.test(query) &&
      !wantsRecommendations &&
      !wantsVisualization;

    if (wantsRecommendations && !wantsVisualization) return "analyze";
    if (wantsVisualization && !wantsRecommendations) return "visualize";
    if (wantsRecommendations && wantsVisualization)
      return this.contextProduct ? "visualize" : "ambiguous";
    if (roomOnly) return this.contextProduct ? "visualize" : "ambiguous";
    return "none";
  }

  _handleRoomIntent(query, referencedProduct = null) {
    const intent = this._classifyRoomIntent(query);
    if (intent === "none") return false;
    if (intent === "analyze") {
      this._triggerVisualizer("analyze");
      return true;
    }
    if (intent === "visualize") {
      return this._handleLocalVisualizerCommand(query, referencedProduct, true);
    }
    this._askRoomIntentClarification();
    return true;
  }

  _askRoomIntentClarification() {
    this._addMsg(
      "Do you want me to recommend rugs based on your room, or show this specific rug placed in your room?",
      "assistant",
    );
    this._addMsg(
      this.ui.chips(["Analyze my room", "See in my room"]),
      "assistant",
      true,
    );
  }

  _productForVisualizer(query = "", referencedProduct = null) {
    return (
      referencedProduct ||
      this.contextProduct ||
      this.store.getLastViewed() ||
      this._visibleResultProducts()[0] ||
      null
    );
  }

  _prepareVisualizerProduct(product) {
    if (!product) return null;
    const sizes = this._availableSizesForProduct(product);
    const selectedSize =
      product.size && String(product.size).toLowerCase() !== "default title"
        ? product.size
        : "";
    return {
      ...product,
      size: selectedSize || sizes[0] || product.size || "",
    };
  }

  _handleLocalVisualizerCommand(
    query,
    referencedProduct = null,
    alreadyClassified = false,
  ) {
    if (!alreadyClassified && this._classifyRoomIntent(query) !== "visualize")
      return false;
    const baseProduct = this._productForVisualizer(query, referencedProduct);
    if (this._requestVisualizerSize(baseProduct)) return true;
    const product = this._prepareVisualizerProduct(baseProduct);
    if (!product) {
      this._addMsg(
        "Please select a rug first before visualizing it in your room.",
        "assistant",
      );
      return true;
    }
    this._launchRoomManagerForProduct(product);
    return true;
  }

  _handleLocalProductCommand(query, referencedProduct = null) {
    const normalized = String(query || "")
      .trim()
      .toLowerCase();
    if (
      /^(view product|open product|product page|view product page|open pdp|view pdp)$/i.test(
        normalized,
      )
    ) {
      return this._openCurrentProductPage();
    }
    const product = referencedProduct || this.contextProduct;
    if (!product) return false;
    this._syncSelectedVariantContext(true);
    const activeProduct = this.contextProduct || product;
    if (this._isProductSizeQuery(query)) {
      this._setContextProduct(activeProduct);
      this._addMsg(this._productSizeText(activeProduct), "assistant", true);
      return true;
    }
    if (this._isProductPriceQuery(query)) {
      this._setContextProduct(activeProduct);
      this._addMsg(this._productPriceText(activeProduct), "assistant", true);
      return true;
    }
    if (!this._isProductInfoQuery(query)) return false;
    this._setContextProduct(product);
    this._addMsg(this._productInfoText(product), "assistant", true);
    this._addMsg(this.ui.productActionCard(product), "assistant", true);
    return true;
  }

  async openTool(toolId) {
    this._ensureDrawer();
    this.toggleToolMenu(false);
    this._syncContext();

    const product = this._currentProductOrRecent();
    switch (toolId) {
      case "visualizer":
        this._triggerVisualizer("visualize");
        break;
      case "analyze":
        this._triggerVisualizer("analyze");
        break;
      case "compare":
        this._addMsg(
          "Product comparison is turned off for this store. I can still help with size, material, care, and rug pad guidance.",
          "assistant",
        );
        break;
      case "similar-rugs":
        await this._showSimilarRugs(product);
        break;
      case "budget-alternatives":
        await this._showBudgetAlternatives(product);
        break;
      case "rug-pads":
        this.sendAction(
          product
            ? `Recommend a rug pad for ${product.title}`
            : "Recommend rug pads for my rug and room",
        );
        break;
      case "perfect-size":
        this.sendAction(`Help me find the perfect rug size for my room`);
        break;
      case "material-finder":
        this.sendAction(`What's the best rug material for me?`);
        break;
      case "care-guide":
        this.sendAction(`How do I clean and care for this rug?`);
        break;
      case "recently-viewed":
      case "viewed-products":
        this.sendAction("recently-viewed");
        break;
      case "better-value":
        this.sendAction(`Show me cheaper alternatives to this rug`);
        break;
      case "style-match":
        this.sendAction(`Show me rugs with a similar style to this one`);
        break;
      case "consultation":
      case "services":
      case "contact-store":
      case "track-order":
        this._openStoreOwnerChat();
        break;
      default:
        this.sendAction("Help me find a rug");
    }
  }

  async _showSimilarRugs(product) {
    const targetProduct = (product && (product.id || product.handle)) ? product : this._getActiveProduct();
    if (!targetProduct) {
      await this._doBrowse(this.store.data.facts, 6);
      return;
    }
    const loadId = "load-" + Date.now();
    this._addMsg(this.ui.loading("Finding similar rugs..."), "assistant", true, loadId);
    try {
      let products = [];
      const prodId = targetProduct.id || targetProduct.handle || "";
      const prodHandle = targetProduct.handle || targetProduct.id || "";
      if (prodId || prodHandle) {
        try {
          const reco = await this.api.recommend(prodId, prodHandle);
          if (Array.isArray(reco)) {
            products = reco;
          } else if (reco && typeof reco === "object") {
            products = reco.products || reco.results || reco.recommendations || reco.payload?.products || [];
            if (!products.length && Array.isArray(reco.sections)) {
              for (const sec of reco.sections) {
                const secRecs = sec.recommendations || sec.products || sec.results || [];
                if (secRecs.length > 0) {
                  products.push(...secRecs);
                }
              }
            }
          }
        } catch (e) {
          console.warn("api.recommend failed, falling back to search", e);
        }
      }
      if (!products.length) {
        const query = targetProduct.title || targetProduct.style || targetProduct.color || targetProduct.material || "rugs";
        const searchRes = await this.api.search(query);
        products = (searchRes?.products || searchRes?.results || searchRes || []).filter(
          p => String(p.id) !== String(targetProduct.id) && p.handle !== targetProduct.handle
        );
      }
      this._removeMsg(loadId);
      if (Array.isArray(products) && products.length) {
        this.registerProducts(products);
        this.currentBrowseResults = products;
        this._addMsg(
          `Here are similar rugs to <strong>${this.ui.esc(targetProduct.title || "this rug")}</strong>:`,
          "assistant"
        );
        this._addMsg(
          this.ui.horizontalProductCarousel(
            products.slice(0, 6),
            `Similar to ${targetProduct.title || "this rug"}`
          ),
          "assistant",
          true
        );
      } else {
        await this._doBrowse(this.store.data.facts, 6);
      }
    } catch (err) {
      this._removeMsg(loadId);
      console.error("Similar rugs error:", err);
      await this._doBrowse(this.store.data.facts, 6);
    }
  }

  async _showBudgetAlternatives(product) {
    if (!product?.id) {
      this.sendAction("Show budget alternatives based on my browsing context");
      return;
    }
    const loadId = "load-" + Date.now();
    this._addMsg(this.ui.loading(), "assistant", true, loadId);
    try {
      const reco = await this.api.recommend(product.id);
      const products = Array.isArray(reco)
        ? reco
        : reco.products || reco.payload?.products || [];
      const currentPrice = this._numericPrice(product.price);
      const alternatives = (Array.isArray(products) ? products : [])
        .filter(
          (item) =>
            !currentPrice || this._numericPrice(item.price) < currentPrice,
        )
        .sort(
          (a, b) => this._numericPrice(a.price) - this._numericPrice(b.price),
        );
      this._removeMsg(loadId);
      if (alternatives.length) {
        this.registerProducts(alternatives);
        this.currentBrowseResults = alternatives;
        this._addMsg(
          this.ui.curatedPicks(
            alternatives,
            `Lower-priced alternatives to ${product.title}`,
            alternatives.length,
          ),
          "assistant",
          true,
        );
      } else {
        this.sendAction(`Find lower-priced alternatives to ${product.title}`);
      }
    } catch (err) {
      this._removeMsg(loadId);
      console.error("Budget alternatives error:", err);
      this.sendAction(`Find lower-priced alternatives to ${product.title}`);
    }
  }

  async _styleMatch() {
    const facts = { ...this.store.data.facts };
    if (this.contextProduct) {
      if (this.contextProduct.style) facts.style = [this.contextProduct.style];
      if (this.contextProduct.color) facts.color = [this.contextProduct.color];
    }
    if (Object.keys(facts).length) {
      await this._doBrowse(facts, 6);
    } else {
      this.sendAction(
        "Match my style using my browsing activity and ask only what is missing",
      );
    }
  }

  _numericPrice(price) {
    return this.ui.priceNumber(price);
  }

  _productDisplayPrice(product = {}) {
    return this.ui.priceNumber(
      product.price ||
        product.sale_price ||
        product.discounted_price ||
        product.final_price,
    );
  }

  _extractMaxPrice(text = "") {
    const patterns = [
      /\b(?:under|below|less\s+than|up\s+to|maximum|max|budget|within)\b[^0-9$]{0,24}\$?\s*([0-9]+(?:\.[0-9]{1,2})?)\s*\$?/i,
      /\$?\s*([0-9]+(?:\.[0-9]{1,2})?)\s*\$?\s*(?:or\s+less|and\s+under|or\s+under|max(?:imum)?|budget)/i,
    ];
    for (const pattern of patterns) {
      const match = String(text || "").match(pattern);
      const value = parseFloat(match?.[1]);
      if (Number.isFinite(value) && value > 0) return value;
    }
    return null;
  }

  _isBudgetClearingQuery(text = "") {
    return /\b(any\s*price|anyprice|all\s+prices?|no\s+(?:budget|price\s*limit|price\s*filter)|remove\s+(?:budget|price\s*(?:limit|filter)?)|clear\s+(?:budget|price\s*(?:limit|filter)?))\b/i.test(
      text,
    );
  }

  _hasExplicitBudgetQuery(text = "") {
    return (
      Boolean(this._extractMaxPrice(text)) ||
      /\b(under|below|less\s+than|up\s+to|max(?:imum)?|budget|cheaper\s+than|price\s+limit)\b/i.test(
        text,
      )
    );
  }

  _shouldClearStoredBudgetForQuery(text = "") {
    const query = String(text || "");
    if (this._isBudgetClearingQuery(query)) return true;
    if (this._hasExplicitBudgetQuery(query)) return false;
    return (
      /\b(show|find|search|browse|recommend|looking\s+for|need|want)\b/i.test(
        query,
      ) &&
      /\b(rug|rugs|runner|pad|carpet|blue|red|green|beige|ivory|maharaja|oriental|traditional|washable|wool|jute|outdoor|indoor)\b/i.test(
        query,
      )
    );
  }

  _clearStoredBudget() {
    this.store.clearFacts?.(["budget", "max_price", "price"]);
    if (this.store.data?.facts) {
      delete this.store.data.facts.budget;
      delete this.store.data.facts.max_price;
      delete this.store.data.facts.price;
    }
  }

  _factsWithoutStoredBudget(facts = {}) {
    const next = { ...(facts || {}) };
    delete next.budget;
    delete next.max_price;
    delete next.price;
    return next;
  }

  _extractRequestedProductCount(text = "") {
    const match = String(text || "").match(
      /\b(?:show|find|browse|recommend|display|list)\s+(?:me\s+)?(\d{1,2})\s+(?:area\s+)?(?:rug|rugs|runner|runners|pad|pads)\b/i,
    );
    if (!match) return null;
    const count = Number(match[1]);
    if (!Number.isFinite(count) || count < 1) return null;
    return Math.min(count, 20);
  }

  _isOrderTrackingQuery(text = "") {
    return (
      /\b(track|tracking|status|where)\b.*\b(order|package|shipment|delivery)\b/i.test(
        text,
      ) ||
      /^\s*(order\s+tracking|track\s+my\s+order|track\s+order)\s*$/i.test(text)
    );
  }

  _isServiceIntentQuery(text = "") {
    if (
      /\b(this|current|same)\s+rug\b/i.test(text) &&
      /\b(size|sizes?|price|cost|available|availability)\b/i.test(text)
    )
      return false;
    return (
      /\b(service|services|consultation|concultation|appointment|cleaning|clean|stain|repair|restoration|appraisal|custom rug)\b/i.test(
        text,
      ) && !this._isOrderTrackingQuery(text)
    );
  }

  _isRugPadProductQuery(text = "") {
    return /\brug\s*pad\b|\bpads?\b/i.test(text);
  }

  _browseFactsForQuery(query = "", facts = {}) {
    const next = this._shouldClearStoredBudgetForQuery(query)
      ? this._factsWithoutStoredBudget(facts)
      : { ...(facts || {}) };
    if (this._isRugPadProductQuery(query)) {
      next.category = ["Rug Pads"];
      next.product_type = ["Rug Pads"];
    } else if (/\b(rug|rugs|runner|runners|carpet|carpets)\b/i.test(query)) {
      next.category = ["Rug"];
    }
    return next;
  }

  _cleanAssistantText(text = "") {
    return String(text || "")
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/â€™/g, "'")
      .replace(/â€œ|â€�/g, '"')
      .replace(/â€“|â€”/g, "-")
      .replace(/â€¦/g, "...")
      .trim();
  }

  _noProductMatchMessage(query = "", facts = {}) {
    const mergedFacts = {
      ...(facts || {}),
      ...this._browseFactsForQuery(query, {}),
    };
    const queryText = String(query || "").toLowerCase();
    if (!mergedFacts.color) {
      const colorMatch = queryText.match(
        /\b(blue|red|green|beige|ivory|cream|black|white|gray|grey|brown|pink|orange|yellow|navy|teal|multi)\b/,
      );
      if (colorMatch) mergedFacts.color = colorMatch[1];
    }
    if (!mergedFacts.size) {
      const sizeMatch = queryText.match(
        /\b\d{1,2}\s*(?:'|ft|feet)\s*(?:x|by|\*)\s*\d{1,2}\s*(?:'|ft|feet)?\s*(?:\d{1,2}\s*(?:"|in|inch|inches))?/,
      );
      if (sizeMatch) mergedFacts.size = sizeMatch[0].replace(/\s+/g, " ");
    }
    if (
      !mergedFacts.room &&
      /\b(kids?|children|pets?|pet-friendly|dog|cat)\b/.test(queryText)
    ) {
      mergedFacts.room = "kids and pets";
    }
    const parts = [];
    const addFact = (label, value) => {
      const text = Array.isArray(value) ? value.join(", ") : value;
      if (text) parts.push(label ? `${label} ${text}` : String(text));
    };
    addFact("", mergedFacts.color);
    addFact("around", mergedFacts.size);
    addFact("for", mergedFacts.room);
    addFact("", mergedFacts.material);

    const criteria = parts.length ? ` for ${parts.join(", ")}` : "";
    return `I couldn't find exact rug matches${criteria}. Try removing one filter, like color or exact size, and I can show the closest durable options for kids and pets.`;
  }

  _productsForBudget(products = [], query = "", facts = {}) {
    const queryMaxPrice = this._extractMaxPrice(query);
    const useStoredBudget = !this._shouldClearStoredBudgetForQuery(query);
    const factBudget = useStoredBudget
      ? facts?.budget ||
        facts?.max_price ||
        this.store.data.facts?.budget ||
        this.store.data.facts?.max_price
      : null;
    const maxPrice = queryMaxPrice || this.ui.priceNumber(factBudget);
    if (!maxPrice) return { products, maxPrice: null, removed: 0 };
    const filtered = (products || []).filter((product) => {
      const price = this._productDisplayPrice(product);
      return price > 0 && price <= maxPrice + 0.01;
    });
    return {
      products: filtered,
      maxPrice,
      removed: (products || []).length - filtered.length,
    };
  }

  // --- Event Binding ---
  _bindEvents() {
    // FAB & Drawer
    this.fab?.addEventListener("click", () => this.toggleDrawer());
    document
      .getElementById("close-drawer")
      ?.addEventListener("click", () => this.toggleDrawer());
    this.menuBtn?.addEventListener("click", () => this.toggleToolMenu());
    this.overlay?.addEventListener("click", () => this._closeAll());

    // Chat
    this.sendBtn?.addEventListener("click", () => this._send());
    this.chatInput?.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this._send();
      }
    });
    document
      .getElementById("camera-btn")
      ?.addEventListener("click", () => this.sendAction("visualizer"));
    document.addEventListener("variant-change", () =>
      this._syncSelectedVariantContext(true),
    );
    document.addEventListener("change", (e) => {
      if (
        e.target?.matches?.(
          'input[name="id"], select[name="id"], .product-form__input input, .product-form__input select',
        )
      ) {
        setTimeout(() => this._syncSelectedVariantContext(true), 0);
      }
    });
    // Vertical Scroll Indicator Listener
    if (this.chatArea) {
      this.chatArea.addEventListener("scroll", () =>
        this._updateScrollIndicator(),
      );
    }
    if (this.scrollIndicator) {
      this.scrollIndicator.addEventListener("click", () => {
        if (this.chatArea) {
          this.chatArea.scrollTo({
            top: this.chatArea.scrollHeight,
            behavior: "smooth",
          });
        }
      });
    }

    const chatContainer =
      document.querySelector("#chat-container") || this.chatArea;
    if (chatContainer) {
      chatContainer.addEventListener(
        "scroll",
        (e) => {
          if (
            e.target &&
            e.target.classList &&
            e.target.classList.contains("chat-product-carousel")
          ) {
            const carousel = e.target;
            const container = carousel.closest(".chat-carousel-container");
            if (container) {
              const prevBtn = container.querySelector(".carousel-scroll-prev");
              const nextBtn = container.querySelector(".carousel-scroll-next");
              if (prevBtn) {
                prevBtn.style.display = carousel.scrollLeft > 0 ? "block" : "none";
              }
              if (nextBtn) {
                const maxScroll = carousel.scrollWidth - carousel.clientWidth;
                nextBtn.style.display =
                  carousel.scrollLeft >= maxScroll - 2 ? "none" : "block";
              }
            }
          }
        },
        true,
      );

      chatContainer.addEventListener("click", (e) => {
        const prevScrollBtn = e.target.closest(".carousel-scroll-prev");
        const nextScrollBtn = e.target.closest(".carousel-scroll-next");
        const viewBtn = e.target.closest(".view-product-btn");
        const seeInRoomBtn = e.target.closest(".see-in-room-btn");
        const showSimilarBtn = e.target.closest(".show-similar-btn");
        const actionChipBtn = e.target.closest(".action-chip-btn");

        if (prevScrollBtn) {
          e.preventDefault();
          e.stopPropagation();
          const container = prevScrollBtn.closest(".chat-carousel-container");
          const carousel = container?.querySelector(".chat-product-carousel");
          if (carousel) carousel.scrollBy({ left: -240, behavior: "smooth" });
        } else if (nextScrollBtn) {
          e.preventDefault();
          e.stopPropagation();
          const container = nextScrollBtn.closest(".chat-carousel-container");
          const carousel = container?.querySelector(".chat-product-carousel");
          if (carousel) carousel.scrollBy({ left: 240, behavior: "smooth" });
        } else if (viewBtn) {
          e.preventDefault();
          e.stopPropagation();
          const url = viewBtn.dataset.url;
          const id = viewBtn.dataset.id;
          if (id && window.app) {
            window.app.openProduct(id, url);
          } else if (url) {
            window.location.href = url;
          }
        } else if (seeInRoomBtn) {
          e.preventDefault();
          e.stopPropagation();
          const handle = seeInRoomBtn.dataset.handle;
          const id = seeInRoomBtn.dataset.id;
          if (id && window.app) {
            window.app.seeProductInRoom(id, seeInRoomBtn);
          } else if (handle && window.app) {
            window.app.sendAction("visualizer");
          }
        } else if (showSimilarBtn) {
          e.preventDefault();
          e.stopPropagation();
          const id = showSimilarBtn.dataset.id;
          const title = showSimilarBtn.dataset.title;
          if (id && window.app) {
            window.app._showSimilarRugs({ id, title });
          }
        } else if (actionChipBtn) {
          e.preventDefault();
          e.stopPropagation();
          const action = actionChipBtn.dataset.action;
          if (action && window.app) {
            window.app.sendAction(action);
          }
        }
      });
    }

    // Global handler for dynamic data-app-action buttons
    document.body.addEventListener("click", (e) => {
      const actionBtn = e.target.closest("[data-app-action]");
      if (actionBtn && window.app) {
        if (actionBtn.tagName === "A" || actionBtn.tagName === "BUTTON") {
          e.preventDefault();
          if (actionBtn.tagName !== "A") e.stopPropagation();
        }
        const action = actionBtn.dataset.appAction;
        if (typeof window.app[action] === "function") {
          const args = [];
          let i = 0;
          while (`arg${i}` in actionBtn.dataset) {
            args.push(actionBtn.dataset[`arg${i}`]);
            i++;
          }
          args.push(actionBtn);
          window.app[action](...args);
        }
      }
    });

    // Global handler for forms
    document.body.addEventListener("submit", (e) => {
      if (e.target.matches(".app-managed-form")) {
        e.preventDefault();
        const formType = e.target.dataset.formType;
        if (formType === "appointment") {
          if (typeof window.app?.submitAppointment === "function") {
            window.app.submitAppointment(e);
          }
        } else if (formType === "track-order") {
          if (typeof window.app?.submitOrderTrack === "function") {
            window.app.submitOrderTrack(e);
          }
        }
      }
    });

    // Header search
    const headerSearch = document.getElementById("header-search-input");
    headerSearch?.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && e.target.value.trim()) {
        this.openAssistantWith(e.target.value.trim());
        e.target.value = "";
      }
    });

    // Cart
    document
      .getElementById("cart-toggle")
      ?.addEventListener("click", () => this.openCart());
    document
      .getElementById("close-cart")
      ?.addEventListener("click", () => this.closeCart());
    document
      .getElementById("header-cart-btn")
      ?.addEventListener("click", () => this.openCart());

    // Mobile menu
    document
      .getElementById("menu-toggle")
      ?.addEventListener("click", () => this.openMobileMenu());
    document
      .getElementById("close-mobile-menu")
      ?.addEventListener("click", () => this.closeMobileMenu());

    // Hero CTA
    document
      .getElementById("hero-cta-ai")
      ?.addEventListener("click", () => this.openTool("style-match"));

    // PDP buttons
    document
      .getElementById("pdp-back-btn")
      ?.addEventListener("click", () => this.goHome());
    document
      .getElementById("pdp-add-cart")
      ?.addEventListener("click", () => this._pdpAddToCart());
    document
      .getElementById("pdp-wishlist")
      ?.addEventListener("click", () => this._pdpToggleWishlist());
    document.getElementById("pdp-visualizer")?.addEventListener("click", () => {
      if (!this.drawerOpen) this.toggleDrawer();
      this.sendAction("visualizer");
    });

    // Keyboard: Escape to close
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this._closeAll();
    });

    // Click outside to close (desktop)
    document.addEventListener("click", (e) => {
      if (this.drawerOpen && window.innerWidth > 768) {
        if (
          !this.drawer.contains(e.target) &&
          !this.fab.contains(e.target) &&
          !e.target.closest(".toast")
        ) {
          this.toggleDrawer();
        }
      }
      if (
        this.cartOpen &&
        this.cartDrawer &&
        !this.cartDrawer.contains(e.target) &&
        !e.target.closest(".assistant-cart-button")
      ) {
        this.closeCart();
      }
    });

    // Shopify Route and Variant Change Detection
    const handleRouteChange = () => {
      setTimeout(() => {
        const path = window.location.pathname;
        const isPDP = path.includes("/products/");

        if (isPDP) {
          const parts = path.split("/");
          const handleIndex = parts.indexOf("products") + 1;
          const handle = parts[handleIndex];

          const currentHandle =
            this.contextProduct?.handle || this.contextProduct?.shopify_handle;

          if (handle && handle.split("?")[0] !== currentHandle) {
            const cleanHandle = handle.split("?")[0];
            fetch(`/products/${cleanHandle}.js`)
              .then((res) => res.json())
              .then((data) => {
                const product = {
                  id: String(data.id),
                  handle: data.handle,
                  title: data.title,
                  price: (data.price / 100).toFixed(2),
                  imgUrl: data.images && data.images[0] ? data.images[0] : "",
                  url: `/products/${data.handle}`,
                  collection: data.vendor || "",
                  vendor: data.vendor || "",
                  brand: data.vendor || "",
                };
                const urlParams = new URLSearchParams(window.location.search);
                const variantId = urlParams.get("variant");
                if (variantId && data.variants) {
                  const v = data.variants.find(
                    (v) => String(v.id) === variantId,
                  );
                  if (v) {
                    product.size = v.public_title || v.title || "";
                    product.price = (v.price / 100).toFixed(2);
                  }
                } else if (data.variants && data.variants[0]) {
                  product.size =
                    data.variants[0].public_title ||
                    data.variants[0].title ||
                    "";
                }
                this._setContextProduct(product);
                this._syncContext();
              })
              .catch((err) =>
                console.error("Failed to fetch product data:", err),
              );
          } else {
            const urlParams = new URLSearchParams(window.location.search);
            const variantId = urlParams.get("variant");
            if (variantId && window.ShopifyContextPayload?.product) {
              if (
                window.ShopifyContextPayload.product.variant?.id != variantId
              ) {
                this._syncContext();
              }
            }
          }
        } else if (this.contextProduct) {
          this.contextProduct = null;
          this._syncContext();
        }
      }, 100);
    };
    document.addEventListener("change", (e) => {
      if (
        e.target.matches(
          'input[name="id"], select[name="id"], input[name="id"] ~ *, select[name="id"] ~ *',
        )
      ) {
        handleRouteChange();
      }
    });

    // Intercept pushState for SPA themes
    const originalPushState = history.pushState;
    history.pushState = function () {
      originalPushState.apply(this, arguments);
      handleRouteChange();
    };

    // Swipe down to close (mobile)
    const drawerHeader = document.querySelector(".drawer-header");
    if (drawerHeader) {
      let startY = 0;
      drawerHeader.addEventListener(
        "touchstart",
        (e) => {
          startY = e.touches[0].clientY;
        },
        { passive: true },
      );

      drawerHeader.addEventListener(
        "touchmove",
        (e) => {
          if (!startY) return;
          const currentY = e.touches[0].clientY;
          const diff = currentY - startY;
          if (diff > 50 && this.drawerOpen) {
            this.toggleDrawer();
            startY = 0;
          }
        },
        { passive: true },
      );

      drawerHeader.addEventListener("touchend", () => {
        startY = 0;
      });
    }
  }

  // --- Drawer ---
  _isPDP() {
    return (
      document.getElementById("pdp-view")?.style.display === "block" ||
      window.location.pathname.includes("/products/")
    );
  }

  toggleDrawer() {
    this.drawerOpen = !this.drawerOpen;
    this.drawer.classList.toggle("open", this.drawerOpen);

    const isPDP = this._isPDP();

    if (this.drawerOpen) {
      this.drawer.removeAttribute("inert");
      if (this.chatArea.children.length === 0)
        this.drawer.classList.add("unroll");
      // On PDP (desktop), add side-by-side class
      if (isPDP) {
        document.body.classList.add("pdp-assistant-open");
      }
      setTimeout(() => this._updateScrollIndicator(), 150);
    } else {
      this.drawer.setAttribute("inert", "");
      this.drawer.classList.remove("unroll");
      this.toggleToolMenu(false);
      document.body.classList.remove("pdp-assistant-open");
      // Accessibility focus restoration
      if (this.fab) this.fab.focus();
    }

    this.fab.classList.toggle("hidden", this.drawerOpen);

    // On PDP desktop, don't show overlay (side-by-side mode)
    const isMobile = window.innerWidth <= 768;
    if (isPDP && !isMobile) {
      // No overlay needed in side-by-side mode
      this.overlay.classList.remove("active");
      this.overlay.setAttribute("inert", "");
    } else {
      const active = this.cartOpen || this.drawerOpen;
      this.overlay.classList.toggle("active", active);
      if (active) this.overlay.removeAttribute("inert");
      else this.overlay.setAttribute("inert", "");
    }

    if (this.drawerOpen) {
      if (this.chatArea.children.length === 0 && !this._chatRestored)
        this._sendGreeting();

      // Greet with the current product if on a PDP
      if (isPDP) {
        const activeProduct = this._getActiveProduct();
        if (activeProduct) {
          this.contextProduct = activeProduct;
          const greeting = `Now viewing <strong>${this.ui.esc(activeProduct.title)}</strong>. I can help with sizing, care, similar styles, or show it in your room.`;
          const msgs = document.querySelectorAll(".message.assistant");
          if (
            msgs.length === 0 ||
            !msgs[msgs.length - 1].innerHTML.includes(
              this.ui.esc(activeProduct.title),
            )
          ) {
            this._addMsg(greeting, "assistant", true);
            this._loadPDPSuggestedQuestions();
          } else if (!document.getElementById("load-pdp-dynamic-chips")) {
            this._loadPDPSuggestedQuestions();
          }
        }
      }

      setTimeout(() => {
        if (this.chatInput) this.chatInput.focus();
      }, 300);
    }
  }

  openAssistantWith(msg) {
    if (!this.drawerOpen) this.toggleDrawer();
    setTimeout(() => this.sendAction(msg), 350);
  }

  // --- Cart Drawer ---
  async openCart() {
    try {
      await this.store.init();
    } catch (e) {
      console.warn("Cart sync warning:", e);
    }
    this._setCartOpen(true);
  }

  closeCart() {
    this._setCartOpen(false);
  }

  async toggleCart() {
    if (!this.cartOpen) {
      try {
        await this.store.init();
      } catch (e) {
        console.warn("Cart sync warning:", e);
      }
    }
    this._setCartOpen(!this.cartOpen);
  }

  _setCartOpen(open) {
    if (!this.cartDrawer) return;
    this.cartOpen = !!open;
    this.cartDrawer.classList.toggle("open", this.cartOpen);
    this.cartDrawer.setAttribute(
      "aria-hidden",
      this.cartOpen ? "false" : "true",
    );
    if (this.cartOpen) this.cartDrawer.removeAttribute("inert");
    else this.cartDrawer.setAttribute("inert", "");

    document
      .querySelectorAll(".assistant-cart-button, #cart-toggle")
      .forEach((btn) => {
        btn.setAttribute("aria-expanded", this.cartOpen ? "true" : "false");
      });

    const active = this.cartOpen || this.drawerOpen;
    this.overlay.classList.toggle("active", active);
    if (active) this.overlay.removeAttribute("inert");
    else this.overlay.setAttribute("inert", "");

    if (this.cartOpen) this._renderCart();
  }

  _renderCart() {
    const container = document.getElementById("cart-items");
    const footer = document.getElementById("cart-footer");
    if (this.store.data.cart.length === 0) {
      container.innerHTML = `<div class="cart-empty"><i class="fa fa-shopping-bag" aria-hidden="true"></i><p>Your cart is empty</p><button class="discovery-btn btn-primary btn-sm" style="margin-top:var(--space-md)" data-app-action="closeCart">Start Shopping</button></div>`;
      footer.style.display = "none";
      return;
    }
    footer.style.display = "block";
    container.innerHTML = this.store.data.cart
      .map(
        (
          item,
          i,
        ) => `<div class="cart-item" style="animation: slideInRight 0.3s ease both; animation-delay: ${i * 40}ms">
            <img class="cart-item-img" src="${this.ui.escAttr(item.imgUrl || "")}" alt="${this.ui.esc(item.title)}" onerror="this.style.display='none'">
            <div class="cart-item-info">
                <div class="cart-item-title">${this.ui.esc(item.title)}</div>
                <div class="cart-item-price">${this.ui.formatPrice(item.price)}</div>
                <div style="font-size:var(--text-xs);color:var(--color-text-muted);margin-bottom:var(--space-xs)">${this.ui.esc(item.size || "")} ${this.ui.esc(item.color || "")}</div>
                <div style="display:flex; align-items:center; gap:var(--space-sm);">
                    <div style="display:flex; align-items:center; border:1px solid var(--color-border); border-radius:var(--radius-sm); overflow:hidden;">
                        <button style="border:none;background:var(--color-surface-alt);padding:4px 8px;cursor:pointer;" aria-label="Decrease quantity" data-app-action="updateCartQty" data-arg0="${this.ui.escJS(item.variant_id || item.id)}" data-arg1="${this.ui.escJS(item.size || "")}" data-arg2="-1" data-arg3="${this.ui.escJS(item.key || "")}"><svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="vertical-align: middle;"><line x1="5" y1="12" x2="19" y2="12"></line></svg></button>
                        <span style="padding:0 8px;font-size:var(--text-sm);">${item.qty || 1}</span>
                        <button style="border:none;background:var(--color-surface-alt);padding:4px 8px;cursor:pointer;" aria-label="Increase quantity" data-app-action="updateCartQty" data-arg0="${this.ui.escJS(item.variant_id || item.id)}" data-arg1="${this.ui.escJS(item.size || "")}" data-arg2="1" data-arg3="${this.ui.escJS(item.key || "")}"><svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="vertical-align: middle;"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg></button>
                    </div>
                    <button class="cart-item-remove" data-app-action="removeCartItem" data-arg0="${this.ui.escJS(item.variant_id || item.id)}" data-arg1="${this.ui.escJS(item.size || "")}" data-arg2="${this.ui.escJS(item.key || "")}" style="margin-top:0;"><i class="fa fa-trash-alt"></i> Remove</button>
                </div>
            </div>
        </div>`,
      )
      .join("");
    document.getElementById("cart-subtotal").textContent =
      `$${this.store.getCartTotal().toFixed(2)}`;
  }

  async removeCartItem(variantId, size = "", lineKey = "") {
    await this.store.removeFromCart(variantId, size, lineKey);
    this._updateCartUI();
    this._renderCart();
    this.toast("Item removed from cart");
  }

  async removeFromCart(variantId, size = "", lineKey = "") {
    await this.removeCartItem(variantId, size, lineKey);
  }

  async updateCartQty(id, size, delta, lineKey = "") {
    await this.store.updateCartQty(id, size, Number(delta), lineKey);
    this._updateCartUI();
    this._renderCart();
  }

  _updateCartUI() {
    const count = this.store.data.cart.reduce((s, c) => s + (c.qty || c.quantity || 1), 0);
    const badges = document.querySelectorAll(
      "#cart-count, .assistant-cart-count, [data-cart-count], .cart-bubble__text-count, [data-testid='cart-bubble']",
    );
    badges.forEach((badge) => {
      badge.textContent = count ? String(count) : "";
      badge.setAttribute("data-count", count);
      badge.setAttribute(
        "aria-label",
        count ? `${count} items in cart` : "Cart is empty",
      );
      badge.classList.toggle("hidden", count === 0);
    });

    document.querySelectorAll(".cart-bubble").forEach((bubble) => {
      bubble.classList.toggle("visually-hidden", count === 0);
    });

    document.querySelectorAll("cart-icon").forEach((icon) => {
      if (typeof icon.renderCartBubble === "function") {
        icon.renderCartBubble(count, false);
      }
    });

    const evtDetail = {
      resource: this.store._cart || { item_count: count, items: this.store.data.cart },
      sourceId: "shopping-agent",
      data: { itemCount: count, source: "shopping-agent" },
      item_count: count,
    };

    document.dispatchEvent(new CustomEvent("cart:update", { bubbles: true, detail: evtDetail }));
    document.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail: evtDetail }));
    window.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail: evtDetail }));
    document.documentElement.dispatchEvent(new CustomEvent("cart:updated", { bubbles: true, detail: evtDetail }));

    if (this.cartOpen) {
      this._renderCart();
    }
  }

  // --- Mobile Menu ---
  openMobileMenu() {
    document.getElementById("mobile-menu").classList.add("open");
    document.getElementById("mobile-menu").removeAttribute("inert");
    document
      .getElementById("menu-toggle")
      .setAttribute("aria-expanded", "true");
  }
  closeMobileMenu() {
    document.getElementById("mobile-menu").classList.remove("open");
    document.getElementById("mobile-menu").setAttribute("inert", "");
    document
      .getElementById("menu-toggle")
      .setAttribute("aria-expanded", "false");
  }

  // --- Close All ---
  _closeAll() {
    this.toggleToolMenu(false);
    if (this.drawerOpen) this.toggleDrawer();
    if (this.cartOpen) this.closeCart();
    this.closeMobileMenu();
  }

  // --- Toast ---
  toast(msg, duration = 2500) {
    const el = document.getElementById("toast");
    el.innerHTML = `<i class="fa fa-check-circle" aria-hidden="true"></i> ${this.ui.esc(msg)}
            <div class="toast-progress" style="animation-duration: ${duration}ms"></div>`;
    el.classList.add("show");
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => el.classList.remove("show"), duration);
  }

  // --- Chat Messages ---
  _addMsg(content, role = "assistant", isHTML = false, id = null) {
    const div = document.createElement("div");
    div.className = `message ${role}`;
    if (id) div.id = id;
    if (isHTML) {
      div.innerHTML = content;
    } else {
      div.textContent = content;
    }
    this.chatArea.appendChild(div);
    this.chatArea.scrollTop = this.chatArea.scrollHeight;
    setTimeout(() => this._updateScrollIndicator(), 60);

    // Save to IDB (unless it's a temporary loading message)
    if (!id || (!id.startsWith("load-") && !id.startsWith("skel-"))) {
      this.db
        .saveMessage(
          this.store.sessionId,
          role,
          isHTML ? div.innerHTML : div.textContent,
        )
        .catch(console.warn);
    }
  }

  _updateScrollIndicator() {
    if (!this.scrollIndicator || !this.chatArea) return;
    const { scrollTop, scrollHeight, clientHeight } = this.chatArea;
    const isScrollable = scrollHeight - clientHeight > 30;
    const isNotAtBottom = scrollTop + clientHeight < scrollHeight - 45;
    if (isScrollable && isNotAtBottom) {
      this.scrollIndicator.removeAttribute("hidden");
    } else {
      this.scrollIndicator.setAttribute("hidden", "");
    }
  }

  _removeMsg(id) {
    const el = document.getElementById(id);
    if (el) el.remove();
  }

  // --- Greeting ---
  _sendGreeting() {
    const payload = window.ShopifyContextPayload || {};

    let greeting = `Hello! I'm your personal rug consultant. How can I help you today?`;
    let chips = [];
    let showWelcomePrompt = true;

    if (this.contextProduct) {
      greeting = `Looking at the <strong>${this.ui.esc(this.contextProduct.title)}</strong>? I can show you how it looks in your room, or find similar styles.`;
      chips = ["See in my room", "Is it easy to clean?", "Show similar styles"];
      showWelcomePrompt = false;
    } else if (payload.template === "collection" && payload.collection) {
      greeting = `Browsing the <strong>${this.ui.esc(payload.collection.title)}</strong> collection? I can help you narrow down by size, color, or budget.`;
      chips = ["Show top rated", "Filter by size", "What materials are best?"];
      showWelcomePrompt = false;
    } else if (payload.cart && payload.cart.item_count > 0) {
      greeting = `You have ${payload.cart.item_count} item${payload.cart.item_count > 1 ? "s" : ""} in your cart — want a second opinion before you check out?`;
      chips = ["Do I need a rug pad?", "Care instructions", "Track my order"];
      showWelcomePrompt = false;
    }

    this._addMsg(greeting, "assistant", true);

    if (showWelcomePrompt) {
      this._addMsg(this.ui.welcomePrompt(), "assistant", true);
    } else if (chips.length > 0) {
      this._addMsg(this.ui.chips(chips), "assistant", true);
    }
  }

  // --- Send ---
  async _send() {
    const text = this.chatInput.value.trim();
    if (!text || this.sending) return;

    // Interactive Service Flow step check
    if (this.pendingServiceRequest) {
      this.sending = true;
      this.sendBtn.disabled = true;
      this.chatInput.value = "";
      this._addMsg(text, "user");
      
      const handled = await this._continueServiceFlow(text);
      if (handled !== false) {
        this.sending = false;
        this.sendBtn.disabled = false;
        return;
      }
      // If it returned false, the flow was aborted (e.g. user typed a shopping query)
      // Fall through to normal processing!
    } else {
      this.sending = true;
      this.sendBtn.disabled = true;
      this.chatInput.value = "";
      this._addMsg(text, "user");
    }

    // Agent transfer: intercept before any backend call or chat message
    if (this._isStoreContactQuery(text)) {
      await this._openStoreOwnerChat();
      this.sending = false;
      this.sendBtn.disabled = false;
      return;
    }

    await this._processQuery(text);
    this.sending = false;
    this.sendBtn.disabled = false;
  }

  startServiceFlow(initialService = null) {
    this._ensureDrawer();
    this.toggleToolMenu(false);

    const rawService = initialService ? String(initialService).trim() : null;
    const serviceName = rawService
      ? rawService.replace(/^[^\w\s]+/, "").trim()
      : null;

    if (serviceName) {
      this.pendingServiceRequest = { service: serviceName, step: "NAME" };
      this._addMsg(
        `Got it! 🛠️ <strong>${this.ui.esc(serviceName)}</strong>.<br>May I have your <strong>Full Name</strong> so our service team knows who to address?`,
        "assistant",
        true,
      );
    } else {
      this.pendingServiceRequest = { service: null, step: "SERVICE" };
      this._addMsg(
        `What service can we help you with today? Tap an option below or type your request:`,
        "assistant",
      );
      const serviceChips = [
        "🛠️ Custom Rug Sizing",
        "🧼 Professional Rug Cleaning",
        "🧵 Repair & Restoration",
        "🏡 In-Home Consultation",
        "📦 Delivery & Installation",
      ];
      this._addMsg(this.ui.chips(serviceChips), "assistant", true);
    }
    setTimeout(() => {
      if (this.chatArea) {
        this.chatArea.scrollTop = this.chatArea.scrollHeight;
        this._updateScrollIndicator();
      }
    }, 80);
  }

  async _continueServiceFlow(text) {
    if (!this.pendingServiceRequest) return false;

    const req = this.pendingServiceRequest;
    const cleanText = (text || "").trim();

    if (/\b(cancel|exit|stop|nevermind|never mind|quit|no)\b/i.test(cleanText) && cleanText.length < 15) {
        this.pendingServiceRequest = null;
        this._addMsg("Okay, I've canceled your service request. Let me know if you need anything else!", "assistant");
        return true;
    }

    if (/\b(cost|price|pricing|how much|fee|quote|estimate)\b/i.test(cleanText)) {
        this.pendingServiceRequest = null;
        this._addMsg(`For information regarding the cost of our services, please reach out to our team directly. Our staff will be happy to provide an estimate and assist you during store hours.<br><br><button class="discovery-btn btn-primary btn-sm" data-app-action="openShopifyInbox" style="margin-top: 8px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-weight: 600; padding: 10px 16px; border-radius: 6px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); border: 1px solid var(--color-border); background: var(--color-accent); color: #fff;"><i class="fa fa-comments"></i> Chat Live with Store</button>`, "assistant", true);
        return true;
    }

    // Check if the input matches one of our predefined service chips
    const serviceChipsLower = [
        "custom rug sizing",
        "professional rug cleaning",
        "repair & restoration",
        "in-home consultation",
        "delivery & installation"
    ];
    const isExactService = serviceChipsLower.some(chip => cleanText.toLowerCase().includes(chip));

    // Abort hardcoded flow and pass to LLM if the user expresses shopping intent (but not if they clicked a service chip)
    if (!isExactService && /\b(rug|rugs|shop|buy|looking for|show me|find|size|color)\b/i.test(cleanText)) {
        this.pendingServiceRequest = null;
        return false;
    }

    if (req.step === "SERVICE") {
      const label = cleanText.replace(/^[^\w\s]+/, "").trim() || cleanText;
      req.service = label;
      req.step = "NAME";
      this._addMsg(
        `Got it! 🛠️ <strong>${this.ui.esc(label)}</strong>.<br>May I have your <strong>Full Name</strong> so our service team knows who to address?`,
        "assistant",
        true,
      );
    } else if (req.step === "NAME") {
      const sanitizedName = cleanText.replace(/[^a-zA-Z\s\-]/g, "").trim();
      if (!sanitizedName || sanitizedName.length < 2) {
          this._addMsg(`Please provide a valid name.`, "assistant");
          return true;
      }
      req.name = sanitizedName;
      req.step = "PHONE";
      const firstName = sanitizedName.split(" ")[0];
      this._addMsg(
        `Thanks <strong>${this.ui.esc(firstName)}</strong>!<br>What is your <strong>Phone Number</strong> (or mobile) so our team can call or text you details?`,
        "assistant",
        true,
      );
    } else if (req.step === "PHONE") {
      const sanitizedPhone = cleanText.replace(/[^\d\s\+\-\(\)]/g, "").trim();
      const digits = sanitizedPhone.replace(/\D/g, "");
      if (digits.length < 7) {
          this._addMsg(`Please provide a valid phone number.`, "assistant");
          return true;
      }
      req.phone = sanitizedPhone;
      const finalReq = { ...req };
      this.pendingServiceRequest = null;

      this.sending = true;
      if (this.sendBtn) this.sendBtn.disabled = true;

      const loadId = "load-service-" + Date.now();
      this._addMsg(`<div id="${loadId}"><i class="fa fa-spinner fa-spin"></i> Submitting your request...</div>`, "assistant", true);

      try {
          const res = await this.api.contactStore({
              name: finalReq.name,
              phone: finalReq.phone,
              service: finalReq.service,
              notes: "Service request submitted via AI Assistant.",
              page_url: window.location.href,
          });

          await this._sendNativeShopifyContact({
              name: finalReq.name,
              phone: finalReq.phone,
              service: finalReq.service,
              notes: "Service request submitted via AI Assistant.",
              body: `Service Request: ${finalReq.service}\nName: ${finalReq.name}\nPhone: ${finalReq.phone}`
          });
          
          document.getElementById(loadId)?.remove();

          if (res && res.success === true && res.status === "delivered") {
              this._addMsg(
                `<div class="confirmation-card interactive-service-card" style="background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 12px; padding: 16px; margin-top: 8px;">
                    <h4 style="margin-top: 0; color: var(--color-success); display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600;">
                        <i class="fa fa-check-circle"></i> Service Request Sent!
                    </h4>
                    <p style="font-size: 13px; color: var(--color-text-secondary); margin-bottom: 0;">Thanks for contacting us! Our team has received your request for <strong>${this.ui.esc(finalReq.service)}</strong> and will reach out to you shortly at <strong>${this.ui.esc(finalReq.phone)}</strong>.</p>
                </div>`,
                "assistant",
                true
              );
          } else {
              const errorReason = res?.error || res?.message || "We could not deliver your request automatically right now.";
              const storePhone = window.ShopifyContextPayload?.shop?.phone || "our store team";
              this._addMsg(
                `<div class="confirmation-card interactive-service-card" style="background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 12px; padding: 16px; margin-top: 8px;">
                    <h4 style="margin-top: 0; color: #d32f2f; display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600;">
                        <i class="fa fa-exclamation-triangle"></i> Request Delivery Pending
                    </h4>
                    <p style="font-size: 13px; color: var(--color-text-secondary); margin-bottom: 8px;">${this.ui.esc(errorReason)}</p>
                    <p style="font-size: 13px; color: var(--color-text-secondary); margin-bottom: 0;">Please call or text <strong>${this.ui.esc(storePhone)}</strong> directly or tap below to open Live Chat.</p>
                    <button class="discovery-btn btn-primary btn-sm" data-app-action="openShopifyInbox" style="margin-top: 10px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;"><i class="fa fa-comments"></i> Chat Live with Store</button>
                </div>`,
                "assistant",
                true
              );
          }
      } catch (e) {
          document.getElementById(loadId)?.remove();
          const errorReason = e?.message || "We could not deliver your request automatically right now.";
          const storePhone = window.ShopifyContextPayload?.shop?.phone || "our store team";
          this._addMsg(
            `<div class="confirmation-card interactive-service-card" style="background: var(--color-surface); border: 1px solid var(--color-border); border-radius: 12px; padding: 16px; margin-top: 8px;">
                <h4 style="margin-top: 0; color: #d32f2f; display: flex; align-items: center; gap: 8px; font-size: 15px; font-weight: 600;">
                    <i class="fa fa-exclamation-triangle"></i> Request Delivery Pending
                </h4>
                <p style="font-size: 13px; color: var(--color-text-secondary); margin-bottom: 8px;">${this.ui.esc(errorReason)}</p>
                <p style="font-size: 13px; color: var(--color-text-secondary); margin-bottom: 0;">Please call or text <strong>${this.ui.esc(storePhone)}</strong> directly or tap below to open Live Chat.</p>
                <button class="discovery-btn btn-primary btn-sm" data-app-action="openShopifyInbox" style="margin-top: 10px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px;"><i class="fa fa-comments"></i> Chat Live with Store</button>
            </div>`,
            "assistant",
            true
          );
      } finally {
          this.sending = false;
          if (this.sendBtn) this.sendBtn.disabled = false;
      }
    }
    setTimeout(() => {
      if (this.chatArea) {
        this.chatArea.scrollTop = this.chatArea.scrollHeight;
        this._updateScrollIndicator();
      }
    }, 80);
    return true;
  }

  sendAction(action) {
    this._ensureDrawer();
    this.toggleToolMenu(false);
    if (
      this.pendingServiceRequest &&
      this.pendingServiceRequest.step === "SERVICE"
    ) {
      this._addMsg(action, "user");
      void this._continueServiceFlow(action);
      return;
    }
    if (this.pendingVisualizerIntent) {
      const size = this._findSizeFromText(
        action,
        this.pendingVisualizerIntent.product,
      );
      if (size) {
        this._addMsg(action, "user");
        const product = {
          ...this.pendingVisualizerIntent.product,
          size,
          sizeSelected: true,
          selectedSizeExplicit: true,
        };
        this.pendingVisualizerIntent = null;
        this._launchRoomManagerForProduct(product);
        return;
      }
    }
    if (this.pendingCartIntent) {
      const size = this._findSizeFromText(
        action,
        this.pendingCartIntent.product,
      );
      if (size) {
        this._addMsg(action, "user");
        const product = {
          ...this.pendingCartIntent.product,
          size,
          sizeSelected: true,
          selectedSizeExplicit: true,
        };
        this.pendingCartIntent = null;
        this._addProductToCartWithSize(product, null);
        return;
      }
    }
    if (
      action === "visualizer" ||
      /^see in my room$/i.test(action || "") ||
      /^visualize in my room$/i.test(action || "")
    ) {
      this._handleLocalVisualizerCommand("see in my room", null, true);
    } else if (
      /^analy[sz]e my room$/i.test(action || "") ||
      /^analy[sz]e room$/i.test(action || "")
    ) {
      this._triggerVisualizer("analyze");
    } else if (
      action === "Book an appointment" ||
      action === "consultation" ||
      action === "services"
    ) {
      void this._openStoreOwnerChat();
    } else if (
      action === "contact-store" ||
      this._isStoreContactQuery(action)
    ) {
      void this._openStoreOwnerChat();
      void this._openStoreOwnerChat();
    } else if (/^compare/i.test(action || "")) {
      this._addMsg(
        "Product comparison is turned off for this store. I can help narrow by size, material, care, price, or rug pads.",
        "assistant",
      );
      this.chatArea.scrollTop = this.chatArea.scrollHeight;
    } else if (action === "track-order") {
      void this._openStoreOwnerChat();
    } else if (action === "history" || action === "recently-viewed") {
      this._handleLocalHistoryCommand("recently viewed");
    } else {
      this._addMsg(action, "user");
      this._processQuery(action);
    }
  }

  async _processQuery(query) {
    this._syncSelectedVariantContext(true);
    if (this._isStoreContactQuery(query)) {
      await this._openStoreOwnerChat();
      return;
    }
    const referencedProduct = this._resolveProductReference(query);
    if (referencedProduct) this._setContextProduct(referencedProduct);
    if (this._handleRoomIntent(query, referencedProduct)) return;
    query = this._rewriteContextualFollowup(query);
    const budgetClearingQuery = this._isBudgetClearingQuery(query);
    const shouldClearStoredBudget =
      this._shouldClearStoredBudgetForQuery(query);
    if (shouldClearStoredBudget) this._clearStoredBudget();
    if (budgetClearingQuery) {
      const recentSearch = (this.store.data.searchHistory || []).find(
        (item) => item && !this._isBudgetClearingQuery(item),
      );
      if (recentSearch && !this._isProductSeekingQuery(query)) {
        query = recentSearch;
      }
    }
    this.sending = true;
    this.sendBtn.disabled = true;
    const loadId = "load-" + Date.now();
    const skelId = "skel-" + Date.now();
    const historyHandled = this._handleLocalHistoryCommand(query);
    if (historyHandled) {
      this.sending = false;
      this.sendBtn.disabled = false;
      return;
    }
    const orderTrackingHandled = this._handleLocalOrderTrackingCommand(query);
    if (orderTrackingHandled) {
      this.sending = false;
      this.sendBtn.disabled = false;
      return;
    }
    const productHandled = this._handleLocalProductCommand(
      query,
      referencedProduct,
    );
    if (productHandled) {
      this.sending = false;
      this.sendBtn.disabled = false;
      return;
    }
    const serviceHandled = this._handleLocalServiceCommand(query);
    if (serviceHandled) {
      this.sending = false;
      this.sendBtn.disabled = false;
      return;
    }
    const explicitCountHandled =
      await this._handleExplicitProductCountQuery(query);
    if (explicitCountHandled) {
      this.sending = false;
      this.sendBtn.disabled = false;
      return;
    }
    const rugPadBrowseHandled = await this._handleRugPadProductBrowse(query);
    if (rugPadBrowseHandled) {
      this.sending = false;
      this.sendBtn.disabled = false;
      return;
    }
    const visualizerHandled = this._handleRoomIntent(query, referencedProduct);
    if (visualizerHandled) {
      this.sending = false;
      this.sendBtn.disabled = false;
      return;
    }
    const cartHandled = await this._handleLocalCartCommand(query);
    if (cartHandled) {
      this.sending = false;
      this.sendBtn.disabled = false;
      return;
    }
    const localHandled = await this._handleLocalCommerceIntent(query);
    if (localHandled) {
      this.sending = false;
      this.sendBtn.disabled = false;
      return;
    }

    const avatar = document.querySelector(".sparky-avatar");
    if (avatar) avatar.classList.add("thinking");

    const productSeeking = this._isProductSeekingQuery(query);
    this._addMsg(
      productSeeking
        ? this.ui.browseLoading(query)
        : this.ui.chatLoading(query),
      "assistant",
      true,
      loadId,
    );
    if (productSeeking)
      this._addMsg(this.ui.skeletonGrid(3), "assistant", true, skelId);

    const stages = productSeeking
      ? ["Filtering by your preferences...", "Ranking best matches..."]
      : ["Checking product context...", "Preparing a clear answer..."];
    const stageTimers = stages.map((stageText, i) =>
      setTimeout(
        () => {
          const statusEl = document.querySelector(`#${loadId} .status-text`);
          if (statusEl) statusEl.textContent = stageText;
        },
        800 * (i + 1),
      ),
    );

    try {
      this.store.addSearch(query);
      const queryMaxPrice = this._extractMaxPrice(query);
      if (queryMaxPrice)
        this.store.updateFacts({ budget: queryMaxPrice }, true);
      const memory = await this._assistantMemoryWithMessages();
      if (shouldClearStoredBudget && memory) memory.budget = null;
      const data = await this.api.message(
        query,
        this.store.sessionId,
        {},
        await this._pageContext(),
        memory,
      );

      this._removeMsg(loadId);
      this._removeMsg(skelId);
      stageTimers.forEach((t) => clearTimeout(t));
      if (avatar) avatar.classList.remove("thinking");

      if (!data || data.error) {
        this._addMsg(
          data?.message || "Sorry, I couldn't process that. Please try again.",
          "assistant",
        );
        return;
      }

      const type = data.type || data.intent;
      const payload = data.payload || {};
      const isProductResponse =
        type === "products" ||
        type === "product_discovery" ||
        type === "search";
      const hasProductList = Array.isArray(payload.products);
      const zeroProductResponse =
        isProductResponse && hasProductList && payload.products.length === 0;
      const budgetFilteredResponse = payload.products?.length
        ? this._productsForBudget(payload.products, query, payload.facts)
        : null;
      if (budgetFilteredResponse?.maxPrice) {
        payload.products = budgetFilteredResponse.products;
        payload.total_size = budgetFilteredResponse.products.length;
      }
      
      if (payload.products && payload.products.length > 0) {
        payload.products = await Promise.all(payload.products.map(async (rec) => {
          try {
            const res = await fetch(`/products/${rec.handle || rec.id}.js`);
            if (res.ok) {
              const shopifyProduct = await res.json();
              rec.variants = shopifyProduct.variants || [];
              let matchedVariant = null;
              if (Array.isArray(shopifyProduct.variants)) {
                const targetId = String(rec.selected_variant?.id || rec.variant_id || "");
                const targetSize = String(rec.size || "").toLowerCase().replace(/[\s\-_'"]/g, "");
                matchedVariant = shopifyProduct.variants.find((v) => {
                  if (targetId && String(v.id) === targetId) return true;
                  if (targetSize) {
                    const vTitle = String(v.title || "").toLowerCase().replace(/[\s\-_'"]/g, "");
                    const vOpt1 = String(v.option1 || "").toLowerCase().replace(/[\s\-_'"]/g, "");
                    const vOpt2 = String(v.option2 || "").toLowerCase().replace(/[\s\-_'"]/g, "");
                    if (vTitle === targetSize || vOpt1 === targetSize || vOpt2 === targetSize) return true;
                  }
                  return false;
                });
              }
              if (matchedVariant) {
                if (matchedVariant.price) rec.price = matchedVariant.price / 100;
                if (matchedVariant.compare_at_price) {
                  rec.compare_at_price = matchedVariant.compare_at_price / 100;
                  rec.was_price = rec.compare_at_price;
                }
                rec.selected_variant = matchedVariant;
                rec.variant_id = matchedVariant.id;
              } else {
                if (!rec.price && shopifyProduct.price) {
                  rec.price = shopifyProduct.price / 100;
                }
                if (!rec.compare_at_price) {
                  const compareAt = shopifyProduct.compare_at_price || (shopifyProduct.variants?.[0]?.compare_at_price);
                  if (compareAt) {
                    rec.compare_at_price = compareAt / 100;
                    rec.was_price = compareAt / 100;
                  }
                }
              }
            }
          } catch (e) {
            console.warn("Failed to enrich payload product", rec.handle || rec.id, e);
          }
          return rec;
        }));
      }
      if (payload.product) {
        const cachedProduct = this.productCatalog.get(
          String(payload.product.id),
        );
        this._setContextProduct({
          ...(cachedProduct || {}),
          ...payload.product,
        });
      }

      if (data.message) {
        let msgContent = this._cleanAssistantText(data.message);
        let msgIsHtml = false;
        if (type === "review_summary")
          msgContent = `<div class="review-summary-card">${data.message}</div>`;
        if (type === "review_summary") msgIsHtml = true;
        else if (type === "room_suitability") {
          msgContent = `<div class="suitability-card"><i class="fa fa-home"></i> ${this.ui.esc(msgContent)}</div>`;
          msgIsHtml = true;
        }
        const staleProductMessage =
          budgetFilteredResponse?.removed > 0 &&
          (type === "products" ||
            type === "product_discovery" ||
            type === "search");
        if (!staleProductMessage && !zeroProductResponse)
          this._addMsg(msgContent, "assistant", msgIsHtml);
        if (payload.product) {
          const cachedProduct = this.productCatalog.get(
            String(payload.product.id),
          );
          this._addMsg(
            this.ui.productActionCard({
              ...(cachedProduct || {}),
              ...payload.product,
            }),
            "assistant",
            true,
          );
        }
      }

      if (
        !zeroProductResponse &&
        payload.follow_up_questions &&
        payload.follow_up_questions.length > 0
      ) {
        this._addMsg(
          this.ui.chips(payload.follow_up_questions.slice(0, 3)),
          "assistant",
          true,
        );
      }

      // Handle intent-specific actions
      if (
        data.action_trigger === "OPEN_LIVE_CHAT" ||
        payload.action_trigger === "OPEN_LIVE_CHAT" ||
        (payload.payload && payload.payload.action_trigger === "OPEN_LIVE_CHAT")
      ) {
        this._openLiveChat();
      } else if (type === "products") {
        if (payload.products?.length > 0) {
          const filtered = this._productsForBudget(
            payload.products,
            query,
            payload.facts,
          );
          if (!filtered.products.length) {
            this._addMsg(
              `I found rugs, but none were under ${this.ui.formatPrice(filtered.maxPrice)}. Try raising the budget a little or changing the style/color.`,
              "assistant",
            );
          } else {
            this._addMsg(
              this.ui.horizontalProductCarousel(filtered.products),
              "assistant",
              true,
            );
            this.currentBrowseResults = filtered.products;
          }
        } else if (budgetFilteredResponse?.maxPrice) {
          this._addMsg(
            `I found rugs, but none were under ${this.ui.formatPrice(budgetFilteredResponse.maxPrice)}. Try raising the budget a little or changing the style/color.`,
            "assistant",
          );
        } else if (zeroProductResponse) {
          this._addMsg(
            this._noProductMatchMessage(query, payload.facts),
            "assistant",
          );
        }
      } else if (type === "product_discovery" || type === "search") {
        if (payload.products?.length > 0) {
          const filtered = this._productsForBudget(
            payload.products,
            query,
            payload.facts,
          );
          if (!filtered.products.length) {
            this._addMsg(
              `I found rugs, but none were under ${this.ui.formatPrice(filtered.maxPrice)}. Try raising the budget a little or changing the style/color.`,
              "assistant",
            );
          } else {
            const reason = payload.reasoning || "Matching products";
            this._addMsg(
              this.ui.horizontalProductCarousel(filtered.products, reason),
              "assistant",
              true,
            );
            this.currentBrowseResults = filtered.products;
          }
        } else if (budgetFilteredResponse?.maxPrice) {
          this._addMsg(
            `I found rugs, but none were under ${this.ui.formatPrice(budgetFilteredResponse.maxPrice)}. Try raising the budget a little or changing the style/color.`,
            "assistant",
          );
        } else if (zeroProductResponse) {
          this._addMsg(
            this._noProductMatchMessage(query, payload.facts),
            "assistant",
          );
        } else if (payload.query) {
          await this._doSearch(payload.query);
        } else {
          await this._doBrowse(payload.facts || this.store.data.facts);
        }
      } else if (type === "style_discovery" || type === "conversational") {
        if (!data.message && payload.new_messages) {
          payload.new_messages.forEach((msg) => {
            if (msg.role === "assistant")
              this._addMsg(msg.content, "assistant");
          });
        }
      } else if (type === "visualizer_prompt") {
        this._triggerVisualizer("visualize");
      } else if (type === "analyze_room_prompt") {
        this._triggerVisualizer("analyze");
      } else if (
        type === "product_comparison" ||
        type === "comparison_prompt"
      ) {
        this._addMsg(
          "Product comparison is turned off for this store. Ask me about size, care, material, price, or rug pads instead.",
          "assistant",
        );
      } else if (type === "add_to_cart") {
        let success = false;
        if (payload.product) {
          const cachedProduct = this.productCatalog.get(
            String(payload.product.id),
          );
          const handle =
            payload.product.handle ||
            cachedProduct?.handle ||
            cachedProduct?.shopify_handle ||
            payload.product.id;
          success = await this.quickAddToCart(
            handle,
            payload.product.title,
            payload.product.price,
            payload.product.image_url || payload.product.imgUrl,
            payload.product.size,
            payload.product.color,
          );
        }

        const msgs = [];
        if (success && payload.product)
          msgs.push(
            `Added <strong>${payload.product.title}</strong> to your cart.`,
          );
        if (payload.similar_rugs?.length > 0)
          msgs.push(
            this.ui.horizontalProductCarousel(
              payload.similar_rugs,
              "You may also like",
            ),
          );
        if (payload.rug_pads?.length > 0)
          msgs.push(
            this.ui.horizontalProductCarousel(
              payload.rug_pads,
              "Recommended Rug Pads",
            ),
          );
        if (msgs.length > 0)
          this._addMsg(msgs.join("<br><br>"), "assistant", true);
      }

      let backendFacts = null;
      if (data.facts && Object.keys(data.facts).length > 0)
        backendFacts = data.facts;
      else if (payload.facts && Object.keys(payload.facts).length > 0)
        backendFacts = payload.facts;
      else if (data.filter_state && Object.keys(data.filter_state).length > 0)
        backendFacts = data.filter_state;
      else if (payload.filter_state) backendFacts = payload.filter_state;
      if (backendFacts) {
        const factsToStore = shouldClearStoredBudget
          ? this._factsWithoutStoredBudget(backendFacts)
          : backendFacts;
        this.store.updateFacts(factsToStore, true);
      }
    } catch (err) {
      this._removeMsg(loadId);
      this._removeMsg(skelId);
      stageTimers.forEach((t) => clearTimeout(t));
      if (avatar) avatar.classList.remove("thinking");
      console.error("Backend error:", err);
      this._addMsg(
        "I'm having trouble connecting right now. Please try again in a moment.",
        "assistant",
      );
    }

    this.sending = false;
    this.sendBtn.disabled = false;
  }

  _isStoreContactQuery(text = "") {
    const q = String(text || "")
      .trim()
      .toLowerCase();
    if (!q) return false;
    // Broad match for agent transfer / store contact intents
    return /\b(talk\s+to\s+(the\s+)?(store\s*)?owner|talk\s+to\s+(the\s+)?store|contact\s+(the\s+)?(store|owner|team|us)|message\s+(the\s+)?(store|owner|team)|speak\s+(to|with)\s+(the\s+)?(store|owner|human|team|agent|support|representative|rep)|open\s+(shopify\s+)?(inbox|chat)|shopify\s+(inbox|chat)|chat\s+with\s+(us|owner|store|team|support)|live\s+chat|start\s+chat|human\s+(agent|support|help)|\bagent\b|\bhuman\b|\brepresentative\b|\breal\s+person\b|\bsupport\s*(team|agent)?\b|\bcustomer\s+(service|support|care)\b|\bhelp\s+desk\b|\bescalate\b|\btransfer\b|\bconnect\s+(me\s+)?(to|with)\s+(a\s+)?(human|agent|person|owner|store|team|support|representative|rep)\b)\b/i.test(
      q,
    );
  }

  _isStoreHandoffStatus(content = "") {
    const text = String(content)
      .replace(/<[^>]+>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return (
      /I opened Shopify Inbox/i.test(text) ||
      /I found the chat widget on this page/i.test(text) ||
      /I could not find a live chat widget on this page/i.test(text) ||
      /Open Shopify Chat to send this to the store team/i.test(text) ||
      /Shopify Chat Handoff Ready/i.test(text) ||
      /The backend prepared the Shopify Inbox handoff/i.test(text)
    );
  }

  _removeStoreHandoffStatusMessages() {
    Array.from(
      this.chatArea?.querySelectorAll(".message.assistant") || [],
    ).forEach((el) => {
      if (this._isStoreHandoffStatus(el.innerHTML || el.textContent))
        el.remove();
    });
  }

  async _openStoreOwnerChat() {
    this._removeStoreHandoffStatusMessages();
    this._addMsg("Switching to a store agent. Opening chat...", "assistant");
    this.toast("Connecting you to a store agent...");
    await new Promise((r) => setTimeout(r, 1200));
    // Close the Discovery-AI drawer first
    if (this.drawerOpen) {
      this.toggleDrawer();
      // Brief delay to let the drawer animation finish and Shopify launcher reappear
      await new Promise((r) => setTimeout(r, 350));
    }
    const status = await this.openShopifyInbox();
    if (status !== "opened") {
      this.toast("Click the Chat button in the bottom-right corner");
    }
    return status === "opened";
  }

  // --- Conversational Discovery (direct API) ---
  async _doConversational(query) {
    try {
      let facts = { ...this.store.data.facts };
      if (this.contextProduct) facts._current_product = this.contextProduct;

      const data = await this.api.discover(
        query,
        "conversational",
        this.store.data.conversationState,
        facts,
      );
      this.store.data.conversationState = data.state;
      if (data.facts) this.store.updateFacts(data.facts, true);
      this.store.save();

      if (data.new_messages) {
        data.new_messages.forEach((msg) => {
          if (msg.role === "assistant") this._addMsg(msg.content, "assistant");
        });
      }
    } catch (err) {
      console.error("Conversational error:", err);
    }
  }

  // --- Search ---
  async _doSearch(q) {
    try {
      const data = await this.api.search(q);
      if (data.products?.length > 0) {
        const filtered = this._productsForBudget(
          data.products,
          q,
          this.store.data.facts,
        );
        if (!filtered.products.length) {
          this._addMsg(
            `I found rugs, but none were under ${this.ui.formatPrice(filtered.maxPrice)}. Try a higher budget or fewer filters.`,
            "assistant",
          );
          return;
        }
        const reason = `Found ${filtered.products.length} products matching "${q}"`;
        this._addMsg(
          this.ui.horizontalProductCarousel(filtered.products, reason),
          "assistant",
          true,
        );
        this.currentSearchResults = filtered.products;
      } else {
        this._addMsg(
          "No exact matches found. Try different keywords or tell me more about what you're looking for.",
          "assistant",
        );
      }
    } catch (err) {
      console.error("Search error:", err);
    }
  }

  // --- Browse ---
  async _doBrowse(facts, limit = 4, options = {}) {
    try {
      const data = await this.api.browse(facts, 1, limit);
      let products = data.products || [];
      if (options.requireRugPads)
        products = products.filter((product) => this._isRugPadProduct(product));
      if (!products.length && options.requireRugPads) {
        const fallbackFacts = {
          ...facts,
          category: undefined,
          product_type: undefined,
          query: "rug pad non slip rug grip underlay",
          search_query: "rug pad",
        };
        const fallbackData = await this.api.browse(
          fallbackFacts,
          1,
          Math.max(limit, 8),
        );
        products = (
          fallbackData.products ||
          fallbackData.payload?.products ||
          []
        ).filter((product) => this._isRugPadProduct(product));
      }
      if (!products.length && options.requireRugPads) {
        products = await this._fetchRugPadProducts(facts?.size, limit);
      }
      if (products.length > 0) {
        const filtered = this._productsForBudget(
          products,
          options.query || "",
          facts,
        );
        if (!filtered.products.length) {
          const label = options.requireRugPads ? "rug pads" : "rugs";
          this._addMsg(
            `I found ${label}, but none were under ${this.ui.formatPrice(filtered.maxPrice)}. Try a higher budget or fewer filters.`,
            "assistant",
          );
          return;
        }
        const reason = this._generateReason(facts);
        if (options.title || limit > 4) {
          const title = options.title || reason;
          this._addMsg(
            this.ui.horizontalProductCarousel(
              filtered.products.slice(0, limit),
              title,
            ),
            "assistant",
            true,
          );
        } else {
          this._addMsg(
            this.ui.curatedPicks(
              filtered.products,
              reason,
              filtered.products.length,
            ),
            "assistant",
            true,
          );
        }
        this.currentBrowseResults = filtered.products;
      } else if (options.requireRugPads) {
        this._addMsg(this.ui.rugPadFallbackCard(), "assistant", true);
      }
    } catch (err) {
      console.error("Browse error:", err);
      if (options.requireRugPads) {
        this._addMsg(this.ui.rugPadFallbackCard(), "assistant", true);
      }
    }
  }

  _generateReason(facts) {
    const reasons = [];
    if (facts.style) reasons.push(`${facts.style} style`);
    if (facts.color) reasons.push(`${facts.color} tones`);
    if (facts.material) reasons.push(`${facts.material} material`);
    if (facts.room) reasons.push(`perfect for ${facts.room}`);
    if (facts.budget) reasons.push(`within your budget`);
    return reasons.length > 0
      ? `Based on your preferences (${reasons.join(", ")})`
      : "Based on your preferences";
  }

  showAllResults() {
    if (this.currentBrowseResults && this.currentBrowseResults.length > 0) {
      this._addMsg(
        this.ui.productGrid(this.currentBrowseResults, true),
        "assistant",
        true,
      );
      this.toast("Showing all results");
    }
  }

  // --- Compare ---
  _showCompare() {
    this._addMsg(
      this.ui.compareTable(this.store.data.compare),
      "assistant",
      true,
    );
  }
  addCompare(item) {
    const added = this.store.addToCompare(item);
    if (added) {
      this.toast(
        `${item.title} added to compare (${this.store.data.compare.length}/4)`,
      );
      if (this.store.data.compare.length >= 3) {
        this.toast("Opening Comparison Room...");
        this._openComparisonRoom();
      }
    } else {
      this.toast(
        this.store.data.compare.some((c) => c.id === item.id)
          ? "Already in compare list"
          : "Compare list is full (max 4)",
      );
    }
  }
  removeCompare(id) {
    this.store.removeFromCompare(id);
    this._showCompare();
  }
  clearCompare() {
    this.store.clearCompare();
    this._addMsg("Compare list cleared.", "assistant");
  }

  async _openCompareSelector() {
    //
    //

    let cv = document.getElementById("compare-selector-view");
    if (!cv) {
      cv = document.createElement("div");
      cv.id = "compare-selector-view";
      cv.className = "view-section";
      document.getElementById("main-content").appendChild(cv);
    }
    cv.style.display = "block";

    let products = this.currentBrowseResults || [];
    if (products.length < 10) {
      try {
        const res = await this.api.browse({}, 1, 20);
        products = res.products || [];
      } catch (e) {}
    }

    const ui = new UIRenderer();

    cv.innerHTML = `
            <div class="container" style="padding-top:var(--space-2xl); padding-bottom:100px;">
                <h2><i class="fa fa-th-large"></i> Select Rugs to Compare</h2>
                <p style="color:var(--color-text-muted); margin-top:var(--space-sm);">Click "Add" on any rug to select it for comparison. Choose at least 2 rugs.</p>
                <div class="compare-selector-grid">
                    ${products
                      .map((p) => {
                        const isSelected = this.store.data.compare.some(
                          (c) => c.id === p.id,
                        );
                        return `<div class="compare-item" id="cs-${p.id}" style="${isSelected ? "border-color:var(--color-success)" : ""}">
                            <img src="${p.image_url}" alt="${ui.escAttr(p.title)}">
                            <div class="ci-body">
                                <div class="ci-price">${ui.formatPrice(p.price)}</div>
                                <button class="discovery-btn btn-sm ${isSelected ? "btn-success" : "btn-secondary"} ci-add" data-app-action="toggleCompareSelection" data-arg0="${p.id}" data-arg1="${ui.escJS(p.title)}" data-arg2="${ui.escJS(p.price)}" data-arg3="${p.image_url}" data-arg4="${ui.escJS(p.size || "")}" data-arg5="${ui.escJS(p.color || "")}" data-arg6="${ui.escJS(p.style || "")}" data-arg7="${ui.escJS(p.material || "")}">${isSelected ? "Added" : "Add"}</button>
                            </div>
                        </div>`;
                      })
                      .join("")}
                </div>
            </div>
            <div class="compare-sticky-bar">
                <span id="compare-sticky-count" style="font-weight:600">${this.store.data.compare.length} Selected</span>
                <button class="discovery-btn btn-primary" data-app-action="executeCompareSelection">Compare Selected</button>
            </div>
        `;
    window.scrollTo(0, 0);

    if (this.drawerOpen) this.toggleDrawer(); // Hide chat so they can select
  }

  toggleCompareSelection(
    id,
    title,
    price,
    imgUrl,
    size,
    color,
    style,
    material,
    btn,
  ) {
    const item = { id, title, price, imgUrl, size, color, style, material };
    const exists = this.store.data.compare.some((c) => c.id === id);

    if (exists) {
      this.store.removeFromCompare(id);
      btn.textContent = "Add";
      btn.className = "discovery-btn btn-sm btn-secondary ci-add";
      btn.closest(".compare-item").style.borderColor = "var(--color-border)";
    } else {
      const added = this.store.addToCompare(item);
      if (added) {
        btn.textContent = "Added";
        btn.className = "discovery-btn btn-sm btn-success ci-add";
        btn.closest(".compare-item").style.borderColor = "var(--color-success)";
      } else {
        this.toast("Compare list is full (max 4)");
      }
    }

    const countSpan = document.getElementById("compare-sticky-count");
    if (countSpan)
      countSpan.textContent = `${this.store.data.compare.length} Selected`;
  }

  executeCompareSelection() {
    if (this.store.data.compare.length < 2) {
      this.toast("Please select at least 2 rugs to compare.");
      return;
    }
    this._openComparisonRoom();
  }

  _openComparisonRoom() {
    const items = this.store.data.compare;
    this.store.updateFacts({
      comparison_room: true,
      comparison_room_rugs: items,
    });

    //
    //
    let cr = document.getElementById("comparison-room");
    if (!cr) {
      cr = document.createElement("div");
      cr.id = "comparison-room";
      cr.className = "view-section";
      document.getElementById("main-content").appendChild(cr);
    }
    cr.style.display = "block";
    cr.innerHTML = `
            <div class="container comparison-room-container" style="padding-top:var(--space-2xl); padding-bottom: 80px;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--space-lg);">
                    <h2 style="margin:0;"><i class="fa fa-balance-scale"></i> Comparison Room</h2>
                    <button class="discovery-btn btn-secondary btn-sm" data-app-action="exitComparisonRoom"><i class="fa fa-arrow-left"></i> Exit Comparison Room</button>
                </div>
                
                <div class="comparison-cards">
                    ${items
                      .map(
                        (p, i) => `
                        <div class="comparison-card">
                            <img src="${p.imgUrl || p.image_url}" alt="${this.ui.escAttr(p.title)}" onerror="this.style.display='none'">
                            <div class="cc-info">
                                <div class="cc-number">Rug ${i + 1}:</div>
                                <h3 class="cc-title">${this.ui.esc(p.title)}</h3>
                                <div class="cc-rating"><i class="fa fa-star" style="color:#2f6b49"></i><i class="fa fa-star" style="color:#2f6b49"></i><i class="fa fa-star" style="color:#2f6b49"></i><i class="fa fa-star" style="color:#2f6b49"></i><i class="fa fa-star-half-alt" style="color:#2f6b49"></i> <span style="font-size:13px; color:#555;">(4.8, 112 reviews)</span></div>
                                <p class="cc-desc">${this.ui.esc(p.style || "Premium Area Rug")}</p>
                                <button class="discovery-btn btn-ghost cc-quick-view" style="width:100%; border: 1px solid #ccc; margin-bottom:8px;" data-app-action="openProduct" data-arg0="${p.id}">Quick View</button>
                                <button class="discovery-btn btn-primary cc-add-cart" style="width:100%; background:#5b8a6a; border-color:#5b8a6a;" data-app-action="quickAddProduct" data-arg0="${p.id}">Add to Cart</button>
                            </div>
                        </div>
                    `,
                      )
                      .join("")}
                </div>

                <h3 class="comparison-table-heading">COMPARISON TABLE</h3>
                <div class="comparison-table-wrapper">
                    <table class="comparison-detailed-table">
                        <tbody>
                            <tr>
                                <td class="attr-label"><i class="fa fa-dollar-sign"></i> PRICE</td>
                                ${items.map((p) => `<td>${this.ui.formatPrice(p.price)}</td>`).join("")}
                            </tr>
                            <tr>
                                <td class="attr-label"><i class="fa fa-layer-group"></i> MATERIAL</td>
                                ${items.map((p) => `<td>${this.ui.esc(p.material || "100% Polypropylene, Machine-woven")}</td>`).join("")}
                            </tr>
                            <tr>
                                <td class="attr-label"><i class="fa fa-expand-arrows-alt"></i> SIZE</td>
                                ${items.map((p) => `<td>${this.ui.esc(p.size || "8' x 10' Rectangle")}</td>`).join("")}
                            </tr>
                            <tr>
                                <td class="attr-label"><i class="fa fa-folder"></i> COLLECTION</td>
                                ${items.map((p) => `<td>${this.ui.esc(p.collection || p.style || "Heritage Collection")}</td>`).join("")}
                            </tr>
                            <tr>
                                <td class="attr-label"><i class="fa fa-palette"></i> COLOR PALETTE</td>
                                ${items.map((p) => `<td>${this.ui.esc(p.color || "Sapphire Blue, Cream")}</td>`).join("")}
                            </tr>
                            <tr>
                                <td class="attr-label"><i class="fa fa-shield-alt"></i> DURABILITY</td>
                                ${items.map((p) => `<td>High, suitable for high traffic</td>`).join("")}
                            </tr>
                            <tr>
                                <td class="attr-label"><i class="fa fa-tint"></i> CARE INSTRUCTIONS</td>
                                ${items.map((p) => `<td>Professional clean, regular vacuum</td>`).join("")}
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        `;
    if (!this.drawerOpen) this.toggleDrawer();
    this._addMsg(
      `You are now in the <strong>Comparison Room</strong> with ${items.length} rugs. What would you like to know to help you decide?`,
      "assistant",
      true,
    );
    const actionChips = [
      "Which is most durable?",
      "Which is best for kids?",
      "Which is the softest?",
      "Compare prices",
      "Compare materials",
      "Show me a table",
    ];
    this._addMsg(this.ui.chips(actionChips), "assistant", true);
    this._syncContext();
    window.scrollTo(0, 0);
  }

  exitComparisonRoom() {
    this.store.updateFacts({
      comparison_room: false,
      comparison_room_rugs: null,
    });
    const cr = document.getElementById("comparison-room");
    if (cr) cr.style.display = "none";
    this.goHome();
  }

  // --- Live Chat Handoff ---
  _openLiveChat() {
    const chatHost = document.querySelector("shopify-chat");
    let opened = false;

    // Check for public API first (if Shopify Inbox adds one in the future)
    if (
      window.Shopify &&
      window.Shopify.Inbox &&
      typeof window.Shopify.Inbox.open === "function"
    ) {
      window.Shopify.Inbox.open();
      opened = true;
    } else if (chatHost && chatHost.shadowRoot) {
      // Fallback: Simulate click on shadow DOM activator
      const activator = chatHost.shadowRoot.querySelector(
        '[part="activator"], button',
      );
      if (activator) {
        activator.click();
        opened = true;

        // Verify/retry if aria-expanded didn't flip
        setTimeout(() => {
          if (activator.getAttribute("aria-expanded") === "false") {
            activator.click();
          }
        }, 300);
      }
    }

    if (opened) {
      this.toast("Opening live chat...");
      if (this.drawerOpen) this.toggleDrawer(); // Hide our AI chat so user can see human chat
    } else {
      this._addMsg(
        "I couldn't open the live chat widget directly. Please look for the chat icon on your screen or use our Contact page.",
        "assistant",
      );
    }
  }

  // --- Visualizer ---
  openVisualizerDirectly() {
    if (!this.contextProduct) return;
    
    let rawRug = this.contextProduct.imgUrl ||
      this.contextProduct.image_url ||
      this.contextProduct.image ||
      this.contextProduct.featured_image ||
      "";

    let extractedRugUrl = "";
    if (typeof rawRug === 'string') {
      extractedRugUrl = rawRug;
    } else if (rawRug && typeof rawRug === 'object') {
      extractedRugUrl = rawRug.src || rawRug.url || rawRug.gcs_image_url || "";
    }

    const isInvalidUrl = (url) => {
      if (!url || typeof url !== 'string') return true;
      const clean = url.trim().toLowerCase();
      return clean === "" || clean === "no image" || clean === "no_image" || clean.includes("no-image") || clean.includes("no_image") || clean.includes("placeholder");
    };

    let extractedProductUrl = this.contextProduct.url || this.contextProduct.product_url || this.contextProduct.uri || "";
    let extractedHandle = this.contextProduct.handle || this.contextProduct.shopify_handle || "";
    if (!extractedHandle && extractedProductUrl) {
      const match = extractedProductUrl.match(/\/products\/([^\/\?#]+)/);
      if (match) extractedHandle = match[1];
    }
    if (extractedHandle) {
      extractedHandle = String(extractedHandle).replace(/^owfam-/, '').replace(/^\/products\//, '').split('?')[0].split('#')[0].trim();
    }

    const currentPdpHandle = window.location.pathname.includes('/products/')
      ? (window.location.pathname.split('/products/')[1] || '').split('?')[0].split('/')[0].replace(/^owfam-/, '').trim()
      : '';
    const isCurrentPdpProduct = Boolean(
      currentPdpHandle && extractedHandle && (
        currentPdpHandle === extractedHandle ||
        currentPdpHandle === extractedHandle.replace(/^owfam-/, '').trim()
      )
    );

    // Only fallback to PDP DOM image if extractedRugUrl is invalid OR if this product IS the current PDP product and missing image
    if (isInvalidUrl(extractedRugUrl) || (isCurrentPdpProduct && isInvalidUrl(extractedRugUrl))) {
      const pdpBtn = document.querySelector('#pdp-see-in-room, .pdp-view-in-room .see-in-room-btn, [data-rug]');
      if (pdpBtn) {
        const btnRug = pdpBtn.getAttribute('data-rug') || pdpBtn.dataset.rug || '';
        if (!isInvalidUrl(btnRug)) {
          extractedRugUrl = btnRug;
        }
      }
      if (isInvalidUrl(extractedRugUrl) && window.RugVariantData && window.RugVariantData.images) {
        const selectedVarId = this._selectedVariantIdFromPage();
        if (selectedVarId && window.RugVariantData.images[selectedVarId]) {
          const varImg = window.RugVariantData.images[selectedVarId];
          if (!isInvalidUrl(varImg)) extractedRugUrl = varImg;
        }
        if (isInvalidUrl(extractedRugUrl)) {
          const firstImg = Object.values(window.RugVariantData.images).find(img => !isInvalidUrl(img));
          if (firstImg) extractedRugUrl = firstImg;
        }
      }
      if (isInvalidUrl(extractedRugUrl)) {
        const galleryImg = document.querySelector('.product__media img, .product-single__photo img, .product-gallery img, .product-card-gallery img, main img[src*="cdn.shopify.com"]');
        if (galleryImg) {
          const src = galleryImg.getAttribute('data-current-url') || galleryImg.currentSrc || galleryImg.src || '';
          if (!isInvalidUrl(src)) extractedRugUrl = src;
        }
      }
    }

    if (extractedRugUrl && extractedRugUrl.startsWith('//')) {
      extractedRugUrl = 'https:' + extractedRugUrl;
    }

    let transformedVariantData = null;
    if (this.contextProduct.variants && Array.isArray(this.contextProduct.variants)) {
      const sizes = {}; const shapes = {}; const colors = {}; const images = {}; const availability = {};
      const prodOptions = Array.isArray(this.contextProduct.options) ? this.contextProduct.options : [];
      const sizeIdx = prodOptions.findIndex(o => (typeof o === 'string' ? o : o.name || '').toLowerCase() === 'size');
      const shapeIdx = prodOptions.findIndex(o => (typeof o === 'string' ? o : o.name || '').toLowerCase() === 'shape');
      const colorIdx = prodOptions.findIndex(o => (typeof o === 'string' ? o : o.name || '').toLowerCase() === 'color');

      this.contextProduct.variants.forEach(v => {
        const vOpts = Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter(Boolean);
        sizes[v.id] = (sizeIdx !== -1 && vOpts[sizeIdx]) ? vOpts[sizeIdx] : (v.option1 || v.title || "");
        shapes[v.id] = (shapeIdx !== -1 && vOpts[shapeIdx]) ? vOpts[shapeIdx] : (v.option2 || "");
        colors[v.id] = (colorIdx !== -1 && vOpts[colorIdx]) ? vOpts[colorIdx] : (v.option3 || "");
        let vImg = v.primary_image_url || v.image_url || (v.image && (v.image.src || v.image)) 
          || v.featured_image?.src || v.featured_image || extractedRugUrl;
        if (vImg && vImg.startsWith('//')) vImg = 'https:' + vImg;
        images[v.id] = vImg;
        availability[v.id] = v.available !== false;
      });
      transformedVariantData = { 
        sizes, 
        shapes, 
        colors, 
        images, 
        availability, 
        isRecommendation: !isCurrentPdpProduct,
        productUrl: extractedProductUrl || `/products/${extractedHandle}`
      };
    }

    const activeVid = this.contextProduct.variant_id || this.contextProduct.variantId || (this.contextProduct.variants && this.contextProduct.variants[0]?.id);
    if (activeVid) window.activeVisualizerVariantId = String(activeVid);

    const launchParams = {
      source: "assistant",
      productId: this.contextProduct.id || "",
      productHandle: extractedHandle,
      variantId: activeVid ? String(activeVid) : "",
      rugUrl: extractedRugUrl,
      productUrl: extractedProductUrl,
      sizeStr: this.contextProduct.size || "",
      shapeStr: this.contextProduct.shape || this.contextProduct.style || "",
      variantData: transformedVariantData, // Transformed for the visualizer
      forceRoomManager: true,
      onLoading: (msg) =>
        this._addMsg(msg || "Preparing your room preview...", "assistant"),
      onError: (msg) => {
        this._addMsg(
          msg ||
            "Sorry, we couldn't load the room visualizer. Please try again.",
          "assistant",
        );
      },
    };

    if (this.drawerOpen) {
      this.toggleDrawer();
    }

    window.activeVisualizerSource = "assistant";
    window.activeVisualizerProductHandle = extractedHandle;
    window.activeVisualizerProductId = this.contextProduct.id || "";
    window.activeVisualizerRugUrl = extractedRugUrl;
    window.activeVisualizerVariantData = transformedVariantData;
    window.activeVisualizerSize = this.contextProduct.size || "";
    window.activeVisualizerShape = this.contextProduct.shape || this.contextProduct.style || "";

    if (!window.activeVisualizerButton && typeof document !== "undefined") {
      const cardBtn = document.querySelector(
        `[data-id="${this.contextProduct.id}"][data-is-chat-card="true"], [data-product-handle="${extractedHandle}"][data-is-chat-card="true"], .see-in-room-btn[data-id="${this.contextProduct.id}"]`
      );
      if (cardBtn) window.activeVisualizerButton = cardBtn;
    }
    if (window.activeVisualizerButton && !window.activeVisualizerButton.getAttribute("data-is-chat-card")) {
      window.activeVisualizerButton.setAttribute("data-is-chat-card", "true");
    }

    window.pendingSeeInRoom = {
      source: "assistant",
      rugUrl: launchParams.rugUrl,
      productUrl: launchParams.productUrl,
      productHandle: extractedHandle,
      variantId: activeVid ? String(activeVid) : "",
      sizeStr: launchParams.sizeStr,
      shapeStr: launchParams.shapeStr,
    };

    if (typeof window.launchUnifiedVisualizer === "function") {
      window.launchUnifiedVisualizer(launchParams).catch((err) => {
        console.error("Room visualizer launch failed:", err);
        launchParams.onError();
      });
    } else if (typeof window.openRoomManager === "function") {
      window.openRoomManager({ rugUrl: launchParams.rugUrl });
    } else {
      this.toast("Room visualizer is not available on this page.");
    }
  }

  _triggerVisualizer(mode) {
    const actualMode = mode || (this.contextProduct ? "visualize" : "analyze");

    if (actualMode === "visualize") {
      if (!this.contextProduct) {
        this._addMsg(
          "Please select a rug first before visualizing it in your room.",
          "assistant",
        );
        return;
      }
      if (this._requestVisualizerSize(this.contextProduct)) return;
      if (typeof window.openRoomManager === "function") {
        this._launchRoomManagerForProduct(this.contextProduct);
        return;
      }
      this._addMsg(this.ui.visualizerUpload("visualize"), "assistant", true);
      setTimeout(() => {
        const drops = document.querySelectorAll(".viz-drop");
        const drop = drops[drops.length - 1];
        if (!drop) return;
        const fileInput = drop.nextElementSibling;
        const analyzeBtn = fileInput.nextElementSibling;

        drop.addEventListener("click", () => {
          fileInput.click();
        });
        drop.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            fileInput.click();
          }
        });
        drop.addEventListener("dragover", (e) => {
          e.preventDefault();
          drop.style.borderColor = "var(--color-accent)";
          drop.classList.add("drag-over");
        });
        drop.addEventListener("dragleave", () => {
          drop.style.borderColor = "var(--color-border)";
          drop.classList.remove("drag-over");
        });
        drop.addEventListener("drop", (e) => {
          e.preventDefault();
          drop.style.borderColor = "var(--color-border)";
          drop.classList.remove("drag-over");
          if (e.dataTransfer.files.length) {
            fileInput.files = e.dataTransfer.files;
            this._previewFile(
              fileInput.files[0],
              drop,
              analyzeBtn,
              "visualize",
            );
          }
        });
        fileInput.addEventListener("change", () => {
          if (fileInput.files.length)
            this._previewFile(
              fileInput.files[0],
              drop,
              analyzeBtn,
              "visualize",
            );
        });
      }, 100);
      return;
    }

    // Analyze Room is intentionally separate from See in My Room:
    // it uploads one room photo, extracts room facts, and suggests rugs.
    this._addMsg(this.ui.visualizerUpload("analyze"), "assistant", true);
    setTimeout(() => {
      const drops = document.querySelectorAll(".viz-drop");
      const drop = drops[drops.length - 1];
      if (!drop) return;
      const fileInput = drop.nextElementSibling;
      const analyzeBtn = fileInput.nextElementSibling;

      drop.addEventListener("click", () => {
        fileInput.click();
      });
      drop.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          fileInput.click();
        }
      });
      drop.addEventListener("dragover", (e) => {
        e.preventDefault();
        drop.classList.add("drag-over");
      });
      drop.addEventListener("dragleave", () =>
        drop.classList.remove("drag-over"),
      );
      drop.addEventListener("drop", (e) => {
        e.preventDefault();
        drop.classList.remove("drag-over");
        if (e.dataTransfer.files.length) {
          fileInput.files = e.dataTransfer.files;
          this._previewFile(fileInput.files[0], drop, analyzeBtn, "analyze");
        }
      });
      fileInput.addEventListener("change", () => {
        if (fileInput.files.length)
          this._previewFile(fileInput.files[0], drop, analyzeBtn, "analyze");
      });
    }, 100);
  }

  _previewFile(file, drop, btn, mode) {
    const reader = new FileReader();
    reader.onload = (e) => {
      drop.classList.add("has-file");
      drop.innerHTML = `<img src="${e.target.result}" class="viz-preview-img" alt="Selected room photo preview">
                <div class="viz-file-meta">
                    <strong>${this.ui.esc(file.name)}</strong>
                    <small>Ready to ${mode === "visualize" ? "visualize this rug" : "analyze this room"}</small>
                </div>
                <span class="discovery-btn btn-secondary btn-sm viz-file-label"><i class="fa fa-sync-alt"></i> Choose different photo</span>`;
    };
    reader.readAsDataURL(file);
    btn.disabled = false;
    btn.onclick = () => this._analyzeRoom(file, mode);
  }

  async _analyzeRoom(file, mode) {
    const loadId = "load-" + Date.now();
    const isVisualizing = mode === "visualize";

    if (isVisualizing && !this.contextProduct) {
      this._addMsg(
        "Please select a rug first before visualizing it in your room.",
        "assistant",
      );
      return;
    }

    const msg = isVisualizing
      ? "Visualizing rug in your room…"
      : "Analyzing your room…";
    this._addMsg(
      `<div style="display:flex;align-items:center;gap:var(--space-xs)"><i class="fa fa-spinner fa-spin" aria-hidden="true"></i> ${msg}</div>`,
      "assistant",
      true,
      loadId,
    );
    try {
      const data = await this.api.analyzeImage(file, {
        skipVisualizerPoints: !isVisualizing,
      });
      this._removeMsg(loadId);

      // Draw AR Rug Overlay or Open JS Visualizer
      if (data.points && data.points.length > 0 && isVisualizing) {
        if (typeof window.handleAnalyzerSuccess === "function") {
          this._addMsg("Opening the room manager...", "assistant");
          await window.handleAnalyzerSuccess(data, file, false);
        } else {
          this._drawAROverlay(file, data.points);
          this._addMsg("Here is how the rug looks in your space!", "assistant");
        }
      } else if (isVisualizing) {
        this._addMsg("Here is how the rug looks in your space!", "assistant");
      }

      if (isVisualizing) {
        if (data.facts) {
          this.store.updateFacts(data.facts, true);
          this.store.addUploadedPhoto(file, data.facts);
          this._syncContext();
        }
      } else {
        this._addMsg(this.ui.roomAnalysis(data), "assistant", true);
        const roomFacts = this._factsFromRoomAnalysis(data);
        if (Object.keys(roomFacts).length) {
          this.store.updateFacts(roomFacts, true);
          this.store.addUploadedPhoto(file, roomFacts);
          this._syncContext();
          this._addMsg("Here are rugs that match your room:", "assistant");
          await this._doBrowse(roomFacts, 6);
        } else {
          this._addMsg(
            "I analyzed the photo, but did not get enough room details to filter rugs. Try a wider, brighter room photo.",
            "assistant",
          );
        }
      }
    } catch (err) {
      this._removeMsg(loadId);
      console.error("Room analysis error:", err);
      const errMsg = isVisualizing
        ? "We hit a snag visualizing the rug. Could you try a clearer photo or different angle?"
        : "We hit a snag analyzing your room. Could you try a clearer photo or different angle?";
      this._addMsg(errMsg, "assistant");
    }
  }

  _factsFromRoomAnalysis(data = {}) {
    const facts = data.facts || data.payload?.facts || data.filter_state || {};
    return Object.fromEntries(
      Object.entries(facts).filter(
        ([key, value]) =>
          key &&
          !key.startsWith("_") &&
          value !== undefined &&
          value !== null &&
          value !== "",
      ),
    );
  }

  _drawAROverlay(file, points) {
    const drop = document.getElementById("viz-drop");
    const imgUrl = this.contextProduct ? this.contextProduct.imgUrl : null;
    if (!drop || !imgUrl) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const bgImg = new Image();
      bgImg.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = bgImg.width;
        canvas.height = bgImg.height;
        canvas.style.maxWidth = "100%";
        canvas.style.borderRadius = "var(--radius-sm)";
        const ctx = canvas.getContext("2d");

        // Draw room
        ctx.drawImage(bgImg, 0, 0);

        // Draw rug polygon
        const rugImg = new Image();
        rugImg.crossOrigin = "Anonymous";
        rugImg.onload = () => {
          ctx.save();
          ctx.beginPath();
          // Sort points clockwise to prevent bow-tie clipping masks
          const center = points
            .reduce((acc, p) => [acc[0] + p[0], acc[1] + p[1]], [0, 0])
            .map((val) => val / points.length);
          const sortedPoints = [...points].sort((a, b) => {
            return (
              Math.atan2(a[1] - center[1], a[0] - center[0]) -
              Math.atan2(b[1] - center[1], b[0] - center[0])
            );
          });
          sortedPoints.forEach((p, i) => {
            const x = p[0] <= 1 ? p[0] * canvas.width : p[0];
            const y = p[1] <= 1 ? p[1] * canvas.height : p[1];
            if (i === 0) ctx.moveTo(x, y);
            else ctx.lineTo(x, y);
          });
          ctx.closePath();
          ctx.clip();
          // Basic bounding box for the image mapping
          const xs = points.map((p) =>
            p[0] <= 1 ? p[0] * canvas.width : p[0],
          );
          const ys = points.map((p) =>
            p[1] <= 1 ? p[1] * canvas.height : p[1],
          );
          const minX = Math.min(...xs),
            maxX = Math.max(...xs);
          const minY = Math.min(...ys),
            maxY = Math.max(...ys);
          ctx.globalCompositeOperation = "multiply";
          ctx.drawImage(rugImg, minX, minY, maxX - minX, maxY - minY);
          ctx.restore();
        };
        rugImg.src = imgUrl;

        drop.innerHTML = "";
        drop.appendChild(canvas);
        drop.insertAdjacentHTML(
          "beforeend",
          `<p style="font-size:var(--text-xs);color:var(--color-text-muted);margin-top:var(--space-xs)">AR Overlay Rendered</p>`,
        );
      };
      bgImg.src = e.target.result;
    };
    reader.readAsDataURL(file);
  }

  // --- PDP ---
  openProduct(id, fallbackUrl = null) {
    const p = this.productCatalog.get(String(id));
    if (!p && !fallbackUrl) {
      this.toast(
        "Product details are no longer available. Refresh the results and try again.",
      );
      return;
    }

    // Save state to pre-open the agent on the next page
    sessionStorage.setItem("rugbie_open_agent_on_load", "true");

    // Navigate to the product page
    if (p) {
      const handle = p.handle || p.shopify_handle || p.id;
      window.location.href = p.url || `/products/${handle}`;
    } else {
      window.location.href = fallbackUrl;
    }
  }

  openStoredProduct(id) {
    const cached = this.productCatalog.get(String(id));
    const stored =
      cached ||
      this.store.data.viewed.find((item) => String(item.id) === String(id));
    if (!stored) {
      this.toast("Product details are no longer available.");
      return;
    }

    // Save state to pre-open the agent on the next page
    sessionStorage.setItem("rugbie_open_agent_on_load", "true");

    // Navigate to the product page
    const handle = stored.handle || stored.shopify_handle || stored.id;
    window.location.href = stored.url || `/products/${handle}`;
  }

  _isAddToCartQuery(text = "") {
    return /\b(add|put|place)\b.*\b(cart|bag)\b|\bbuy this\b|\badd to cart\b/i.test(
      text,
    );
  }

  _isCartCompareQuery(text = "") {
    return /\bcompare\b/i.test(text) && /\b(cart|bag|items?)\b/i.test(text);
  }

  _isCartContentsQuery(text = "") {
    const normalized = String(text || "")
      .toLowerCase()
      .replace(/[^\w\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();
    return (
      /\b(what|whats|what is|show|view|see|list|display|open|check|review)\b.*\b(cart|bag)\b/.test(
        normalized,
      ) ||
      /\b(cart|bag)\b.*\b(contents?|items?|summary|details?)\b/.test(
        normalized,
      ) ||
      /^(cart|bag|my cart|my bag|show cart|view cart|cart contents)$/.test(
        normalized,
      )
    );
  }

  _isCartRugPadQuery(text = "") {
    return (
      /\brug\s*pad\b|\bpad\b/i.test(text) &&
      /\b(cart|need|recommend|should|use)\b/i.test(text)
    );
  }

  _isRugPadProduct(product = {}) {
    const haystack = [
      product.title,
      product.handle,
      product.shopify_handle,
      product.url,
      product.product_url,
      product.product_type,
      product.type,
      product.category,
      product.categories,
      product.collection,
      product.collection_name,
      product.collections,
      product.tags,
      product.description,
      product.body_html,
      product.variants?.map?.((variant) => [
        variant.title,
        variant.option1,
        variant.option2,
        variant.option3,
      ]),
      product.vendor,
    ]
      .flat()
      .join(" ")
      .toLowerCase();
    return /\brug\s*pads?\b|\bpadding\b|\bcarpet\s*pads?\b|\bnon[-\s]?slip\b|\brug\s*grip\b|\bgripper\b|\bunderlay\b|\bcushion(?:ed|ing)?\s*pad\b/.test(
      haystack,
    );
  }

  _rugPadProductsFromResponse(data) {
    const products = data?.products || data?.payload?.products || [];
    return (Array.isArray(products) ? products : []).filter((product) =>
      this._isRugPadProduct(product),
    );
  }

  _imageFromShopifySuggestProduct(product = {}) {
    const image =
      product.image ||
      product.featured_image ||
      product.image_url ||
      product.imgUrl ||
      "";
    if (typeof image === "string") return image;
    return (
      image.url || image.src || image.originalSrc || image.transformedSrc || ""
    );
  }

  _normalizeShopifySuggestProduct(product = {}) {
    const url = product.url || product.product_url || "";
    const handleFromUrl = url
      ? String(url).split("/products/")[1]?.split(/[?#/]/)[0]
      : "";
    const handle = product.handle || handleFromUrl || product.id;
    const price =
      product.price ||
      product.price_min ||
      product.min_price ||
      product.price_varies ||
      product.variants?.[0]?.price ||
      "";
    const imageUrl = this._imageFromShopifySuggestProduct(product);
    return {
      ...product,
      id: product.id || handle,
      handle,
      title: product.title || product.text || product.name || "Rug Pad",
      url: url || (handle ? `/products/${handle}` : "/search?q=rug%20pad"),
      product_url:
        url || (handle ? `/products/${handle}` : "/search?q=rug%20pad"),
      image_url: imageUrl,
      imgUrl: imageUrl,
      price,
      product_type:
        product.product_type || product.type || product.category || "",
      category: product.category || product.product_type || product.type || "",
    };
  }

  async _fetchShopifySuggestProducts(query = "rug pad", limit = 8) {
    try {
      const params = new URLSearchParams({
        q: query,
        "resources[type]": "product",
        "resources[limit]": String(limit),
      });
      const response = await fetch(
        `/search/suggest.json?${params.toString()}`,
        {
          headers: { Accept: "application/json" },
        },
      );
      if (!response.ok) return [];
      const data = await response.json().catch(() => ({}));
      const products =
        data?.resources?.results?.products ||
        data?.products ||
        data?.results?.products ||
        [];
      return (Array.isArray(products) ? products : []).map((product) =>
        this._normalizeShopifySuggestProduct(product),
      );
    } catch (err) {
      console.warn("Shopify predictive rug pad search failed", err);
      return [];
    }
  }

  async _fetchRugPadProducts(size = undefined, limit = 4) {
    const baseFacts = {
      category: ["Rug Pad", "Rug Pads"],
      product_type: ["Rug Pad", "Rug Pads"],
    };
    if (size) baseFacts.size = size;
    let products = [];
    try {
      const data = await this.api.browse(baseFacts, 1, limit);
      products = this._rugPadProductsFromResponse(data);
    } catch (err) {
      console.warn("Rug pad category browse failed", err);
    }
    if (!products.length) {
      try {
        const fallbackData = await this.api.browse(
          {
            query: "rug pad non slip rug grip underlay",
            search_query: "rug pad",
            size,
          },
          1,
          Math.max(limit, 8),
        );
        products = this._rugPadProductsFromResponse(fallbackData);
      } catch (err) {
        console.warn("Rug pad keyword browse failed", err);
      }
    }
    if (!products.length) {
      const seen = new Set();
      const suggestQueries = [
        "rug pad",
        "rug pads",
        "non slip rug pad",
        "rug grip",
        "underlay",
      ];
      for (const query of suggestQueries) {
        const suggested = await this._fetchShopifySuggestProducts(
          query,
          Math.max(limit, 8),
        );
        suggested.forEach((product) => {
          const key = String(
            product.id || product.handle || product.url || product.title || "",
          );
          if (key && !seen.has(key) && this._isRugPadProduct(product)) {
            seen.add(key);
            products.push(product);
          }
        });
        if (products.length >= limit) break;
      }
    }
    return products.slice(0, limit);
  }

  _isCartCareQuery(text = "") {
    return (
      /\b(care|clean|cleaning|wash|instructions?)\b/i.test(text) &&
      !/\btrack\b/i.test(text)
    );
  }

  _isHistoryQuery(text = "") {
    return /^(history|recently viewed|recent rugs|viewed rugs|show history)$/i.test(
      String(text || "").trim(),
    );
  }

  _handleLocalHistoryCommand(query) {
    if (!this._isHistoryQuery(query)) return false;
    const history = this._recentProductHistory();
    if (history.length > 0) {
      this._addMsg(
        "Here are the rugs you've recently viewed or interacted with:",
        "assistant",
      );
      this._addMsg(
        this.ui.horizontalProductCarousel(history),
        "assistant",
        true,
      );
    } else {
      this._addMsg(
        "You haven't viewed any rugs yet. Try searching or opening a rug, then I'll keep it here.",
        "assistant",
      );
    }
    return true;
  }

  _handleLocalOrderTrackingCommand(query) {
    if (!this._isOrderTrackingQuery(query)) return false;

    this._addMsg(
      "For order tracking, please use our store chat. I will open it for you now.",
      "assistant",
    );

    setTimeout(async () => {
      if (this.drawerOpen) {
        this.toggleDrawer();
        await new Promise((r) => setTimeout(r, 350));
      }
      const status = await this.openShopifyInbox();
      if (status !== "opened") {
        this.toast(
          "Click the Chat button in the bottom-right corner for order tracking.",
        );
      }
    }, 2500);

    return true;
  }

  _handleLocalServiceCommand(query) {
    if (!this._isServiceIntentQuery(query)) return false;
    
    const wantsCostInfo = /\b(cost|price|pricing|how much|fee|quote|estimate)\b/i.test(query);
    if (wantsCostInfo) {
      this._addMsg(`For information regarding the cost of our services, please reach out to our team directly. Our staff will be happy to provide an estimate and assist you during store hours.<br><br><button class="discovery-btn btn-primary btn-sm" data-app-action="openShopifyInbox" style="margin-top: 8px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; font-weight: 600; padding: 10px 16px; border-radius: 6px; box-shadow: 0 4px 6px rgba(0,0,0,0.1); border: 1px solid var(--color-border); background: var(--color-accent); color: #fff;"><i class="fa fa-comments"></i> Chat Live with Store</button>`, "assistant", true);
      return true;
    }

    const wantsCleaningTips =
      /\b(stain|spill|clean|cleaning|wash|odor|pet)\b/i.test(query);
    const initialService = wantsCleaningTips
      ? "Professional Rug Cleaning"
      : null;
    this.startServiceFlow(initialService);
    return true;
  }

  async _handleExplicitProductCountQuery(query) {
    const count = this._extractRequestedProductCount(query);
    if (!count) return false;
    const facts = this._browseFactsForQuery(query, {
      ...this.store.data.facts,
    });
    if (this._extractMaxPrice(query))
      facts.budget = this._extractMaxPrice(query);
    const wantsRugPads = this._isRugPadProductQuery(query);
    await this._doBrowse(facts, count, {
      title: `Showing ${count} ${wantsRugPads ? "rug pads" : "rugs"}`,
      requireRugPads: wantsRugPads,
      query,
    });
    return true;
  }

  async _handleRugPadProductBrowse(query) {
    if (!this._isRugPadProductQuery(query)) return false;
    const facts = this._browseFactsForQuery(query, {});
    if (this._extractMaxPrice(query))
      facts.budget = this._extractMaxPrice(query);
    try {
      const products = await this._fetchRugPadProducts(facts?.size, 4);
      const filtered = this._productsForBudget(products, query, facts);
      if (filtered.products.length) {
        this.registerProducts(filtered.products);
        this.currentBrowseResults = filtered.products;
        this._addMsg(
          this.ui.horizontalProductCarousel(
            filtered.products.slice(0, 4),
            "Recommended Rug Pads",
          ),
          "assistant",
          true,
        );
      } else if (products.length && filtered.maxPrice) {
        this._addMsg(
          `I found rug pads, but none were under ${this.ui.formatPrice(filtered.maxPrice)}. Try a higher budget or fewer filters.`,
          "assistant",
        );
      } else {
        this._addMsg(this.ui.rugPadFallbackCard(), "assistant", true);
      }
    } catch (err) {
      console.warn("Rug pad product browse failed", err);
      this._addMsg(this.ui.rugPadFallbackCard(), "assistant", true);
    }
    return true;
  }

  _cartItemsForCompare(cartItems) {
    return (cartItems || []).map((item) => ({
      id: item.product_id || item.id || item.variant_id,
      variant_id: item.variant_id,
      title: item.title,
      price: item.price,
      size: item.size,
      color: item.color || "",
      style: item.style || "",
      material: item.material || "",
      imgUrl: item.imgUrl || item.image,
      image_url: item.imgUrl || item.image,
      url: item.url || item.product_url,
      handle: item.handle,
    }));
  }

  async _handleLocalCartCommand(query) {
    if (
      !this._isCartContentsQuery(query) &&
      !this._isCartCompareQuery(query) &&
      !this._isCartRugPadQuery(query) &&
      !this._isCartCareQuery(query)
    ) {
      return false;
    }

    const cartItems = await this._refreshCartMemory();
    const activeProduct = this._getActiveProduct();

    if (!cartItems.length) {
      if (this._isCartCareQuery(query)) {
        if (activeProduct) {
          const title = `Care guide for ${activeProduct.title || "this rug"}`;
          this._addMsg(
            this.ui.cartCareCard([activeProduct], title),
            "assistant",
            true,
          );
        } else {
          this._addMsg(
            this.ui.knowledgeCard(
              "Rug Care & Cleaning Guide",
              `<p>Here are essential care instructions for keeping your rugs in top condition:</p>
              <ul>
                <li><strong>Regular Vacuuming:</strong> Vacuum regularly without a beater bar to avoid pulling fibers.</li>
                <li><strong>Spot Cleaning:</strong> Blot spills immediately with a clean, dry white cloth. Never rub.</li>
                <li><strong>Deep Clean:</strong> Rotate every 6 months for even wear. For tough stains, professional cleaning is recommended.</li>
              </ul>
              <p style="margin-top:8px">Open any rug page to see care guidance specific to its material!</p>`,
              "fa-droplet"
            ),
            "assistant",
            true
          );
        }
        return true;
      }
      if (this._isCartRugPadQuery(query)) {
        if (activeProduct) {
          this._addMsg(this.ui.rugPadAdviceCard([activeProduct]), "assistant", true);
          try {
            const sizes = this._availableSizesForProduct(activeProduct);
            const products = await this._fetchRugPadProducts(
              sizes.length ? sizes : undefined,
              4,
            );
            if (products.length)
              this._addMsg(
                this.ui.horizontalProductCarousel(
                  products.slice(0, 4),
                  "Recommended Rug Pads",
                ),
                "assistant",
                true,
              );
            else this._addMsg(this.ui.rugPadFallbackCard(), "assistant", true);
          } catch (e) {
            console.warn("Rug pad search failed", e);
            this._addMsg(this.ui.rugPadFallbackCard(), "assistant", true);
          }
          return true;
        }
      }
      this._addMsg(
        "Your cart is empty right now. Add a rug first, then I can suggest a rug pad.",
        "assistant",
      );
      return true;
    }

    if (this._isCartContentsQuery(query)) {
      this._addMsg(this.ui.cartStateCard(cartItems), "assistant", true);
      return true;
    }

    if (this._isCartCompareQuery(query)) {
      this._addMsg(
        "I can help you choose based on size, care, material, or price, but this assistant does not compare cart items side by side.",
        "assistant",
      );
      this._addMsg(this.ui.cartStateCard(cartItems), "assistant", true);
      return true;
    }

    if (this._isCartRugPadQuery(query)) {
      this._addMsg(this.ui.rugPadAdviceCard(cartItems), "assistant", true);
      try {
        const sizes = [
          ...new Set(cartItems.map((item) => item.size).filter(Boolean)),
        ];
        const products = await this._fetchRugPadProducts(
          sizes.length ? sizes[0] : undefined,
          4,
        );
        if (products.length)
          this._addMsg(
            this.ui.horizontalProductCarousel(
              products.slice(0, 4),
              "Recommended Rug Pads",
            ),
            "assistant",
            true,
          );
        else this._addMsg(this.ui.rugPadFallbackCard(), "assistant", true);
      } catch (e) {
        console.warn("Rug pad search failed", e);
        this._addMsg(this.ui.rugPadFallbackCard(), "assistant", true);
      }
      return true;
    }

    if (this._isCartCareQuery(query)) {
      if (activeProduct && !/\bcart\b/i.test(query)) {
        const title = `Care guide for ${activeProduct.title || "this rug"}`;
        this._addMsg(
          this.ui.cartCareCard([activeProduct], title),
          "assistant",
          true,
        );
      } else {
        this._addMsg(this.ui.cartCareCard(cartItems), "assistant", true);
      }
      return true;
    }

    return false;
  }

  _isProductSeekingQuery(text = "") {
    return (
      /\b(show|find|search|browse|recommend|looking for|need|want)\b/i.test(
        text,
      ) &&
      /\b(rug|rugs|runner|pad|carpet|blue|red|green|washable|wool|jute|outdoor|indoor|cheap|under)\b/i.test(
        text,
      )
    );
  }

  _availableSizesForProduct(product = {}) {
    const sizes = [];
    if (Array.isArray(product.variants)) {
      product.variants.forEach((v) => {
        let title =
          this._variantOption(product, v, "Size") || v.option1 || v.title;
        
        if (title && title.includes("/")) {
          const parts = title.split("/").map((p) => p.trim());
          const sizePart = parts.find(
            (p) => /\d+['"]/.test(p) || (p.toLowerCase().includes('x') && /\d/.test(p))
          );
          if (sizePart) title = sizePart;
        }

        if (title && String(title).toLowerCase() !== "default title")
          sizes.push(title);
      });
    }
    if (
      product.size &&
      String(product.size).toLowerCase() !== "default title"
    ) {
      if (!product.size.includes("/") || sizes.length === 0) {
        sizes.push(product.size);
      }
    }
    if (Array.isArray(product.sizes)) sizes.push(...product.sizes);
    return [...new Set(sizes.map((s) => String(s).trim()).filter(Boolean))];
  }

  _findSizeFromText(text = "", product = null) {
    const normalizeStr = (s) =>
      String(s)
        .toLowerCase()
        .replace(/[\s\-_]+/g, " ")
        .replace(/["'”’]/g, "")
        .trim();
    const normalized = normalizeStr(text);
    const knownSizes = product ? this._availableSizesForProduct(product) : [];
    const exact = knownSizes.find((size) =>
      normalized.includes(normalizeStr(size)),
    );
    if (exact) return exact;
    const normalizedForRegex = String(text).toLowerCase().trim();
    const match = normalizedForRegex.match(
      /(\d{1,2})\s*(?:'|ft|feet|")?\s*(?:x|by|x)\s*(\d{1,2})\s*(?:'|ft|feet|")?/i,
    );
    return match ? `${match[1]}' x ${match[2]}'` : "";
  }

  async _handleLocalCommerceIntent(query) {
    if (/\b(store\s+(hours|time|timing|schedule)|opening\s+hours|when\s+are\s+you\s+open|business\s+hours)\b/i.test(query)) {
      const shopName = window.ShopifyContextPayload?.shop?.name || "Vogue Rugs";
      this._addMsg(
        `<strong>${this.ui.esc(shopName)} Store Hours:</strong><br/>` +
        `• Monday - Saturday: 10:00 AM - 6:00 PM<br/>` +
        `• Sunday: Closed<br/>` +
        `• Online Store: Open 24/7`,
        "assistant",
        true
      );
      return true;
    }

    if (/\b(show|see|find|get)?\s*similar\s*(rugs|styles|products|items)?\b/i.test(query) || /^\s*similar\s*$/i.test(query)) {
      const activeProduct = this._getActiveProduct();
      await this._showSimilarRugs(activeProduct);
      return true;
    }

    if (this.pendingCartIntent) {
      const size = this._findSizeFromText(
        query,
        this.pendingCartIntent.product,
      );
      if (!size) return false;
      const product = {
        ...this.pendingCartIntent.product,
        size,
        sizeSelected: true,
        selectedSizeExplicit: true,
      };
      this.pendingCartIntent = null;
      await this._addProductToCartWithSize(product, null);
      return true;
    }

    if (this.pendingVisualizerIntent) {
      const size = this._findSizeFromText(
        query,
        this.pendingVisualizerIntent.product,
      );
      if (!size) return false;
      const product = {
        ...this.pendingVisualizerIntent.product,
        size,
        sizeSelected: true,
        selectedSizeExplicit: true,
      };
      this.pendingVisualizerIntent = null;
      this._setContextProduct(product);
      this._launchRoomManagerForProduct(product);
      return true;
    }

    if (!this._isAddToCartQuery(query)) return false;
    const product =
      this.contextProduct ||
      (this.lastShownProducts && this.lastShownProducts[0]) ||
      this.store.getLastViewed();
    if (!product) {
      this._addMsg(
        "Please choose a rug first, then I can add it to your cart.",
        "assistant",
      );
      return true;
    }

    let sizes = this._availableSizesForProduct(product);

    if (sizes.length === 0) {
      const handle = product.handle || product.shopify_handle || product.id;
      if (handle) {
        try {
          const res = await fetch(`/products/${handle}.js`);
          if (res.ok) {
            const fullProd = await res.json();
            if (fullProd && fullProd.variants) {
              product.variants = fullProd.variants;
              product.options = fullProd.options;
              sizes = this._availableSizesForProduct(product);
            }
          }
        } catch (e) {
          console.warn("[Discovery-AI] Could not fetch variants for typed size selection:", e);
        }
      }
    }

    const typedSize = this._findSizeFromText(query, product);
    const userSelectedSizeExplicitly = product._sizeSelectedByUser === true || product.selectedSizeExplicitByUser === true;
    const hasExplicitSizeInCommand = !!typedSize || userSelectedSizeExplicitly;

    if (sizes.length > 0 && !hasExplicitSizeInCommand) {
      this.pendingCartIntent = { product };
      this._addMsg(
        `Which size would you like for <strong>${this.ui.esc(product.title)}</strong>?`,
        "assistant",
        true,
      );
      this._addMsg(this.ui.chips(sizes.slice(0, 8)), "assistant", true);
      return true;
    }

    const selectedSize = typedSize || (userSelectedSizeExplicitly ? product.size : sizes[0]);
    await this._addProductToCartWithSize(
      {
        ...product,
        size: selectedSize || product.size,
        sizeSelected: true,
        selectedSizeExplicit: true,
        _sizeSelectedByUser: true,
      },
      null,
    );
    return true;
  }

  async selectResultSize(id, size, button) {
    const product = this.productCatalog.get(String(id));
    if (!product) return;
    const previousSize =
      product.size && String(product.size).toLowerCase() !== "default title"
        ? String(product.size)
        : "";
    const sizeChanged = previousSize !== String(size);
    const matchingVariant = Array.isArray(product.variants)
      ? product.variants.find(
          (variant) =>
            (this._variantOption(product, variant, "Size") || variant.title) ===
            size,
        )
      : null;
    const updatedProduct = matchingVariant
      ? this._applyVariantToProduct(product, matchingVariant, true)
      : { ...product, size, sizeSelected: true, selectedSizeExplicit: true };
    updatedProduct._sizeSelectedByUser = true;
    Object.assign(product, updatedProduct);
    this.productCatalog.set(String(id), product);
    if (this.contextProduct && String(this.contextProduct.id) === String(id)) {
      this.contextProduct = { ...this.contextProduct, ...updatedProduct };
    }
    button
      ?.closest(".result-size-row")
      ?.querySelectorAll(".result-size-chip")
      .forEach((chip) => chip.classList.remove("selected"));
    button?.classList.add("selected");
    if (sizeChanged) this.toast(`Selected ${size}`);
    if (
      this.pendingCartIntent &&
      String(this.pendingCartIntent.product.id) === String(id)
    ) {
      this.pendingCartIntent = null;
      await this._addProductToCartWithSize(product, button);
    } else if (
      this.pendingVisualizerIntent &&
      String(this.pendingVisualizerIntent.product.id) === String(id)
    ) {
      this.pendingVisualizerIntent = null;
      this._setContextProduct(product);
      this._launchRoomManagerForProduct(product);
    }
  }

  async _addProductToCartWithSize(product, button) {
    const handle = product.handle || product.shopify_handle || product.id;
    await this.quickAddToCart(
      handle,
      product.title,
      product.price,
      product.image_url || product.imgUrl || "",
      product.size || "",
      product.color || "",
      button,
      product,
    );
  }

  _setContextProduct(product) {
    if (!product) return;
    this.contextProduct = {
      ...product,
      imgUrl: product.imgUrl || product.image_url || product.image || "",
    };
    if (this.contextProduct.id) {
      this.productCatalog.set(
        String(this.contextProduct.id),
        this.contextProduct,
      );
    }
    this._updateLiveContext();
    this._renderToolMenu();
    this._syncContext();
  }

  _hasExplicitProductSize(product = {}) {
    return !!(
      product.sizeSelected ||
      product.selectedSizeExplicit ||
      product.explicitSizeSelected ||
      product._sizeSelectedByUser ||
      product.userSelectedSize
    );
  }

  _requestVisualizerSize(product) {
    if (!product) {
      this._addMsg(
        "Please select a rug first before visualizing it in your room.",
        "assistant",
      );
      return true;
    }
    const sizes = this._availableSizesForProduct(product);
    // Always ask the user to pick a size so the visualizer uses the correct
    // dimensions. Only skip when there are no size variants at all, or user already selected one.
    if (sizes.length > 0 && !this._hasExplicitProductSize(product)) {
      this.pendingVisualizerIntent = { product };
      this._ensureDrawer();
      this._addMsg(
        `Please select the size for <strong>${this.ui.esc(product.title)}</strong> before I open the room preview.`,
        "assistant",
        true,
      );
      this._addMsg(this.ui.chips(sizes.slice(0, 8)), "assistant", true);
      return true;
    }
    return false;
  }



  _launchRoomManagerForProduct(product) {
    const prepared = this._prepareVisualizerProduct(product);
    if (!prepared) {
      this._addMsg(
        "Please select a rug first before visualizing it in your room.",
        "assistant",
      );
      return;
    }
    this._setContextProduct(prepared);
    const sizeCopy = prepared.size
      ? ` using <strong>${this.ui.esc(prepared.size)}</strong>`
      : "";
    this._addMsg(
      `Opening the room manager${sizeCopy}. Select a room photo and I'll place the rug there.`,
      "assistant",
      true,
    );
    this.openVisualizerDirectly();
  }

  async quickAddProduct(id, button) {
    const p = this.productCatalog.get(String(id));
    if (!p) {
      console.warn("[Discovery-AI] Product not in catalog:", id);
      return;
    }
    const sizes = this._availableSizesForProduct(p);
    if (sizes.length > 0 && !p._sizeSelectedByUser) {
      this.pendingCartIntent = { product: p };
      this._ensureDrawer();
      this._addMsg(
        `Which size would you like for <strong>${this.ui.esc(p.title)}</strong>?`,
        "assistant",
        true,
      );
      this._addMsg(this.ui.chips(sizes.slice(0, 8)), "assistant", true);
      return;
    }
    // Pass the handle so Store can resolve variant from Shopify
    const handle = p.handle || p.shopify_handle || p.id;
    await this.quickAddToCart(
      handle,
      p.title,
      p.price,
      p.image_url || "",
      p.size || "",
      p.color || "",
      button,
    );
  }

  async seeProductInRoom(id, button) {
    let p = this.productCatalog.get(String(id));
    if (!p) return;

    let rawHandle = p.shopify_handle || p.handle || '';
    if (!rawHandle && (p.url || p.product_url || p.uri)) {
      const pUrl = p.product_url || p.url || p.uri || '';
      const m = pUrl.match(/\/products\/([^\/\?#]+)/);
      if (m) rawHandle = m[1];
    }
    if (!rawHandle && p.id && !/^\d+$/.test(String(p.id)) && !String(p.id).startsWith('rec_')) {
      rawHandle = String(p.id);
    }
    const cleanHandle = String(rawHandle || '')
      .replace(/^owfam-/, '')
      .replace(/^\/products\//, '')
      .split('?')[0]
      .split('#')[0]
      .trim();

    const btn = button || (typeof document !== 'undefined' ? document.querySelector(`[data-app-action="seeProductInRoom"][data-arg0="${id}"], [data-id="${id}"][data-is-chat-card="true"]`) : null);
    if (btn) {
      window.activeVisualizerButton = btn;
      btn.setAttribute('data-is-chat-card', 'true');
      if (cleanHandle) btn.setAttribute('data-product-handle', cleanHandle);
      if (p.image_url || p.imgUrl) btn.setAttribute('data-rug', p.image_url || p.imgUrl);
      if (p.size) btn.setAttribute('data-size', p.size);
    }
    window.activeVisualizerSource = 'assistant';
    window.activeVisualizerProductHandle = cleanHandle || p.id || '';
    window.activeVisualizerProductId = p.id || '';

    if ((!p.variants || p.variants.length === 0 || !p.options || !p.variants[0]?.options) && cleanHandle) {
      const candidates = [cleanHandle, cleanHandle.replace(/^owfam-/, ''), 'owfam-' + cleanHandle.replace(/^owfam-/, '')].filter(Boolean);
      for (const h of candidates) {
        try {
          let shopifyProduct = null;
          try {
            const rMeta = await fetch(`/products/${h}?view=variants-json`);
            if (rMeta.ok) {
              const metaJson = await rMeta.json();
              if (metaJson && Array.isArray(metaJson.variants) && metaJson.variants.length) {
                shopifyProduct = metaJson;
              }
            }
          } catch (_) {}

          if (!shopifyProduct) {
            const resp = await fetch(`/products/${h}.js`);
            if (resp.ok) {
              shopifyProduct = await resp.json();
            }
          }

          if (shopifyProduct && Array.isArray(shopifyProduct.variants)) {
            p = {
              ...p,
              handle: h,
              variants: shopifyProduct.variants,
              options: shopifyProduct.options
            };
            this.productCatalog.set(String(id), p);
            window.activeVisualizerProductHandle = h;
            break;
          }
        } catch (_) {}
      }
    }
    this._launchRoomManagerForProduct(p);
  }


  showSimilar(id) {
    const p = this.productCatalog.get(String(id));
    if (p)
      this.contextProduct = {
        ...p,
        imgUrl: p.imgUrl || p.image_url || p.image || p.featured_image || "",
      };
    this._updateLiveContext();
    if (!this.drawerOpen) this.toggleDrawer();
    this.sendAction("Show similar rugs");
    this._syncContext();
  }

  viewProduct(
    id,
    title,
    size,
    color,
    price,
    style,
    material,
    imgUrl,
    raw = {},
  ) {
    this.contextProduct = {
      ...raw,
      id,
      title,
      size,
      color,
      price,
      style,
      material,
      imgUrl,
      url:
        raw.url ||
        raw.product_url ||
        (raw.handle ? `/products/${raw.handle}` : ""),
      available: raw.available,
      shape: raw.shape,
      collection: raw.collection || raw.collection_name || "",
    };
    this._updateLiveContext();
    this._renderToolMenu();
    this.store.addViewed(this.contextProduct);
    try {
      if (this.recommender)
        this.recommender.trackProductView(this.contextProduct, "view");
    } catch (e) {
      console.warn("Recommender track failed:", e);
    }
    this._previousView =
      document.getElementById("home-view")?.style.display !== "none"
        ? "home"
        : "pdp";

    // Update PDP UI
    const pdpMainImg = document.getElementById("pdp-main-img");
    if (pdpMainImg) {
      pdpMainImg.src = imgUrl;
      pdpMainImg.alt = title;
    }
    document.getElementById("pdp-title").textContent = title;
    document.getElementById("pdp-price").textContent =
      this.ui.formatPrice(price);
    document.getElementById("pdp-color").textContent = color || "—";
    document.getElementById("pdp-style").textContent = style || "—";
    document.getElementById("pdp-material").textContent = material || "—";
    document.getElementById("pdp-collection").textContent =
      this.contextProduct.collection || "—";
    document.getElementById("pdp-description").textContent =
      raw.description || raw.product_description || "";

    // Size selector
    const sizes = Array.isArray(raw.sizes) ? raw.sizes : size ? [size] : [];
    document.getElementById("pdp-sizes").innerHTML = sizes.length
      ? sizes
          .map(
            (s) =>
              `<button class="size-btn ${s === size ? "active" : ""}" data-size="${this.ui.escAttr(s)}" data-app-action="updateSelectedSize" data-arg0="${this.ui.escAttr(s)}">${this.ui.esc(s)}</button>`,
          )
          .join("")
      : '<span class="product-card-meta">See product availability for size options.</span>';

    // Wishlist button state
    const wishBtn = document.getElementById("pdp-wishlist");
    if (this.store.isWishlisted(id)) {
      wishBtn.innerHTML =
        '<i class="fa fa-heart" style="color:#e53935"></i> Saved';
    } else {
      wishBtn.innerHTML = '<i class="fa fa-heart"></i> Save';
    }

    // Show PDP

    //
    //
    //

    window.scrollTo(0, 0);

    // Update assistant context
    // Banner UI removed in favor of inline chat bubbles

    // Update assistant greeting
    this._addMsg(
      `Now viewing <strong>${this.ui.esc(title)}</strong>. I can help with sizing, care, similar styles, or show it in your room.`,
      "assistant",
      true,
    );
    // Suggested question chips will be loaded dynamically by _autoOpenPDPAssistant

    // Load recommendations
    this._loadPDPRecommendations(id);
    this._syncContext();

    // Update style facts
    if (style) this.store.updateFacts({ style: [style] });

    // Auto-open assistant with product context (Rufus-style)
    this._autoOpenPDPAssistant();
  }

  async _autoOpenPDPAssistant() {
    // Open the drawer if not already open
    if (!this.drawerOpen) {
      // Small delay to let PDP render first
      setTimeout(() => this.toggleDrawer(), 300);
    }

    // Load dynamic suggested questions from backend
    await this._loadPDPSuggestedQuestions();

    // Bind the inline Ask bar
    this._bindPDPAskBar();
  }

  async _loadPDPSuggestedQuestions() {
    if (!this.contextProduct) return;

    // Prevent duplicate chip bubbles
    const existing = document.getElementById("load-pdp-dynamic-chips");
    if (existing) existing.remove();

    let dynamicQuestions = [];

    // Generate deterministic questions based strictly on available PDP data
    if (this.contextProduct.options && this.contextProduct.options.length > 0) {
      dynamicQuestions.push(
        `What sizes are available for the ${this.contextProduct.title}?`,
      );
    } else {
      dynamicQuestions.push("What are the dimensions of this rug?");
    }

    // Material is a core attribute almost always handled well by the assistant context
    dynamicQuestions.push("What material is this rug made of?");

    // Add a specific third question if data exists, otherwise fallback to care
    if (this.contextProduct.thickness) {
      dynamicQuestions.push("What is the pile height of this rug?");
    } else {
      dynamicQuestions.push("What are the care instructions for this rug?");
    }

    // Combine dynamic questions and exactly two static actions
    const allChips = [...dynamicQuestions, "See in my room", "Care Guide"];

    // Render chips in the chat panel with a non-saving ID
    this._addMsg(
      this.ui.chips(allChips),
      "assistant",
      true,
      "load-pdp-dynamic-chips",
    );
  }

  _bindPDPAskBar() {
    const input = document.getElementById("pdp-ask-input");
    const sendBtn = document.getElementById("pdp-ask-send");
    if (!input || !sendBtn) return;

    // Remove old listeners by cloning
    const newInput = input.cloneNode(true);
    const newSend = sendBtn.cloneNode(true);
    input.parentNode.replaceChild(newInput, input);
    sendBtn.parentNode.replaceChild(newSend, sendBtn);

    newInput.addEventListener("keydown", (e) => {
      if (e.key === "Enter") {
        e.preventDefault();
        if (newInput.value.trim()) {
          this.askFromPDP(newInput.value.trim());
          newInput.value = "";
        }
      }
    });

    newSend.addEventListener("click", (e) => {
      e.preventDefault();
      if (newInput.value.trim()) {
        this.askFromPDP(newInput.value.trim());
        newInput.value = "";
      }
    });
  }

  askFromPDP(question) {
    // Open drawer if not already open
    if (!this.drawerOpen) this.toggleDrawer();
    // Send the question through the chat
    this._addMsg(question, "user");
    this._processQuery(question);
  }

  async _loadPDPRecommendations(productId) {
    try {
      const reco = await this.api.recommend(productId);
      const products = Array.isArray(reco)
        ? reco
        : reco.products || reco.payload?.products || [];
      if (Array.isArray(products) && products.length > 0) {
        this.registerProducts(products);
        const carousel = document.getElementById("pdp-reco-carousel");
        carousel.innerHTML = products
          .map(
            (
              p,
            ) => `<div class="reco-card" data-app-action="openProduct" data-arg0="${this.ui.escAttr(p.id)}">
                    <img src="${this.ui.escAttr(p.image_url)}" alt="${this.ui.escAttr(p.title)}">
                    <div class="reco-card-body">
                        <h4>${this.ui.esc(p.title)}</h4>
                        <p>${this.ui.esc(p.price)}</p>
                        ${p.llm_reason ? `<p class="llm-reason"><i class="fa fa-sparkles"></i> ${this.ui.esc(p.llm_reason)}</p>` : ""}
                    </div>
                </div>`,
          )
          .join("");
        document.getElementById("pdp-reco-section").style.display = "block";
      }
    } catch (err) {
      console.error("Recommendations error:", err);
    }
  }

  async _pdpAddToCart() {
    if (!this.contextProduct) return;
    const p = this.contextProduct;
    const handle = p.handle || p.shopify_handle || p.id;
    await this.store.addToCart({
      id: handle,
      handle: handle,
      title: p.title,
      price: p.price,
      imgUrl: p.imgUrl,
      size: p.size,
      color: p.color,
    });
    this._updateCartUI();

    // Button animation
    const btn = document.getElementById("pdp-add-cart");
    btn.innerHTML = '<i class="fa fa-check"></i> Added!';
    btn.classList.add("discovery-btn-success-flash");
    setTimeout(() => {
      btn.innerHTML = '<i class="fa fa-shopping-bag"></i> Add to Cart';
      btn.classList.remove("btn-success-flash");
    }, 2000);

    // Chat notification
    if (this.drawerOpen) {
      this._addMsg(this.ui.cartAdded(p.title), "assistant", true);
    }
    this.toast(`${p.title} added to cart`);
  }

  _pdpToggleWishlist() {
    if (!this.contextProduct) return;
    const p = this.contextProduct;
    const added = this.store.toggleWishlist({
      id: p.id,
      title: p.title,
      price: p.price,
      imgUrl: p.imgUrl,
    });
    const btn = document.getElementById("pdp-wishlist");
    if (added) {
      btn.innerHTML = '<i class="fa fa-heart" style="color:#e53935"></i> Saved';
      this.toast(`${p.title} saved to wishlist`);
    } else {
      btn.innerHTML = '<i class="fa fa-heart"></i> Save';
      this.toast(`${p.title} removed from wishlist`);
    }
  }

  goHome() {
    document.getElementById("pdp-reco-section").style.display = "none";
    this.contextProduct = null;
    this._updateLiveContext();
    this._renderToolMenu();
    this.pdpBanner?.classList.remove("visible");
    document.body.classList.remove("pdp-assistant-open");
    if (this.drawerOpen) this.toggleDrawer();
    this._syncContext();
    window.scrollTo(0, 0);
  }

  // --- Quick actions from product cards ---
  async quickAddVariant(variantId, btnElem) {
    if (btnElem) {
      const originalText = btnElem.innerHTML;
      btnElem.innerHTML = `<i class="fa fa-spinner fa-spin"></i>`;
      btnElem.disabled = true;
      try {
        const result = await this.store.addToCart({ variant_id: variantId });
        this._updateCartUI();
        if (result?.success) {
          this.toast(`Added to cart`);
          this._addMsg(
            `<div class="confirmation-card compact">
                <p><i class="fa fa-check-circle" style="color:var(--color-success)" aria-hidden="true"></i> Successfully added to your cart.</p>
            </div>`,
            "assistant",
            true
          );
          // Show dynamic recommendations
          this._handlePostCartAddition(result.cartItem);
        } else {
          this.toast("Failed to add to cart");
        }
      } finally {
        btnElem.innerHTML = originalText;
        btnElem.disabled = false;
      }
    } else {
      const result = await this.store.addToCart({ variant_id: variantId });
      this._updateCartUI();
      if (result?.success) {
        this.toast(`Added to cart`);
        this._addMsg(
          `<div class="confirmation-card compact">
              <p><i class="fa fa-check-circle" style="color:var(--color-success)" aria-hidden="true"></i> Successfully added to your cart.</p>
          </div>`,
          "assistant",
          true
        );
        this._handlePostCartAddition(result.cartItem);
      }
    }
  }

  async quickAddToCart(
    idOrHandle,
    title,
    price,
    imgUrl,
    size,
    color,
    btnElem,
    product = null,
  ) {
    const result = await this.store.addToCart({
      id: idOrHandle,
      handle: idOrHandle,
      title,
      price,
      imgUrl,
      size,
      color,
    });
    this._updateCartUI();

    if (result?.success) {
      this.toast(`${title} added to cart`);
      const formattedSize =
        size && String(size).toLowerCase() !== "default title" ? size : "";
      const sizeMsg = formattedSize
        ? ` of size <strong>${this.ui.esc(formattedSize)}</strong>`
        : "";
      this._addMsg(
        `<div class="confirmation-card compact">
                <p><i class="fa fa-check-circle" style="color:var(--color-success)" aria-hidden="true"></i> Added <strong>${this.ui.esc(title)}</strong>${sizeMsg} to your cart.</p>
            </div>`,
        "assistant",
        true,
      );
      if (product) this._setContextProduct(product);
      if (btnElem) {
        const orig = btnElem.innerHTML;
        btnElem.innerHTML = '<i class="fa fa-check"></i> Added!';
        btnElem.classList.add("discovery-btn-success-flash");
        setTimeout(() => {
          btnElem.innerHTML = orig;
          btnElem.classList.remove("btn-success-flash");
        }, 1500);
      }

      // Auto-suggest rug pads matching the rug
      try {
        const padRes = await this.api.search("rug pad");
        let rawPads = padRes?.results || padRes?.products || (Array.isArray(padRes) ? padRes : []);
        let pads = rawPads.filter((p) => {
          const pTitle = String(p.title || p.name || "").toLowerCase();
          const pType = String(p.product_type || p.category || "").toLowerCase();
          const pTags = Array.isArray(p.tags) ? p.tags.join(" ").toLowerCase() : String(p.tags || "").toLowerCase();
          return pTitle.includes("pad") || pTitle.includes("grip") || pTitle.includes("underlay") || pType.includes("pad") || pTags.includes("pad");
        });
        if (pads.length === 0) {
          const colRes = await fetch("/collections/rug-pads/products.json?limit=8").then(r => r.ok ? r.json() : null).catch(() => null);
          if (colRes && colRes.products) {
            pads = colRes.products.map(p => ({
              id: p.id,
              title: p.title,
              price: p.variants?.[0]?.price || "0.00",
              imgUrl: p.images?.[0]?.src || "",
              handle: p.handle,
              product_type: "Rug Pad",
              sizes: (p.options?.find(o => /size/i.test(o.name))?.values) || []
            }));
          }
        }
        if (pads.length > 0) {
          this.registerProducts(pads);
          this._addMsg(
            this.ui.horizontalProductCarousel(
              pads.slice(0, 4),
              "Rug Pads",
            ),
            "assistant",
            true,
          );
        }
      } catch (padErr) {
        console.warn("[Discovery-AI] Auto rug pad fetch failed:", padErr);
      }

      return true;
    }

    this.toast(`Failed to add ${title} - ${result?.error || "unknown error"}`);
    console.error("[Discovery-AI] quickAddToCart failed:", result);
    return false;
  }

  toggleWishlistHP(id, title, price, imgUrl, btnElem) {
    const added = this.store.toggleWishlist({ id, title, price, imgUrl });
    if (btnElem) btnElem.classList.toggle("wishlisted", added);
    this.toast(added ? `${title} saved` : `${title} removed from wishlist`);
  }

  async _sendNativeShopifyContact() {
    // Native storefront /contact form posts require Google reCAPTCHA tokens on Shopify themes.
    // Service request delivery & durable storage is fully handled by /assistant/contact-store.
    return Promise.resolve();
  }

  // --- Appointment ---
  async submitAppointment(event) {
    event.preventDefault();
    const form = event.target;
    const service =
      form.service?.options[form.service.selectedIndex]?.text || "Consultation";
    const name = form.full_name?.value?.trim() || "Not provided";
    const phone = form.phone?.value?.trim() || "Not provided";
    const email = form.email?.value?.trim() || "";
    const notes = form.notes?.value?.trim() || "";
    const shopEmail =
      window.ShopifyContextPayload?.shop?.email || "hello@thediscovery.ai";
    const product =
      this.contextProduct || window.ShopifyContextPayload?.product || null;
    const subject = `Service Request - ${service}`;
    const body = [
      "New service request from Discovery-AI assistant:",
      "",
      `Service: ${service}`,
      `Name: ${name}`,
      `Phone: ${phone}`,
      `Email: ${email || "Not provided"}`,
      `Notes: ${notes || "Not provided"}`,
      product?.title ? `Product: ${product.title}` : "",
      product?.url
        ? `Product Link: ${new URL(product.url, window.location.origin).toString()}`
        : "",
      `Page: ${window.location.href}`,
      "",
      "Photo upload note: if a photo is needed, please ask the customer to reply with the image.",
    ]
      .filter(Boolean)
      .join("\n");
    
    // Dispatch native contact form POST to store owner email
    this._sendNativeShopifyContact({ name, email, phone, service, notes, body });

    form.closest(".appointment-card").remove();
    const loadId = "load-contact-" + Date.now();
    this._addMsg(
      this.ui.loading("Sending this to the store team..."),
      "assistant",
      true,
      loadId,
    );

    let backendReady = false;
    let handoffId = "";
    try {
      const data = await this.api.contactStore({
        session_id: this.store.sessionId || "",
        name,
        phone,
        email: email || null,
        service,
        notes,
        page_url: window.location.href,
        product,
        cart: window.ShopifyContextPayload?.cart || {},
      });
      backendReady =
        data?.success === true ||
        data?.handoff === "shopify_inbox" ||
        data?.configured === true;
      handoffId = data?.handoff_id || "";
    } catch (e) {
      console.warn("[Discovery-AI] contact-store capture failed:", e);
    } finally {
      this._removeMsg(loadId);
    }

    const status = await this.openShopifyInbox();
    if (status === "opened") {
      const copied = await this._copyTextToClipboard(body);
      this._addMsg(
        this.ui.inboxHandoffCard(body, shopEmail, handoffId, true, copied),
        "assistant",
        true,
      );
      return;
    }

    if (status === "found_but_failed") {
      this._addMsg(
        "I found the chat widget on this page, but I'm unable to open it automatically right now. Please try clicking the chat icon directly.",
        "assistant",
      );
    } else if (backendReady) {
      this._addMsg(
        "The backend prepared the Shopify Inbox handoff, but I could not find the chat launcher on this page.",
        "assistant",
      );
    }
    this._addMsg(
      this.ui.inboxHandoffCard(body, shopEmail, handoffId, false, false),
      "assistant",
      true,
    );
  }

  async _copyTextToClipboard(text) {
    try {
      if (!navigator.clipboard?.writeText) return false;
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_) {
      return false;
    }
  }

  async copyHandoffMessage(message, button) {
    const copied = await this._copyTextToClipboard(message || "");
    this.toast(copied ? "Message copied" : "Could not copy message");
    if (copied && button) {
      const original = button.innerHTML;
      button.innerHTML = '<i class="fa fa-check"></i> Copied';
      setTimeout(() => {
        button.innerHTML = original;
      }, 1200);
    }
  }

  async openShopifyInbox() {
    // --- Helper: full-event click sequence ---
    const clickEl = (el) => {
      if (!el) return false;
      try {
        // Focus first — Shopify's widget may require focus for re-activation
        if (typeof el.focus === "function") el.focus();
        el.dispatchEvent(
          new PointerEvent("pointerdown", {
            bubbles: true,
            cancelable: true,
            composed: true,
            pointerId: 1,
            pointerType: "mouse",
          }),
        );
        el.dispatchEvent(
          new MouseEvent("mousedown", {
            bubbles: true,
            cancelable: true,
            composed: true,
          }),
        );
        el.dispatchEvent(
          new PointerEvent("pointerup", {
            bubbles: true,
            cancelable: true,
            composed: true,
            pointerId: 1,
            pointerType: "mouse",
          }),
        );
        el.dispatchEvent(
          new MouseEvent("mouseup", {
            bubbles: true,
            cancelable: true,
            composed: true,
          }),
        );
        el.click();
        return true;
      } catch (_) {
        try {
          el.click();
          return true;
        } catch (__) {
          return false;
        }
      }
    };

    // --- Helper: keyboard activation (Enter + Space) ---
    const keyboardActivate = (el) => {
      if (!el) return false;
      try {
        if (typeof el.focus === "function") el.focus();
        for (const key of ["Enter", " "]) {
          el.dispatchEvent(
            new KeyboardEvent("keydown", {
              key,
              code: key === " " ? "Space" : "Enter",
              bubbles: true,
              cancelable: true,
              composed: true,
            }),
          );
          el.dispatchEvent(
            new KeyboardEvent("keyup", {
              key,
              code: key === " " ? "Space" : "Enter",
              bubbles: true,
              cancelable: true,
              composed: true,
            }),
          );
        }
        return true;
      } catch (_) {
        return false;
      }
    };

    // --- Helper: find the best Shopify Chat launcher button ---
    const findLauncher = () => {
      const isInDiscoveryWidget = (el) => {
        if (el.closest?.(".discovery-ai-widget")) return true;
        const root = el.getRootNode?.();
        return !!root?.host?.closest?.(".discovery-ai-widget");
      };
      const collect = (root = document, seen = new Set()) => {
        if (!root || seen.has(root)) return [];
        seen.add(root);
        const nodes = Array.from(
          root.querySelectorAll?.(
            'shopify-chat, button, [role="button"], a, [part="activator"], .activator, [aria-label="Chat"]',
          ) || [],
        );
        for (const node of nodes) {
          if (node.shadowRoot) nodes.push(...collect(node.shadowRoot, seen));
        }
        return nodes;
      };
      const candidates = collect().filter((el) => !isInDiscoveryWidget(el));
      const launchers = candidates
        .map((el) => {
          const rect = el.getBoundingClientRect();
          const style = window.getComputedStyle(el);
          const className =
            typeof el.className === "object"
              ? el.className.baseVal || ""
              : el.className || "";
          const text =
            `${el.tagName} ${el.getAttribute("aria-label") || ""} ${el.getAttribute("title") || ""} ${el.textContent || ""} ${el.id || ""} ${className}`.toLowerCase();
          const visible =
            rect.width > 8 &&
            rect.height > 8 &&
            style.display !== "none" &&
            style.visibility !== "hidden" &&
            style.opacity !== "0";
          const chatLike = /\b(chat|inbox|message|messenger|support)\b/.test(
            text,
          );
          const exactActivator = el.matches?.(
            'button[part="activator"], button.activator, [part="activator"][aria-label="Chat"], .activator[aria-label="Chat"]',
          );
          const floating =
            style.position === "fixed" ||
            style.position === "sticky" ||
            (rect.right > window.innerWidth * 0.55 &&
              rect.bottom > window.innerHeight * 0.55);
          return {
            el,
            rect,
            text,
            visible,
            chatLike,
            exactActivator,
            floating,
          };
        })
        .filter(
          (item) => item.visible && (item.chatLike || item.exactActivator),
        );

      launchers.sort((a, b) => {
        const aScore =
          (a.exactActivator ? 1000 : 0) +
          (a.floating ? 100 : 0) +
          a.rect.right +
          a.rect.bottom;
        const bScore =
          (b.exactActivator ? 1000 : 0) +
          (b.floating ? 100 : 0) +
          b.rect.right +
          b.rect.bottom;
        return bScore - aScore;
      });
      return launchers[0]?.el || null;
    };

    // --- Helper: find the deepest clickable button inside a shopify-chat shadow root ---
    const findShopifyChatButton = () => {
      const chatEl = document.querySelector("shopify-chat");
      if (!chatEl?.shadowRoot) return null;
      // Walk shadow roots recursively to find the activator button
      const walk = (root) => {
        if (!root) return null;
        const btn = root.querySelector(
          'button[part="activator"], button.activator, [aria-label="Chat"], button',
        );
        if (btn) {
          // If this button itself has a shadow root, go deeper
          if (btn.shadowRoot) {
            const deeper = walk(btn.shadowRoot);
            if (deeper) return deeper;
          }
          return btn;
        }
        // Check child elements with shadow roots
        for (const child of root.querySelectorAll("*")) {
          if (child.shadowRoot) {
            const found = walk(child.shadowRoot);
            if (found) return found;
          }
        }
        return null;
      };
      return walk(chatEl.shadowRoot);
    };

    // --- Attempt Strategy: try click + keyboard on both generic launcher and direct shopify-chat button ---
    const attemptOpen = () => {
      // Strategy 1: Direct shopify-chat shadow button (most reliable for re-opens)
      const directBtn = findShopifyChatButton();
      if (directBtn) {
        clickEl(directBtn);
        keyboardActivate(directBtn);
        return true;
      }
      // Strategy 2: Generic launcher search
      const launcher = findLauncher();
      if (launcher) {
        // Try the shadow button first, then the outer element
        const shadowBtn = launcher.shadowRoot?.querySelector(
          'button, [role="button"], .activator, [part="activator"]',
        );
        if (shadowBtn) {
          clickEl(shadowBtn);
          keyboardActivate(shadowBtn);
        }
        clickEl(launcher);
        return true;
      }
      return false;
    };

    // --- Execute with retries ---
    // Attempt 1: immediate
    if (attemptOpen()) return "opened";

    // Attempt 2: short delay (widget may be re-rendering after drawer close)
    await new Promise((r) => setTimeout(r, 350));
    if (attemptOpen()) return "opened";

    // Attempt 3: longer delay (some widgets are slow to re-initialize)
    await new Promise((r) => setTimeout(r, 600));
    if (attemptOpen()) return "opened";

    // Fallback: try global API objects
    if (window.ShopifyInbox && typeof window.ShopifyInbox.open === "function") {
      try {
        window.ShopifyInbox.open();
        return "opened";
      } catch (_) {}
    }
    if (window.ShopifyChat && typeof window.ShopifyChat.open === "function") {
      try {
        window.ShopifyChat.open();
        return "opened";
      } catch (_) {}
    }
    if (
      window.Shopify?.inbox &&
      typeof window.Shopify.inbox.open === "function"
    ) {
      try {
        window.Shopify.inbox.open();
        return "opened";
      } catch (_) {}
    }

    return "not_found";
  }

  async submitOrderTrack(event) {
    event.preventDefault();
    const form = event.target;
    const orderId = form.querySelector("#track-order-id").value.trim();
    const email = form.querySelector("#track-email").value.trim();
    form.closest(".appointment-card")?.remove();

    const loadId = "load-track-" + Date.now();
    this._addMsg(
      this.ui.loading("Locating your order..."),
      "assistant",
      true,
      loadId,
    );

    try {
      const res = await fetch(`${API_BASE}/orders/track`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Hardcoded to discovery_ai_llc because we only have Shopify API access for this store
        body: JSON.stringify({
          tenant_id: window.shopkeeperTenant || "vogue_rugs",
          order_number: orderId,
          email,
        }),
      });
      this._removeMsg(loadId);
      const data = await res.json();
      if (!res.ok || data.error || data.success === false) {
        throw new Error(data.message || data.error || "Tracking lookup failed");
      }
      await this._hydrateOrderLineItemImages(data);
      this._addMsg(this.ui.orderStatusCard(data), "assistant", true);
    } catch (e) {
      this._removeMsg(loadId);
      console.error("Order tracking error:", e);
      const detail =
        e?.message || "Please verify your order number and email address.";
      this._addMsg(
        `Sorry, we could not locate that order. ${this.ui.esc(detail)}`,
        "assistant",
        true,
      );
    }
  }

  async _hydrateOrderLineItemImages(order) {
    const items = Array.isArray(order?.line_items) ? order.line_items : [];
    await Promise.all(
      items.map(async (item) => {
        if (!item) return;
        const handle =
          item.handle ||
          String(item.product_url || "")
            .split("/products/")[1]
            ?.split(/[?#/]/)[0];
        const existingImage = this._normalizeOrderImageUrl(
          item.image || item.imgUrl || item.image_url || "",
        );
        if (existingImage) item.image = existingImage;
        const applyProduct = (product) => {
          if (!product) return false;
          item.handle = product.handle || item.handle;
          item.product_url =
            product.url ||
            (product.handle ? `/products/${product.handle}` : item.product_url);
          const variantId = String(item.variant_id || "");
          const variantTitle = String(item.variant_title || "").toLowerCase();
          const matchedVariant = Array.isArray(product.variants)
            ? product.variants.find(
                (variant) => String(variant.id || "") === variantId,
              ) ||
              product.variants.find(
                (variant) =>
                  String(variant.title || "").toLowerCase() === variantTitle,
              )
            : null;
          const image =
            matchedVariant?.featured_image?.src ||
            matchedVariant?.featured_image ||
            product.featured_image ||
            product.image ||
            product.image_url ||
            product.images?.[0] ||
            product.media?.[0]?.src ||
            "";
          const normalized = this._normalizeOrderImageUrl(image);
          if (normalized) item.image = normalized;
          return !!item.product_url;
        };
        try {
          if (handle) {
            const res = await fetch(
              `/products/${encodeURIComponent(handle)}.js`,
            );
            if (res.ok) {
              applyProduct(await res.json());
              return;
            }
          }

          const matchedProduct = await this._findOrderProductByTitle(
            item.title,
          );
          if (matchedProduct) {
            applyProduct(matchedProduct);
            return;
          }

          delete item.product_url;
          delete item.url;
        } catch (e) {
          console.warn("Order item image lookup failed:", e);
        }
      }),
    );
  }

  async _findOrderProductByTitle(title = "") {
    const query = String(title || "").trim();
    if (!query) return null;
    const searches = [
      query,
      query
        .replace(/[^\w\s'-]/g, " ")
        .replace(/\s+/g, " ")
        .trim(),
    ].filter((value, index, list) => value && list.indexOf(value) === index);

    for (const search of searches) {
      try {
        const params = new URLSearchParams({
          q: search,
          "resources[type]": "product",
          "resources[limit]": "4",
          "resources[options][unavailable_products]": "show",
        });
        const res = await fetch(`/search/suggest.json?${params.toString()}`);
        if (!res.ok) continue;
        const data = await res.json();
        const products = data?.resources?.results?.products || [];
        if (!products.length) continue;
        const lowerTitle = query.toLowerCase();
        return (
          products.find(
            (product) =>
              String(product.title || "").toLowerCase() === lowerTitle,
          ) || products[0]
        );
      } catch (e) {
        console.warn("Order product search failed:", e);
      }
    }
    return null;
  }

  _normalizeOrderImageUrl(src = "") {
    if (!src) return "";
    let value = src;
    if (typeof value === "object") {
      value = value.src || value.url || value.preview_image?.src || "";
    }
    value = String(value || "").trim();
    if (!value) return "";
    if (value.startsWith("//")) value = `${window.location.protocol}${value}`;
    if (value.startsWith("/")) value = `${window.location.origin}${value}`;
    try {
      const url = new URL(value, window.location.origin);
      const shopDomain = window.ShopifyContextPayload?.shop?.domain;
      if (
        shopDomain &&
        /\.myshopify\.com$/i.test(url.hostname) &&
        url.hostname !== shopDomain
      ) {
        url.hostname = shopDomain;
      }
      if (!url.searchParams.has("width")) url.searchParams.set("width", "220");
      return url.toString();
    } catch (_) {
      return value;
    }
  }

  // --- Homepage Loading ---
  async _loadHomepage() {
    const renderGrid = (gridId, products) => {
      const grid = document.getElementById(gridId);
      if (!grid || !products?.length) return;
      grid.innerHTML = products.map((p) => buildHomepageCard(p)).join("");
    };
    try {
      const personalFacts = Object.keys(this.store.data.facts || {}).length
        ? this.store.data.facts
        : { room: ["Living Room"] };
      const [trending, best, personalized, living] = await Promise.allSettled([
        this.api.browse({}, 1, 4),
        this.api.browse({}, 2, 4),
        this.api.browse(personalFacts, 1, 4),
        this.api.browse({ room: ["Living Room"] }, 1, 4),
      ]);
      if (trending.status === "fulfilled")
        renderGrid("grid-trending", trending.value.products);
      if (best.status === "fulfilled")
        renderGrid("grid-bestsellers", best.value.products);
      if (personalized.status === "fulfilled")
        renderGrid("grid-personalized", personalized.value.products);
      if (living.status === "fulfilled")
        renderGrid("grid-livingroom", living.value.products);
    } catch (err) {
      console.error("Homepage load error:", err);
    }
  }
}

// ============================================================
// Initialize
// ============================================================
document.addEventListener("DOMContentLoaded", async () => {
  window.app = new App();
  await window.app.store.init();
  await window.app._restoreChatHistory();

  if (sessionStorage.getItem("rugbie_open_agent_on_load") === "true") {
    sessionStorage.removeItem("rugbie_open_agent_on_load");
    if (window.app && window.app.toggleDrawer) {
      // Slight delay to ensure UI is ready
      setTimeout(() => {
        window.app.toggleDrawer();
      }, 100);
    }
  }
});

/**
 * Dynamic CTA logic for Discovery-AI
 */
document.addEventListener("DOMContentLoaded", () => {
  const liveContext = document.getElementById("assistant-live-context");

  // Add base classes
  if (liveContext) liveContext.classList.add("discovery-ticker");

  let currentMessages = [];
  let currentLiveContexts = [];
  let tickerIndex = 0;
  let tickerInterval = null;

  const startTicker = () => {
    if (tickerInterval) clearInterval(tickerInterval);
    tickerIndex = 0;

    if (currentMessages.length === 0) return;

    const updateUI = () => {
      const msg = currentMessages[tickerIndex];
      const ctx = currentLiveContexts[tickerIndex];

      // Add swap out animation
      if (liveContext && ctx) {
        liveContext.style.transition = "all 0.3s ease";
        liveContext.style.opacity = "0";
        liveContext.style.transform = "translateY(-5px)";
        setTimeout(() => {
          liveContext.innerHTML = ctx;
          liveContext.style.opacity = "1";
          liveContext.style.transform = "translateY(0)";
        }, 300);
      }

      tickerIndex = (tickerIndex + 1) % currentMessages.length;
    };

    // Initial update
    updateUI();

    // Loop every 4 seconds
    tickerInterval = setInterval(updateUI, 4000);
  };

  const generateMessages = (variant, product) => {
    let messages = [];
    let contexts = [];

    const currency =
      window.ShopifyContextPayload &&
      window.ShopifyContextPayload.shop &&
      window.ShopifyContextPayload.shop.currency
        ? window.ShopifyContextPayload.shop.currency
        : "$";
    const formatMoney = (dollars) => {
      if (currency === "$") return "$" + Number(dollars).toFixed(2);
      return Number(dollars).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
      });
    };

    const safeTitle = product.title || "this product";
    const optionsText =
      variant && variant.title !== "Default Title" ? variant.title : "";

    const addState = (fabMsg, ctxTitle, ctxVal1, ctxVal2) => {
      messages.push(fabMsg);
      contexts.push(
        '<div class="live-indicator"><span class="live-dot" aria-hidden="true"></span><span class="live-label">' +
          ctxTitle +
          "</span></div>" +
          '<div class="live-details"><strong>' +
          ctxVal1 +
          "</strong><small>" +
          ctxVal2 +
          "</small></div>",
      );
    };

    if (!variant) {
      addState(
        "✨ Discovery-AI knows " + safeTitle,
        "Viewing",
        safeTitle,
        "Ask me anything",
      );
      return { messages, contexts };
    }

    if (
      variant.inventory_management === "shopify" &&
      variant.inventory_quantity <= 5 &&
      variant.inventory_quantity > 0
    ) {
      addState(
        "⚡ Only " + variant.inventory_quantity + " left in this size",
        "Availability",
        "Only " + variant.inventory_quantity + " Left",
        "Order soon",
      );
    }

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const pct = Math.round(
        ((variant.compare_at_price - variant.price) /
          variant.compare_at_price) *
          100,
      );
      const savings = formatMoney(variant.compare_at_price - variant.price);
      addState(
        "🔥 Save " + savings + " today",
        "Savings",
        "Save " + savings,
        "Compared to MSRP",
      );
      addState(
        "🏷️ Up to " + pct + "% Off",
        "Price",
        "Starting at " + formatMoney(variant.price),
        pct + "% Off Today",
      );
    }

    if (
      !variant.compare_at_price ||
      variant.compare_at_price <= variant.price
    ) {
      addState(
        "💰 Starting from " + formatMoney(variant.price),
        "Price",
        "Starting at " + formatMoney(variant.price),
        "Great value",
      );
    }

    if (optionsText) {
      addState("🎨 Viewing " + optionsText, "Variant", optionsText, safeTitle);
    } else {
      addState("👀 Viewing " + safeTitle, "Viewing", safeTitle, "");
    }

    if (product.rating) {
      const ratingVal =
        typeof product.rating === "object" && product.rating.rating
          ? product.rating.rating
          : product.rating;
      const countStr = product.rating_count
        ? product.rating_count + " Verified Buyers"
        : "Verified Buyers";
      addState(
        "⭐ Rated " + ratingVal + "★ by customers",
        "Reviews",
        "⭐ " + ratingVal + " Rating",
        countStr,
      );
    }

    addState(
      "Explore all available sizes",
      "AI Suggestions",
      "Available Sizes",
      "Find the perfect fit",
    );

    const isOutdoor = safeTitle.toLowerCase().includes("outdoor");
    if (isOutdoor) {
      addState(
        "🤔 Ask if this works outdoors",
        "Need Help?",
        "Outdoor Use",
        "Weather resistant?",
      );
    } else {
      addState(
        "🤔 Ask where this works best",
        "Need Help?",
        "Room Placement",
        "Styling tips",
      );
    }

    const isLoop = safeTitle.toLowerCase().includes("loop");
    if (isLoop) {
      addState(
        "🧵 Learn about the loop pile material",
        "Material",
        "Loop Pile",
        "Durability & Texture",
      );
    } else {
      addState(
        "🧵 Learn about this material",
        "Need Help?",
        "Material & Texture",
        "Ask our AI",
      );
    }

    addState(
      "🧼 Ask how to clean this rug",
      "Need Help?",
      "Cleaning & Care",
      "Maintenance guide",
    );
    addState(
      "❤️ Customers also buy matching runners",
      "Popular Choice",
      "Matching Runners",
      "Complete the look",
    );
    addState(
      "✨ Discovery-AI knows this product",
      "AI Assistant",
      "Ask Anything",
      "Your personal shopper",
    );
    addState(
      "📏 Need help choosing the right size?",
      "Need Help?",
      "Sizing Guide",
      "Ask about dimensions",
    );
    addState(
      "🏡 See how this rug fits your room",
      "AI Suggestions",
      "Room Visualizer",
      "Try it in your space",
    );
    addState(
      "💬 Ask anything about this product",
      "Need Help?",
      "Product Q&A",
      "Ask our AI",
    );

    return { messages, contexts };
  };

  const updateCTA = (variantId, product) => {
    if (!product) return;

    let variant = null;
    if (variantId) {
      if (product.variants && Array.isArray(product.variants)) {
        variant = product.variants.find(
          (v) => v.id === variantId || String(v.id) === String(variantId),
        );
      }
      if (
        !variant &&
        product.variant &&
        (product.variant.id === variantId ||
          String(product.variant.id) === String(variantId))
      ) {
        variant = product.variant;
      }
    }

    const data = generateMessages(variant, product);
    currentMessages = data.messages;
    currentLiveContexts = data.contexts;

    if (liveContext) liveContext.hidden = false;

    startTicker();
  };

  if (window.ShopifyContextPayload && window.ShopifyContextPayload.product) {
    const initialVariantId = window.ShopifyContextPayload.product.variant
      ? window.ShopifyContextPayload.product.variant.id
      : null;
    updateCTA(initialVariantId, window.ShopifyContextPayload.product);
  }

  document.addEventListener("variant:changed", function (e) {
    if (
      e.detail &&
      e.detail.variant &&
      window.ShopifyContextPayload &&
      window.ShopifyContextPayload.product
    ) {
      updateCTA(e.detail.variant.id, window.ShopifyContextPayload.product);
    }
  });
});
